import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { FaUsers, FaSearch, FaEdit, FaEye, FaEyeSlash, FaDownload, FaUpload, FaSignOutAlt, FaTimes, FaBars, FaChevronLeft, FaFileAlt, FaCamera, FaTrash, FaCheck, FaExclamationTriangle, FaLink, FaCopy, FaClock, FaSignInAlt, FaSignOutAlt as FaSignOut, FaUserCheck, FaUserGraduate, FaBriefcase, FaPlus, FaColumns } from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { INDIAN_STATES_DISTRICTS, QUALIFICATION_OPTIONS } from './data/indianStatesDistricts';

const isSupervisorJobHidden = (job) => job?.hidden_by_user === true || job?.hidden_by_user === 't';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const JOB_POST_QUALIFICATIONS = ['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate'];

// Helper function to format time in IST (Indian Standard Time)
// Uses toLocaleTimeString with timeZone to properly convert to IST
const formatTimeIST = (dateString) => {
  if (!dateString) return '-';
  
  let date;
  try {
    if (dateString instanceof Date) {
      // If it's already a Date object, use it directly
      date = dateString;
    } else {
      const dateStr = String(dateString).trim();
      
      // Always treat database timestamps as UTC
      // If it already has Z or timezone offset, use as-is
      if (dateStr.includes('Z') || dateStr.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(dateStr);
      } else {
        // For any string that looks like a date-time, ALWAYS treat as UTC
        // Normalize: replace space with T
        let normalizedStr = dateStr.replace(' ', 'T');
        
        // Extract date and time parts - match various formats
        // Format: YYYY-MM-DD HH:MM:SS or YYYY-MM-DDTHH:MM:SS with optional milliseconds
        const dateTimeMatch = normalizedStr.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(\.\d+)?/);
        if (dateTimeMatch) {
          // Explicitly create UTC date by appending Z
          const utcString = `${dateTimeMatch[1]}T${dateTimeMatch[2]}${dateTimeMatch[3] || ''}Z`;
          date = new Date(utcString);
        } else if (normalizedStr.includes('T')) {
          // Has T but didn't match pattern - still append Z to force UTC
          date = new Date(normalizedStr + 'Z');
        } else {
          // No time component - this shouldn't happen for check_in_time/check_out_time
          // But handle it anyway by treating as UTC midnight
          if (normalizedStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            date = new Date(normalizedStr + 'T00:00:00Z');
          } else {
            // Last resort: try to parse and force UTC interpretation
            // Parse as local first to extract components, then reconstruct as UTC
            const tempDate = new Date(dateStr);
            if (!isNaN(tempDate.getTime())) {
              // Extract UTC components from the parsed date
              const year = tempDate.getUTCFullYear();
              const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(tempDate.getUTCDate()).padStart(2, '0');
              const hours = String(tempDate.getUTCHours()).padStart(2, '0');
              const minutes = String(tempDate.getUTCMinutes()).padStart(2, '0');
              const seconds = String(tempDate.getUTCSeconds()).padStart(2, '0');
              const utcString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
              date = new Date(utcString);
            } else {
              date = tempDate;
            }
          }
        }
      }
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateString, 'Type:', typeof dateString);
      return '-';
    }
    
    // Convert to IST (UTC+5:30) using toLocaleTimeString with timeZone
    // This correctly converts from UTC to IST
    const istTime = date.toLocaleTimeString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    return istTime;
  } catch (error) {
    console.error('Error formatting time:', error, dateString);
    return '-';
  }
};

// Helper function to format date in IST
const formatDateIST = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const CALLING_REPORT_COLUMNS_STORAGE_KEY = 'gogig_supervisor_calling_report_columns';

const CALLING_REPORT_COLUMNS = [
  { key: 'name', label: 'Name', minWidth: 100, required: true },
  { key: 'mobile', label: 'Mobile', minWidth: 110 },
  { key: 'email', label: 'Email', minWidth: 140 },
  { key: 'qualification', label: 'Qualification', minWidth: 120 },
  { key: 'district', label: 'District', minWidth: 100 },
  { key: 'state', label: 'State', minWidth: 100 },
  { key: 'source', label: 'Source', minWidth: 100 },
  { key: 'supervisor', label: 'Supervisor', minWidth: 100 },
  { key: 'jobs_applied', label: 'Jobs Applied', minWidth: 200 },
  { key: 'date', label: 'Date applied / Uploaded', minWidth: 120 },
  { key: 'status', label: 'Status', minWidth: 150 },
  { key: 'callback_date', label: 'Callback Date', minWidth: 130 },
  { key: 'company', label: 'Companies', minWidth: 180, editable: true },
  { key: 'remarks', label: 'Remarks', minWidth: 120, editable: true },
  { key: 'joining_date', label: 'Joining Date', minWidth: 100, editable: true },
  { key: 'employee_code', label: 'Employee Code', minWidth: 90, editable: true }
];

function loadCallingReportColumnVisibility() {
  try {
    const raw = localStorage.getItem(CALLING_REPORT_COLUMNS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch (_) { /* ignore */ }
  return {};
}

/** Safe YYYY-MM-DD for <input type="date"> — never throws (invalid dates caused white screen). */
function toCallingReportDateInputValue(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dmy = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, '0');
    const mm = dmy[2].padStart(2, '0');
    return `${dmy[3]}-${mm}-${dd}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toISOString().slice(0, 10);
  } catch (_) {
    return '';
  }
}

function formatCallingReportDateDisplay(value) {
  if (value == null || value === '') return '-';
  const iso = toCallingReportDateInputValue(value);
  if (iso) {
    const d = new Date(`${iso}T00:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }
  return String(value);
}

function CallingReportCol({ colKey, columnVisibility, as = 'td', style, children }) {
  if (columnVisibility && columnVisibility[colKey] === false) return null;
  const Tag = as;
  return <Tag style={style}>{children}</Tag>;
}

class CallingReportErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Something went wrong' };
  }

  componentDidCatch(error, info) {
    console.error('Calling report render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, textAlign: 'center', background: '#fff', borderRadius: 12, border: '1px solid #fecaca' }}>
          <p style={{ color: '#b91c1c', fontWeight: 700, marginBottom: 8 }}>Calling Report failed to render</p>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>{this.state.message}</p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, message: '' })}
            style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SupervisorDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [supervisor, setSupervisor] = useState(() => {
    try {
      const raw = localStorage.getItem('gogig_supervisor_data');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  });

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showPendingAccountsModal, setShowPendingAccountsModal] = useState(false);
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingTotalPages, setPendingTotalPages] = useState(1);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingSearch, setPendingSearch] = useState('');
  const [creatingAccounts, setCreatingAccounts] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Candidate creation form state
  const [showCreateCandidateModal, setShowCreateCandidateModal] = useState(false);
  const [creatingCandidate, setCreatingCandidate] = useState(false);
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    qualification: '',
    course: '',
    referral_code: '',
    establishment_name: '',
    month: '',
    punching_employee_code: '',
    aadhar_no: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    mother_name: '',
    category: '',
    address: '',
    district: '',
    state: '',
    pincode: '',
    institute_name: '',
    specialization: '',
    bank_name: '',
    account_no: '',
    ifsc_code: '',
    date_of_joining: '',
    stipend: '',
    job_role: ''
  });
  
  // Document upload state for candidate creation
  const [newCandidateDocuments, setNewCandidateDocuments] = useState({
    aadhar: null,
    school_certificate: null,
    educational_certificate: null,
    bank_passbook: null,
    candidate_image: null,
    pancard: null,
    signature: null
  });
  const [showCandidateCameraModal, setShowCandidateCameraModal] = useState(false);
  const [candidateCameraStream, setCandidateCameraStream] = useState(null);
  const [candidateCameraDocumentType, setCandidateCameraDocumentType] = useState(null);
  const candidateVideoRef = React.useRef(null);
  
  // Document management state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDocuments, setCandidateDocuments] = useState([]);
  const [documentForm, setDocumentForm] = useState({
    document_type: '',
    file: null
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [editForm, setEditForm] = useState({
    establishment_name: '',
    month: '',
    punching_code: '',
    employee_code: '',
    remark: '',
    contract_code: '',
    apprentice_code: '',
    aadhar_no: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    mother_name: '',
    category: '',
    address: '',
    district: '',
    state: '',
    pincode: '',
    qualification: '',
    institute_name: '',
    specialization: '',
    bank_name: '',
    account_no: '',
    ifsc_code: '',
    seeded_account: '',
    beni_id_status: '',
    date_of_joining: '',
    stipend: '',
    job_role: '',
    establishment_address: '',
    insurance: '',
    id_card: '',
    location: '',
    status: '',
    referral_code: ''
  });

  // Supervisor management state
  const [supervisors, setSupervisors] = useState([]);
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [supervisorForm, setSupervisorForm] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: 'supervisor',
    permissions: {}
  });
  const [editingSupervisor, setEditingSupervisor] = useState(null);
  const [supervisorLoading, setSupervisorLoading] = useState(false);
  const [currentView, setCurrentView] = useState('candidates'); // 'candidates', 'supervisors', 'my-candidates', or 'attendance'
  const isCallingReportView = currentView === 'calling-report' || currentView === 'agreed-to-join-report' || currentView === 'my-report';
  const isAgreedToJoinReport = currentView === 'agreed-to-join-report';
  const isMyReportView = currentView === 'my-report';
  
  // Attendance state
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceCameraModal, setAttendanceCameraModal] = useState(false);
  const [attendanceCameraStream, setAttendanceCameraStream] = useState(null);
  const [attendanceType, setAttendanceType] = useState(null); // 'checkin' or 'checkout'
  const [attendanceImage, setAttendanceImage] = useState(null);
  const attendanceVideoRef = React.useRef(null);
  
  // Refs to prevent concurrent API calls
  const fetchingTodayAttendance = useRef(false);
  const fetchingAttendanceHistory = useRef(false);
  
  // Refresh supervisors list when supervisor profile changes
  useEffect(() => {
    if (supervisor?.id && currentView === 'supervisors' && supervisor?.role === 'admin_supervisor') {
      fetchSupervisors();
    }
  }, [supervisor?.id]);
  
  // Supervisor candidates view state
  const [selectedSupervisorForCandidates, setSelectedSupervisorForCandidates] = useState(null);
  const [supervisorCandidates, setSupervisorCandidates] = useState([]);
  const [supervisorCandidatesLoading, setSupervisorCandidatesLoading] = useState(false);
  const [supervisorCandidatesPage, setSupervisorCandidatesPage] = useState(1);
  const [supervisorCandidatesTotalPages, setSupervisorCandidatesTotalPages] = useState(1);
  const [supervisorCandidatesSearch, setSupervisorCandidatesSearch] = useState('');
  const [showSupervisorCandidatesModal, setShowSupervisorCandidatesModal] = useState(false);
  
  // Registration link state
  const [registrationLink, setRegistrationLink] = useState('');
  const [showRegistrationLinkModal, setShowRegistrationLinkModal] = useState(false);

  // Calling report state (admin supervisor only)
  const [callingReportRows, setCallingReportRows] = useState([]);
  const [callingReportLoading, setCallingReportLoading] = useState(false);
  const [callingReportEditing, setCallingReportEditing] = useState(null); // { id, field }
  const [callingReportEditValue, setCallingReportEditValue] = useState('');
  const [callingReportSaving, setCallingReportSaving] = useState(false);
  const [callingReportPage, setCallingReportPage] = useState(1);
  const [callingReportLimit, setCallingReportLimit] = useState(25);
  const [callingReportTotal, setCallingReportTotal] = useState(0);
  const [callingReportTotalPages, setCallingReportTotalPages] = useState(0);
  const [callingReportSearchInput, setCallingReportSearchInput] = useState('');
  const [callingReportSearch, setCallingReportSearch] = useState('');
  const [callingReportCompanies, setCallingReportCompanies] = useState([]);
  const [callingReportCompanySearch, setCallingReportCompanySearch] = useState('');
  // Calling report filters
  const [callingReportDateFrom, setCallingReportDateFrom] = useState('');
  const [callingReportDateTo, setCallingReportDateTo] = useState('');
  const [callingReportQualification, setCallingReportQualification] = useState('');
  const [callingReportCompanyFilter, setCallingReportCompanyFilter] = useState('');
  const [callingReportStatus, setCallingReportStatus] = useState('');
  const [callingReportCompanyFilterSearch, setCallingReportCompanyFilterSearch] = useState('');
  const [callingReportCompanyFilterDropdownOpen, setCallingReportCompanyFilterDropdownOpen] = useState(false);
  const [callingReportState, setCallingReportState] = useState('');
  const [callingReportDistrict, setCallingReportDistrict] = useState('');
  const [callingReportSource, setCallingReportSource] = useState(''); // '', 'whatsapp', 'uploaded', 'applied'
  const [callingReportStateSearch, setCallingReportStateSearch] = useState('');
  const [callingReportDistrictSearch, setCallingReportDistrictSearch] = useState('');
  const [callingReportFiltersOpen, setCallingReportFiltersOpen] = useState(false);
  const [callingReportStateDropdownOpen, setCallingReportStateDropdownOpen] = useState(false);
  const [callingReportDistrictDropdownOpen, setCallingReportDistrictDropdownOpen] = useState(false);
  const [callingReportColumnVisibility, setCallingReportColumnVisibility] = useState(loadCallingReportColumnVisibility);
  const [callingReportColumnsMenuOpen, setCallingReportColumnsMenuOpen] = useState(false);
  const callingReportColumnsMenuRef = useRef(null);

  const isCallingReportColVisible = useCallback((key) => {
    const col = CALLING_REPORT_COLUMNS.find((c) => c.key === key);
    if (col?.required) return true;
    return callingReportColumnVisibility[key] !== false;
  }, [callingReportColumnVisibility]);

  const callingReportHiddenColumnCount = CALLING_REPORT_COLUMNS.filter(
    (c) => !c.required && callingReportColumnVisibility[c.key] === false
  ).length;

  const setCallingReportColumnVisible = useCallback((key, visible) => {
    setCallingReportColumnVisibility((prev) => {
      const next = { ...prev };
      if (visible) delete next[key];
      else next[key] = false;
      try {
        localStorage.setItem(CALLING_REPORT_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const showAllCallingReportColumns = useCallback(() => {
    setCallingReportColumnVisibility({});
    try {
      localStorage.removeItem(CALLING_REPORT_COLUMNS_STORAGE_KEY);
    } catch (_) { /* ignore */ }
  }, []);

  const crThStyle = { padding: '10px 10px', textAlign: 'left', fontWeight: 700, fontSize: '0.8rem' };

  // Campus Drive (admin supervisor only)
  const [campusDriveRows, setCampusDriveRows] = useState([]);
  const [campusDriveLoading, setCampusDriveLoading] = useState(false);
  const [campusDrivePage, setCampusDrivePage] = useState(1);
  const [campusDriveLimit, setCampusDriveLimit] = useState(25);
  const [campusDriveTotal, setCampusDriveTotal] = useState(0);
  const [campusDriveTotalPages, setCampusDriveTotalPages] = useState(0);
  const [campusDriveSearchInput, setCampusDriveSearchInput] = useState('');
  const [campusDriveSearch, setCampusDriveSearch] = useState('');
  const [campusDriveModalOpen, setCampusDriveModalOpen] = useState(false);
  const [campusDriveSubmitting, setCampusDriveSubmitting] = useState(false);
  const [campusDriveForm, setCampusDriveForm] = useState({
    campus_drive_name: '', college_name: '', college_type: '', poc_name: '', poc_email: '', poc_mobile: '',
    poc_designation: '', state: '', district: '', college_address: '', drive_date: '', total_participated: '', total_joined: ''
  });
  const [campusDriveStateDropdownOpen, setCampusDriveStateDropdownOpen] = useState(false);
  const [campusDriveDistrictDropdownOpen, setCampusDriveDistrictDropdownOpen] = useState(false);
  const [campusDriveStateSearch, setCampusDriveStateSearch] = useState('');
  const [campusDriveDistrictSearch, setCampusDriveDistrictSearch] = useState('');

  // All Jobs & Applied Candidates (admin supervisor only)
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobApplicants, setJobApplicants] = useState([]);
  const [jobApplicantsLoading, setJobApplicantsLoading] = useState(false);
  const [showJobApplicantsModal, setShowJobApplicantsModal] = useState(false);
  const [jobApplicantCounts, setJobApplicantCounts] = useState({});
  const [showHiddenSupervisorJobs, setShowHiddenSupervisorJobs] = useState(false);
  const [jobPostCompanies, setJobPostCompanies] = useState([]);
  const [showSupervisorJobForm, setShowSupervisorJobForm] = useState(false);
  const [supervisorJobForm, setSupervisorJobForm] = useState({
    title: '',
    description: '',
    age_criteria: '',
    stipend_min: '',
    stipend_max: '',
    qualification: [],
    location: '',
    company_id: ''
  });
  const [supervisorJobLogoFile, setSupervisorJobLogoFile] = useState(null);
  const [supervisorJobFormError, setSupervisorJobFormError] = useState('');
  const [supervisorJobFormSuccess, setSupervisorJobFormSuccess] = useState('');
  const [submittingSupervisorJob, setSubmittingSupervisorJob] = useState(false);
  const supervisorJobLogoInputRef = useRef(null);
  const jobPostCompanyTriggerRef = useRef(null);
  const [jobPostCompanyDropdownOpen, setJobPostCompanyDropdownOpen] = useState(false);
  const [jobPostCompanySearch, setJobPostCompanySearch] = useState('');
  const [jobPostCompanyMenuPos, setJobPostCompanyMenuPos] = useState(null);

  // Candidate status options
  const candidateStatuses = [
    'Data Record',
    'Document Pending',
    'Registration Pending',
    'Email Pending',
    'User ID Not Generated',
    'Contract Not Generated',
    'Active',
    'Inactive',
    'Complete',
    'Terminate'
  ];

  // Responsive breakpoints
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  /** Supervisor sidebar is always a fixed off-canvas drawer (hamburger) so main stays full width at every breakpoint. */

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [sidebarOpen]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  useEffect(() => {
    fetchSupervisorProfile();
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [currentPage, search]);

  // Real-time search with debounce - updates search as user types
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when search changes
      setSearch(searchInput);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentView === 'supervisors' && supervisor?.role === 'admin_supervisor') {
      fetchSupervisors();
    } else if (currentView === 'my-candidates' && supervisor?.role === 'admin_supervisor') {
      fetchCandidates();
    } else if (isCallingReportView && supervisor?.role === 'admin_supervisor') {
      // Fetch is done by the calling-report pagination effect below
    } else if (currentView === 'candidates' && supervisor?.role !== 'admin_supervisor') {
      fetchCandidates();
    }
  }, [currentView, supervisor, isCallingReportView]);

  // Calling report: debounce search; reset to page 1 when search changes
  useEffect(() => {
    const t = setTimeout(() => setCallingReportSearch(callingReportSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [callingReportSearchInput]);
  useEffect(() => {
    setCallingReportPage(1);
  }, [callingReportSearch, callingReportDateFrom, callingReportDateTo, callingReportQualification, callingReportCompanyFilter, callingReportStatus, callingReportState, callingReportDistrict, callingReportSource]);

  useEffect(() => {
    if (!callingReportColumnsMenuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (callingReportColumnsMenuRef.current && !callingReportColumnsMenuRef.current.contains(e.target)) {
        setCallingReportColumnsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [callingReportColumnsMenuOpen]);

  // Calling report/agreed report: fetch when view and page/limit/search/filters change
  useEffect(() => {
    if (isCallingReportView && supervisor?.role === 'admin_supervisor') {
      if (callingReportEditing) return;
      fetchCallingReport();
      fetchCallingReportCompanies();
    }
  }, [currentView, supervisor, isCallingReportView, callingReportPage, callingReportLimit, callingReportSearch, callingReportDateFrom, callingReportDateTo, callingReportQualification, callingReportCompanyFilter, callingReportStatus, callingReportState, callingReportDistrict, callingReportSource, callingReportEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Campus drive: debounce search, reset page when search changes
  useEffect(() => {
    const t = setTimeout(() => setCampusDriveSearch(campusDriveSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [campusDriveSearchInput]);
  useEffect(() => { setCampusDrivePage(1); }, [campusDriveSearch]);
  useEffect(() => {
    if (currentView === 'campus-drive' && supervisor?.role === 'admin_supervisor') {
      fetchCampusDrives();
    }
  }, [currentView, supervisor, campusDrivePage, campusDriveLimit, campusDriveSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentView === 'all-jobs' && supervisor?.role === 'admin_supervisor') {
      fetchAllJobs();
    }
  }, [currentView, supervisor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showSupervisorJobForm || supervisor?.role !== 'admin_supervisor') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('gogig_supervisor_access');
        const res = await fetch(`${API_URL}/api/supervisor/companies?limit=500`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!cancelled && data.success) setJobPostCompanies(data.companies || []);
      } catch {
        if (!cancelled) toast.error('Failed to load companies');
      }
    })();
    return () => { cancelled = true; };
  }, [showSupervisorJobForm, supervisor?.role]);

  const updateJobPostCompanyMenuPosition = useCallback(() => {
    const el = jobPostCompanyTriggerRef.current;
    if (!el || !jobPostCompanyDropdownOpen) {
      setJobPostCompanyMenuPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - 16;
    const maxH = Math.max(120, Math.min(320, spaceBelow));
    setJobPostCompanyMenuPos({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(220, r.width),
      maxHeight: maxH
    });
  }, [jobPostCompanyDropdownOpen]);

  useLayoutEffect(() => {
    if (!jobPostCompanyDropdownOpen) {
      setJobPostCompanyMenuPos(null);
      return undefined;
    }
    updateJobPostCompanyMenuPosition();
    const onReposition = () => updateJobPostCompanyMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [jobPostCompanyDropdownOpen, updateJobPostCompanyMenuPosition, jobPostCompanies.length]);

  const fetchAllJobs = async () => {
    setJobsLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const res = await fetch(`${API_URL}/api/supervisor/jobs`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const data = await res.json();
      if (data.success) {
        setJobs(data.jobs || []);
        const counts = {};
        (data.jobs || []).forEach((job) => { counts[job.id] = job.applicant_count ?? 0; });
        setJobApplicantCounts(counts);
      } else setJobs([]);
    } catch (e) {
      toast.error('Failed to load jobs');
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  const handleSupervisorJobVisibility = async (job, hidden) => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const res = await fetch(`${API_URL}/api/supervisor/jobs/${job.id}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ hidden })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update visibility');
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, hidden_by_user: hidden } : j)));
      toast.success(hidden ? 'Job hidden from your list' : 'Job restored to your list');
    } catch (e) {
      console.error('Error updating supervisor job visibility:', e);
      toast.error('Failed to update job visibility');
      fetchAllJobs();
    }
  };

  const supervisorHiddenJobCount = jobs.filter((j) => isSupervisorJobHidden(j)).length;
  const supervisorVisibleJobs = jobs.filter((j) => showHiddenSupervisorJobs || !isSupervisorJobHidden(j));

  const handleViewJobApplicants = async (job) => {
    setSelectedJob(job);
    setShowJobApplicantsModal(true);
    setJobApplicantsLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const res = await fetch(`${API_URL}/api/supervisor/jobs/${job.id}/applicants`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch applicants');
      const data = await res.json();
      if (data.success) {
        setJobApplicants(data.applicants || []);
        setJobApplicantCounts((prev) => ({ ...prev, [job.id]: (data.applicants || []).length }));
      } else setJobApplicants([]);
    } catch (e) {
      toast.error('Failed to load applicants');
      setJobApplicants([]);
    } finally {
      setJobApplicantsLoading(false);
    }
  };

  const handleCopyJobShareLink = async (job) => {
    try {
      const shareUrl = `${window.location.origin}/jobs/${job.id}/share`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Job sharing link copied');
    } catch (err) {
      console.error('Copy share link error:', err);
      toast.error('Failed to copy sharing link');
    }
  };

  const handleSupervisorJobFormChange = (e) => {
    const { name, value } = e.target;
    setSupervisorJobForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSupervisorJobQualToggle = (qual) => {
    setSupervisorJobForm((prev) => ({
      ...prev,
      qualification: prev.qualification.includes(qual)
        ? prev.qualification.filter((q) => q !== qual)
        : [...prev.qualification, qual]
    }));
  };

  const resetSupervisorJobFormFields = () => {
    setSupervisorJobForm({
      title: '',
      description: '',
      age_criteria: '',
      stipend_min: '',
      stipend_max: '',
      qualification: [],
      location: '',
      company_id: ''
    });
    setSupervisorJobLogoFile(null);
    if (supervisorJobLogoInputRef.current) supervisorJobLogoInputRef.current.value = '';
    setJobPostCompanyDropdownOpen(false);
    setJobPostCompanySearch('');
    setJobPostCompanyMenuPos(null);
  };

  const handleSupervisorJobSubmit = async (e) => {
    e.preventDefault();
    setSupervisorJobFormError('');
    setSupervisorJobFormSuccess('');
    if (!supervisorJobForm.title?.trim() || !supervisorJobForm.description?.trim() || !supervisorJobForm.age_criteria?.trim()
      || !supervisorJobForm.stipend_min || !supervisorJobForm.stipend_max || !supervisorJobForm.location?.trim()
      || !supervisorJobForm.company_id || supervisorJobForm.qualification.length === 0) {
      setSupervisorJobFormError('All fields are required');
      return;
    }
    try {
      setSubmittingSupervisorJob(true);
      const token = localStorage.getItem('gogig_supervisor_access');
      const formData = new FormData();
      formData.append('title', supervisorJobForm.title.trim());
      formData.append('description', supervisorJobForm.description.trim());
      formData.append('age_criteria', supervisorJobForm.age_criteria.trim());
      formData.append('stipend_min', String(parseInt(supervisorJobForm.stipend_min, 10)));
      formData.append('stipend_max', String(parseInt(supervisorJobForm.stipend_max, 10)));
      formData.append('qualification', supervisorJobForm.qualification.join(','));
      formData.append('location', supervisorJobForm.location.trim());
      formData.append('company_id', String(supervisorJobForm.company_id));
      if (supervisorJobLogoFile) {
        formData.append('logo', supervisorJobLogoFile);
      }
      const res = await fetch(`${API_URL}/api/supervisor/jobs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || 'Failed to post job');
      }
      setSupervisorJobFormSuccess('Job posted successfully!');
      resetSupervisorJobFormFields();
      setTimeout(() => {
        setShowSupervisorJobForm(false);
        setSupervisorJobFormSuccess('');
        fetchAllJobs();
      }, 1500);
    } catch (err) {
      setSupervisorJobFormError(err.message || 'Failed to post job. Please try again.');
    } finally {
      setSubmittingSupervisorJob(false);
    }
  };

  const handleApplicantStatusChange = async (appId, status) => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const res = await fetch(`${API_URL}/api/supervisor/applications/${appId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) {
        setJobApplicants(prev => prev.map(a => a.id === appId ? { ...a, status } : a));
        toast.success('Status updated');
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch (e) {
      console.error('Error updating applicant status:', e);
      toast.error('Failed to update status');
    }
  };

  const fetchCampusDrives = async () => {
    setCampusDriveLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const params = new URLSearchParams({ page: String(campusDrivePage), limit: String(campusDriveLimit) });
      if (campusDriveSearch) params.set('search', campusDriveSearch);
      const response = await fetch(`${API_URL}/api/supervisor/campus-drives?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch campus drives');
      const data = await response.json();
      if (data.success) {
        setCampusDriveRows(data.rows || []);
        setCampusDriveTotal(data.total ?? 0);
        setCampusDriveTotalPages(data.totalPages ?? 0);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load campus drives');
      setCampusDriveRows([]);
    } finally {
      setCampusDriveLoading(false);
    }
  };

  const submitCampusDrive = async () => {
    const { campus_drive_name, college_name, college_type } = campusDriveForm;
    if (!campus_drive_name?.trim() || !college_name?.trim() || !college_type?.trim()) {
      toast.error('Campus drive name, college name and college type are required');
      return;
    }
    setCampusDriveSubmitting(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/campus-drives`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          campus_drive_name: campusDriveForm.campus_drive_name.trim(),
          college_name: campusDriveForm.college_name.trim(),
          college_type: campusDriveForm.college_type.trim(),
          poc_name: campusDriveForm.poc_name?.trim() || null,
          poc_email: campusDriveForm.poc_email?.trim() || null,
          poc_mobile: campusDriveForm.poc_mobile?.trim() || null,
          poc_designation: campusDriveForm.poc_designation?.trim() || null,
          state: campusDriveForm.state?.trim() || null,
          district: campusDriveForm.district?.trim() || null,
          college_address: campusDriveForm.college_address?.trim() || null,
          drive_date: campusDriveForm.drive_date || null,
          total_participated: campusDriveForm.total_participated !== '' ? parseInt(campusDriveForm.total_participated, 10) : null,
          total_joined: campusDriveForm.total_joined !== '' ? parseInt(campusDriveForm.total_joined, 10) : null
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Campus drive added');
        setCampusDriveModalOpen(false);
        setCampusDriveForm({ campus_drive_name: '', college_name: '', college_type: '', poc_name: '', poc_email: '', poc_mobile: '', poc_designation: '', state: '', district: '', college_address: '', drive_date: '', total_participated: '', total_joined: '' });
        setCampusDriveStateSearch(''); setCampusDriveDistrictSearch('');
        fetchCampusDrives();
      } else {
        toast.error(data.message || 'Failed to add campus drive');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to add campus drive');
    } finally {
      setCampusDriveSubmitting(false);
    }
  };

  const fetchSupervisorProfile = async () => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/profile?_=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      // Check if response is ok before parsing
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // If no JSON content, return early (might be empty response)
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setSupervisor(data.supervisor);
        try {
          localStorage.setItem('gogig_supervisor_data', JSON.stringify(data.supervisor));
        } catch (_) { /* ignore */ }
        // If admin supervisor, set view to supervisors by default
        if (data.supervisor?.role === 'admin_supervisor') {
          setCurrentView('supervisors');
        } else {
          setCurrentView('candidates');
        }
      }
    } catch (error) {
      console.error('Error fetching supervisor profile:', error);
      // Only show error if it's not a network error that might be temporary
      if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
        toast.error('Failed to fetch supervisor profile');
      }
    }
  };

  const fetchCallingReport = async (opts = {}) => {
    const page = opts.page !== undefined ? opts.page : callingReportPage;
    const limit = opts.limit !== undefined ? opts.limit : callingReportLimit;
    const search = opts.search !== undefined ? opts.search : callingReportSearch;
    const dateFrom = opts.dateFrom !== undefined ? opts.dateFrom : callingReportDateFrom;
    const dateTo = opts.dateTo !== undefined ? opts.dateTo : callingReportDateTo;
    const qualification = opts.qualification !== undefined ? opts.qualification : callingReportQualification;
    const companyId = opts.companyId !== undefined ? opts.companyId : callingReportCompanyFilter;
    const callingStatus = opts.callingStatus !== undefined ? opts.callingStatus : (isAgreedToJoinReport ? 'agreed to join' : callingReportStatus);
    const district = opts.district !== undefined ? opts.district : callingReportDistrict;
    const state = opts.state !== undefined ? opts.state : callingReportState;
    const source = opts.source !== undefined ? opts.source : callingReportSource;
    const agreedToJoinOnly = opts.agreedToJoinOnly !== undefined ? opts.agreedToJoinOnly : isAgreedToJoinReport;
    const myReportOnly = opts.myReportOnly !== undefined ? opts.myReportOnly : isMyReportView;
    const excludeAgreedToJoin = opts.excludeAgreedToJoin !== undefined ? opts.excludeAgreedToJoin : isMyReportView;
    setCallingReportLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set('search', search);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (qualification) params.set('qualification', qualification);
      if (companyId) params.set('companyId', String(companyId));
      if (callingStatus) params.set('callingStatus', callingStatus);
      if (agreedToJoinOnly) params.set('agreedToJoinOnly', '1');
      if (myReportOnly) params.set('myReportOnly', '1');
      if (excludeAgreedToJoin) params.set('excludeAgreedToJoin', '1');
      if (district) params.set('district', district);
      if (state) params.set('state', state);
      if (source) params.set('source', source);
      const response = await fetch(`${API_URL}/api/supervisor/calling-report?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch calling report');
      const data = await response.json();
      if (data.success) {
        setCallingReportRows(data.rows || []);
        setCallingReportTotal(data.total ?? 0);
        setCallingReportTotalPages(data.totalPages ?? 0);
        if (opts.page !== undefined) setCallingReportPage(data.page ?? page);
        if (opts.limit !== undefined) setCallingReportLimit(data.limit ?? limit);
      } else {
        toast.error(data.message || 'Failed to load calling report');
      }
    } catch (error) {
      console.error('Fetch calling report error:', error);
      toast.error('Failed to load calling report');
      setCallingReportRows([]);
    } finally {
      setCallingReportLoading(false);
    }
  };

  const fetchCallingReportCompanies = async (search = '') => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const params = new URLSearchParams({ limit: '500' });
      if (search) params.set('search', search);
      const response = await fetch(`${API_URL}/api/supervisor/companies?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setCallingReportCompanies(data.companies || []);
    } catch (e) {
      console.error('Fetch companies error:', e);
      setCallingReportCompanies([]);
    }
  };

  const saveCallingReportCell = async (rowId, field, value, extra = {}) => {
    setCallingReportSaving(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      let body;
      if (field === 'joining_date') body = { joining_date: value || null };
      else if (field === 'employee_code') body = { employee_code: value || null };
      else if (field === 'company_id') body = { company_id: value == null || value === '' ? null : value };
      else if (field === 'calling_status') body = { calling_status: value || null };
      else if (field === 'callback_date') body = { callback_date: value || null };
      else body = { remarks: value == null ? '' : String(value).trim() || null };
      const response = await fetch(`${API_URL}/api/supervisor/calling-report/candidate/${rowId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        setCallingReportEditing(null);
        if (field === 'company_id') {
          const c = data.candidate || {};
          setCallingReportRows(prev => prev.map(r => r.id === rowId ? { ...r, company_id: c.company_id ?? null, company_name: c.company_name ?? null } : r));
        } else if (field === 'calling_status') {
          const c = data.candidate || {};
          setCallingReportRows(prev => prev.map(r => r.id === rowId ? { ...r, calling_status: c.calling_status ?? null } : r));
        } else if (field === 'callback_date') {
          const c = data.candidate || {};
          setCallingReportRows(prev => prev.map(r => r.id === rowId ? { ...r, callback_date: c.callback_date ?? null } : r));
        } else {
          const key = field === 'joining_date' ? 'joining_date' : field === 'employee_code' ? 'employee_code' : 'remarks';
          setCallingReportRows(prev => prev.map(r => r.id === rowId ? { ...r, [key]: (key === 'remarks' ? (value == null ? null : String(value).trim() || null) : value || null) } : r));
        }
        toast.success('Updated');
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Save calling report cell error:', error);
      toast.error('Update failed');
    } finally {
      setCallingReportSaving(false);
    }
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: search.trim()
      });
      
      const response = await fetch(`${API_URL}/api/supervisor/candidates?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Handle 304 Not Modified - this is a successful response, just cached
      if (response.status === 304) {
        // For 304, we don't need to parse JSON as the data hasn't changed
        // The candidates state should already be set from a previous request
        setLoading(false);
        return;
      }
      
      // Check if response is ok before parsing
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // If no JSON content, return early (might be empty response)
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Parse additional_details for each candidate
        const candidatesWithDetails = data.candidates.map(candidate => {
          let additionalDetails = {};
          if (candidate.additional_details) {
            try {
              additionalDetails = JSON.parse(candidate.additional_details);
            } catch (e) {
              console.error('Error parsing additional_details:', e);
            }
          }
          return { ...candidate, ...additionalDetails };
        });
        setCandidates(candidatesWithDetails);
        setTotalCandidates(data.total || 0);
        setTotalPages(data.totalPages || Math.ceil((data.total || 0) / 10));
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      // Only show error if it's not a network error that might be temporary
      if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
        toast.error('Failed to fetch candidates');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search is already handled by the debounced useEffect
    // This just prevents form submission from reloading the page
  };

  const handleCsvUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json().catch(() => ({
        success: false,
        message: `Upload failed (HTTP ${response.status}). Please try again or download a fresh template.`,
        errors: [],
      }));
      setUploadResult(data);
      
      if (data.imported > 0) {
        toast.success(data.message || `Imported ${data.imported} candidate(s)`);
        fetchCandidates();
        if (data.pendingAccountCreation > 0 && (!data.errors || data.errors.length === 0)) {
          setShowUploadModal(false);
          setSelectedFile(null);
          setTimeout(() => {
            toast(`${data.pendingAccountCreation} candidates imported. Create accounts in "Pending Accounts".`, {
              duration: 5000,
              icon: 'ℹ️'
            });
          }, 800);
        } else if (data.errors && data.errors.length > 0) {
          toast(`${data.failed || data.errors.length} row(s) had issues — see details below`, {
            duration: 6000,
            icon: '⚠️'
          });
        } else {
          setShowUploadModal(false);
          setSelectedFile(null);
          setUploadResult(null);
        }
      } else {
        toast.error(data.message || 'Upload failed — no rows imported');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const fail = {
        success: false,
        message: 'Upload failed. Check your network connection and that the file is a valid CSV.',
        errors: [error.message || 'Network or server error'],
      };
      setUploadResult(fail);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      setSelectedFile(file);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const downloadTemplate = () => {
    // Exact match with supervisor dashboard data columns (+ optional linking columns at end)
    const headers = [
      'Establishment', 'Month', 'Punch Code', 'Employee Code', 'Remark', 'Contract', 'Apprentice',
      'Name', 'Mobile', 'Email', 'Aadhar', 'DOB', 'Gender', 'Father', 'Mother', 'Category',
      'Address', 'District', 'State', 'Pincode', 'Qualification', 'Institute', 'Specialization',
      'Bank', 'Account No', 'IFSC', 'Seeded', 'Beni ID', 'Join Date', 'Stipend', 'Job Role',
      'Est. Address', 'Insurance', 'ID Card', 'Location', 'Referral Code',
      'Alternate Mobile', 'Course', 'Company ID', 'Establishment Code'
    ];

    const sampleData = [
      'ABC Pvt Ltd', 'January', 'PC1001', 'EMP1001', '', 'CTR001', 'APP001',
      'John Doe', '9876543330', 'john.doe@example.com', '123412341234', '1999-08-15', 'Male', 'Ravi Doe', 'Sita Doe', 'General',
      'MG Road Bengaluru', 'Bengaluru Urban', 'Karnataka', '560001', 'Bachelor of Technology', 'Indian Institute of Technology', 'Computer Science',
      'HDFC Bank', '123456789012', 'HDFC0001234', 'Yes', 'Active', '2026-01-15', '12000', 'Trainee',
      'Bengaluru HQ', 'Yes', 'Issued', 'Bengaluru', 'REF001',
      '9876543331', 'B.Tech', '1', 'EST001'
    ];

    const csvContent = [headers.join(','), sampleData.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Fetch pending candidates (without accounts)
  const fetchPendingCandidates = async (page = 1, search = '') => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '100',
        ...(search && { search })
      });
      
      const response = await fetch(`${API_URL}/api/supervisor/candidates/pending-accounts?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setPendingCandidates(data.candidates);
        setPendingTotal(data.total);
        setPendingTotalPages(data.totalPages);
        setPendingPage(data.page);
      } else {
        toast.error(data.message || 'Failed to fetch pending candidates');
      }
    } catch (error) {
      console.error('Error fetching pending candidates:', error);
      toast.error('Failed to fetch pending candidates');
    }
  };

  // Create accounts for selected candidates
  const handleCreateAccounts = async (candidateIds = null) => {
    const idsToCreate = candidateIds || Array.from(selectedPendingIds);
    
    if (idsToCreate.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }

    setCreatingAccounts(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/create-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ candidateIds: idsToCreate })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Successfully created ${data.created} accounts`);
        setSelectedPendingIds(new Set());
        fetchPendingCandidates(pendingPage, pendingSearch);
        fetchCandidates(); // Refresh main candidates list
      } else {
        toast.error(data.message || 'Failed to create accounts');
      }
    } catch (error) {
      console.error('Error creating accounts:', error);
      toast.error('Failed to create accounts');
    } finally {
      setCreatingAccounts(false);
    }
  };

  // Create accounts for all pending candidates
  const handleCreateAllAccounts = async () => {
    if (!window.confirm(`Are you sure you want to create accounts for all ${pendingTotal} pending candidates?`)) {
      return;
    }

    setCreatingAccounts(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/create-all-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ defaultPassword: 'candidate123' })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Successfully created ${data.created} accounts`);
        setSelectedPendingIds(new Set());
        fetchPendingCandidates(1, pendingSearch);
        fetchCandidates(); // Refresh main candidates list
      } else {
        toast.error(data.message || 'Failed to create accounts');
      }
    } catch (error) {
      console.error('Error creating all accounts:', error);
      toast.error('Failed to create accounts');
    } finally {
      setCreatingAccounts(false);
    }
  };

  // Toggle selection of pending candidate
  const togglePendingSelection = (candidateId) => {
    const newSelection = new Set(selectedPendingIds);
    if (newSelection.has(candidateId)) {
      newSelection.delete(candidateId);
    } else {
      newSelection.add(candidateId);
    }
    setSelectedPendingIds(newSelection);
  };

  // Select all pending candidates
  const selectAllPending = () => {
    if (selectedPendingIds.size === pendingCandidates.length) {
      setSelectedPendingIds(new Set());
    } else {
      setSelectedPendingIds(new Set(pendingCandidates.map(c => c.id)));
    }
  };

  const fetchRegistrationLink = async () => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/registration-link`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // Use window.location.origin to get the current frontend URL
        const baseUrl = window.location.origin;
        const registrationLink = `${baseUrl}/candidate-register/${data.supervisorId}`;
        setRegistrationLink(registrationLink);
        setShowRegistrationLinkModal(true);
      } else {
        toast.error(data.message || 'Failed to get registration link');
      }
    } catch (error) {
      console.error('Error fetching registration link:', error);
      toast.error('Failed to get registration link');
    }
  };

  const copyRegistrationLink = () => {
    if (registrationLink) {
      navigator.clipboard.writeText(registrationLink).then(() => {
        toast.success('Registration link copied to clipboard!');
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = registrationLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Registration link copied to clipboard!');
      });
    }
  };

  const handleEditClick = (candidate) => {
    setEditingCandidate(candidate);
    setEditForm({
      establishment_name: candidate.establishment_name || '',
      month: candidate.month || '',
      punching_code: candidate.punching_code || candidate.punching_employee_code || '',
      employee_code: candidate.employee_code || '',
      remark: candidate.remark || '',
      contract_code: candidate.contract_code || '',
      apprentice_code: candidate.apprentice_code || '',
      aadhar_no: candidate.aadhar_no || '',
      date_of_birth: candidate.date_of_birth || '',
      gender: candidate.gender || '',
      father_name: candidate.father_name || '',
      mother_name: candidate.mother_name || '',
      category: candidate.category || '',
      address: candidate.address || '',
      district: candidate.district || '',
      state: candidate.state || '',
      pincode: candidate.pincode || '',
      qualification: candidate.qualification || '',
      institute_name: candidate.institute_name || '',
      specialization: candidate.specialization || '',
      bank_name: candidate.bank_name || '',
      account_no: candidate.account_no || '',
      ifsc_code: candidate.ifsc_code || '',
      seeded_account: candidate.seeded_account || '',
      beni_id_status: candidate.beni_id_status || '',
      date_of_joining: candidate.date_of_joining || '',
      stipend: candidate.stipend || '',
      job_role: candidate.job_role || '',
      establishment_address: candidate.establishment_address || '',
      insurance: candidate.insurance || '',
      id_card: candidate.id_card || '',
      location: candidate.location || '',
      status: candidate.status || '',
      referral_code: candidate.referral_code || ''
    });
    setShowEditModal(true);
  };

  // Document Management Functions
  const fetchCandidateDocuments = async (candidateId) => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/${candidateId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCandidateDocuments(data.documents);
      } else {
        toast.error(data.message || 'Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    }
  };

  const handleManageDocuments = (candidate) => {
    setSelectedCandidate(candidate);
    setDocumentForm({ document_type: '', file: null });
    fetchCandidateDocuments(candidate.id);
    setShowDocumentModal(true);
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!documentForm.file || !documentForm.document_type) {
      toast.error('Please select a document type and file');
      return;
    }

    setUploadingDocument(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const formData = new FormData();
      formData.append('document', documentForm.file);
      formData.append('document_type', documentForm.document_type);

      const response = await fetch(`${API_URL}/api/supervisor/candidates/${selectedCandidate.id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Document uploaded successfully!');
        setDocumentForm({ document_type: '', file: null });
        fetchCandidateDocuments(selectedCandidate.id);
        // Refresh the candidates list to update status
        fetchCandidates();
      } else {
        toast.error(data.message || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/${selectedCandidate.id}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Document deleted successfully!');
        fetchCandidateDocuments(selectedCandidate.id);
        // Refresh the candidates list to update status
        fetchCandidates();
      } else {
        toast.error(data.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/${selectedCandidate.id}/documents/${docId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="?([^"]+)"?/i);
        a.download = match?.[1] || fileName || 'document.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Document downloaded');
      } else {
        let message = 'Failed to download document';
        try {
          const data = await response.json();
          if (data?.message) message = data.message;
        } catch (_) { /* non-JSON body */ }
        toast.error(message);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  // Camera functions for mobile document scanning
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setCameraStream(stream);
      setShowCameraModal(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera-video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], `captured_${Date.now()}.jpg`, { type: 'image/jpeg' });
      setDocumentForm({ ...documentForm, file });
      stopCamera();
      toast.success('Photo captured successfully!');
    }, 'image/jpeg', 0.8);
  };

  // Document upload handlers for new candidate creation
  const handleNewCandidateDocumentChange = (docType, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid file (Image, PDF, or DOCX)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size should be less than 10MB');
      return;
    }
    
    setNewCandidateDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
    toast.success(`${docType.replace('_', ' ')} uploaded successfully`);
  };

  const handleRemoveNewCandidateDocument = (docType) => {
    setNewCandidateDocuments(prev => ({
      ...prev,
      [docType]: null
    }));
  };

  // Camera functions for new candidate document capture
  const startCandidateCamera = async (docType) => {
    try {
      const facingMode = docType === 'candidate_image' ? 'user' : 'environment';
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setCandidateCameraStream(stream);
      setCandidateCameraDocumentType(docType);
      setShowCandidateCameraModal(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const stopCandidateCamera = () => {
    if (candidateCameraStream) {
      candidateCameraStream.getTracks().forEach(track => track.stop());
      setCandidateCameraStream(null);
    }
    setShowCandidateCameraModal(false);
    setCandidateCameraDocumentType(null);
  };

  const captureCandidatePhoto = () => {
    if (!candidateVideoRef.current) return;
    
    const video = candidateVideoRef.current;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], `captured_${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleNewCandidateDocumentChange(candidateCameraDocumentType, file);
      stopCandidateCamera();
      toast.success('Photo captured successfully!');
    }, 'image/jpeg', 0.8);
  };

  useEffect(() => {
    if (showCandidateCameraModal && candidateVideoRef.current && candidateCameraStream) {
      candidateVideoRef.current.srcObject = candidateCameraStream;
    }
    return () => {
      if (candidateCameraStream) {
        candidateCameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [showCandidateCameraModal, candidateCameraStream]);

  // Handle candidate creation with documents
  const handleCreateCandidate = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!candidateForm.name || !candidateForm.email || !candidateForm.mobile) {
      toast.error('Name, email, and mobile are required fields');
      return;
    }

    // Validate that at least one document is uploaded (mandatory)
    const hasDocuments = Object.values(newCandidateDocuments).some(doc => doc !== null);
    if (!hasDocuments) {
      toast.error('At least one document is required. Please upload at least one document.');
      return;
    }

    setCreatingCandidate(true);

    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(candidateForm).forEach(key => {
        if (candidateForm[key] !== null && candidateForm[key] !== undefined && candidateForm[key] !== '') {
          formDataToSend.append(key, candidateForm[key]);
        }
      });
      
      // Add documents
      if (newCandidateDocuments.aadhar) formDataToSend.append('aadhar', newCandidateDocuments.aadhar);
      if (newCandidateDocuments.school_certificate) formDataToSend.append('school_certificate', newCandidateDocuments.school_certificate);
      if (newCandidateDocuments.educational_certificate) formDataToSend.append('educational_certificate', newCandidateDocuments.educational_certificate);
      if (newCandidateDocuments.bank_passbook) formDataToSend.append('bank_passbook', newCandidateDocuments.bank_passbook);
      if (newCandidateDocuments.candidate_image) formDataToSend.append('candidate_image', newCandidateDocuments.candidate_image);
      if (newCandidateDocuments.pancard) formDataToSend.append('pancard', newCandidateDocuments.pancard);
      if (newCandidateDocuments.signature) formDataToSend.append('signature', newCandidateDocuments.signature);
      
      const response = await fetch(`${API_URL}/api/supervisor/candidates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Candidate created successfully! ${data.documents_uploaded} document(s) uploaded.`);
        setShowCreateCandidateModal(false);
        // Reset form
        setCandidateForm({
          name: '',
          email: '',
          mobile: '',
          password: '',
          qualification: '',
          course: '',
          referral_code: '',
          establishment_name: '',
          month: '',
          punching_employee_code: '',
          aadhar_no: '',
          date_of_birth: '',
          gender: '',
          father_name: '',
          mother_name: '',
          category: '',
          address: '',
          district: '',
          state: '',
          pincode: '',
          institute_name: '',
          specialization: '',
          bank_name: '',
          account_no: '',
          ifsc_code: '',
          date_of_joining: '',
          stipend: '',
          job_role: ''
        });
        setNewCandidateDocuments({
          aadhar: null,
          school_certificate: null,
          educational_certificate: null,
          bank_passbook: null,
          candidate_image: null,
          pancard: null,
          signature: null
        });
        fetchCandidates();
      } else {
        toast.error(data.message || 'Failed to create candidate');
      }
    } catch (error) {
      console.error('Error creating candidate:', error);
      toast.error('Failed to create candidate: ' + error.message);
    } finally {
      setCreatingCandidate(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/candidates/${editingCandidate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Candidate updated successfully');
        setShowEditModal(false);
        setEditingCandidate(null);
        fetchCandidates(); // Refresh the list
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Update failed');
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      await fetch(`${API_URL}/api/supervisor/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      if (onLogout) onLogout();
    }
  };

  // Supervisor management functions
  const fetchSupervisors = async () => {
    setSupervisorLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/manage-supervisors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        // Filter out the current logged-in admin supervisor from the list
        const filteredSupervisors = data.supervisors.filter(s => s.id !== supervisor?.id);
        setSupervisors(filteredSupervisors);
      } else {
        toast.error('Failed to fetch supervisors');
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      toast.error('Failed to fetch supervisors');
    } finally {
      setSupervisorLoading(false);
    }
  };

  const handleCreateSupervisor = async (e) => {
    e.preventDefault();
    setSupervisorLoading(true);
    
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/manage-supervisors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(supervisorForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Supervisor created successfully');
        setShowSupervisorModal(false);
        setSupervisorForm({
          name: '',
          email: '',
          mobile: '',
          password: '',
          role: 'supervisor',
          permissions: {}
        });
        fetchSupervisors();
      } else {
        toast.error(data.message || 'Failed to create supervisor');
      }
    } catch (error) {
      console.error('Error creating supervisor:', error);
      toast.error('Failed to create supervisor');
    } finally {
      setSupervisorLoading(false);
    }
  };

  const handleEditSupervisor = async (e) => {
    e.preventDefault();
    setSupervisorLoading(true);
    
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/manage-supervisors/${editingSupervisor.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(supervisorForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Supervisor updated successfully');
        setShowSupervisorModal(false);
        setEditingSupervisor(null);
        setSupervisorForm({
          name: '',
          email: '',
          mobile: '',
          password: '',
          role: 'supervisor',
          permissions: {}
        });
        fetchSupervisors();
      } else {
        toast.error(data.message || 'Failed to update supervisor');
      }
    } catch (error) {
      console.error('Error updating supervisor:', error);
      toast.error('Failed to update supervisor');
    } finally {
      setSupervisorLoading(false);
    }
  };

  const handleDeleteSupervisor = async (supervisorId) => {
    if (!window.confirm('Are you sure you want to delete this supervisor?')) {
      return;
    }
    
    setSupervisorLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const response = await fetch(`${API_URL}/api/supervisor/manage-supervisors/${supervisorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Supervisor deleted successfully');
        fetchSupervisors();
      } else {
        toast.error(data.message || 'Failed to delete supervisor');
      }
    } catch (error) {
      console.error('Error deleting supervisor:', error);
      toast.error('Failed to delete supervisor');
    } finally {
      setSupervisorLoading(false);
    }
  };

  const openSupervisorModal = (supervisor = null) => {
    if (supervisor) {
      setEditingSupervisor(supervisor);
      setSupervisorForm({
        name: supervisor.name,
        email: supervisor.email,
        mobile: supervisor.mobile,
        password: '',
        role: supervisor.role,
        permissions: supervisor.permissions || {}
      });
    } else {
      setEditingSupervisor(null);
      setSupervisorForm({
        name: '',
        email: '',
        mobile: '',
        password: '',
        role: 'supervisor',
        permissions: {}
      });
    }
    setShowSupervisorModal(true);
  };

  // Fetch candidates for a specific supervisor
  const fetchSupervisorCandidates = async (supervisorId, page = 1, search = '') => {
    setSupervisorCandidatesLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const params = new URLSearchParams({
        page: page,
        limit: 10,
        search: search
      });
      
      const response = await fetch(`${API_URL}/api/supervisor/manage-supervisors/${supervisorId}/candidates?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Parse additional_details for each candidate
        const candidatesWithDetails = data.candidates.map(candidate => {
          let additionalDetails = {};
          if (candidate.additional_details) {
            try {
              additionalDetails = JSON.parse(candidate.additional_details);
            } catch (e) {
              console.error('Error parsing additional_details:', e);
            }
          }
          return { ...candidate, ...additionalDetails };
        });
        
        setSupervisorCandidates(candidatesWithDetails);
        setSupervisorCandidatesTotalPages(data.totalPages);
      } else {
        toast.error('Failed to fetch candidates');
      }
    } catch (error) {
      console.error('Error fetching supervisor candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setSupervisorCandidatesLoading(false);
    }
  };

  // Open supervisor candidates modal
  const handleViewSupervisorCandidates = (supervisor) => {
    setSelectedSupervisorForCandidates(supervisor);
    setSupervisorCandidatesPage(1);
    setSupervisorCandidatesSearch('');
    setShowSupervisorCandidatesModal(true);
    fetchSupervisorCandidates(supervisor.id, 1, '');
  };

  // Handle supervisor candidates search/pagination
  useEffect(() => {
    if (showSupervisorCandidatesModal && selectedSupervisorForCandidates) {
      fetchSupervisorCandidates(
        selectedSupervisorForCandidates.id,
        supervisorCandidatesPage,
        supervisorCandidatesSearch
      );
    }
  }, [supervisorCandidatesPage, supervisorCandidatesSearch, showSupervisorCandidatesModal]);

  // ==================== ATTENDANCE FUNCTIONS ====================
  
  // Fetch today's attendance status
  const fetchTodayAttendance = useCallback(async () => {
    // Prevent concurrent calls
    if (fetchingTodayAttendance.current) {
      return;
    }
    
    fetchingTodayAttendance.current = true;
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      // Add timestamp to prevent caching
      const response = await fetch(`${API_URL}/api/supervisor/attendance/today?t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        cache: 'no-store'
      });
      
      // Handle 304 Not Modified - this is a successful response, just cached
      // For 304, we don't need to parse JSON as the data hasn't changed
      // However, if we're forcing a refresh (e.g., after check-in), we should still fetch
      if (response.status === 304) {
        fetchingTodayAttendance.current = false;
        return;
      }
      
      // Check if response is ok before parsing
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        fetchingTodayAttendance.current = false;
        return;
      }
      
      const data = await response.json();
      console.log('Today attendance API response:', data);
      if (data.success) {
        // Handle both null and object responses
        if (data.attendance) {
          console.log('Today attendance state updated:', data.attendance);
          setTodayAttendance(data.attendance);
        } else {
          console.log('No attendance found for today, setting to null');
          setTodayAttendance(null);
        }
      } else {
        console.error('Failed to fetch today attendance:', data);
      }
    } catch (error) {
      console.error('Error fetching today attendance:', error);
      // Only show error if it's not a network error that might be temporary
      if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
        toast.error('Failed to fetch attendance status');
      }
    } finally {
      fetchingTodayAttendance.current = false;
    }
  }, []);

  // Fetch attendance history
  const fetchAttendanceHistory = useCallback(async () => {
    // Prevent concurrent calls
    if (fetchingAttendanceHistory.current) {
      return;
    }
    
    fetchingAttendanceHistory.current = true;
    setAttendanceLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const currentDate = new Date();
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      // Add timestamp to prevent caching
      const response = await fetch(`${API_URL}/api/supervisor/attendance?month=${month}&year=${year}&t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        cache: 'no-store'
      });
      
      // Handle 304 Not Modified - this is a successful response, just cached
      if (response.status === 304) {
        // For 304, we don't need to parse JSON as the data hasn't changed
        setAttendanceLoading(false);
        fetchingAttendanceHistory.current = false;
        return;
      }
      
      // Check if response is ok before parsing
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setAttendanceLoading(false);
        fetchingAttendanceHistory.current = false;
        return;
      }
      
      const data = await response.json();
      console.log('Attendance history API response:', data);
      if (data.success) {
        const attendanceArray = data.attendance || [];
        console.log('Attendance history state updated:', attendanceArray.length, 'records');
        console.log('Attendance records:', attendanceArray);
        setAttendanceHistory(attendanceArray);
      } else {
        console.error('Failed to fetch attendance history:', data);
        setAttendanceHistory([]);
      }
    } catch (error) {
      console.error('Error fetching attendance history:', error);
      // Only show error if it's not a network error that might be temporary
      if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
        toast.error('Failed to fetch attendance history');
      }
    } finally {
      setAttendanceLoading(false);
      fetchingAttendanceHistory.current = false;
    }
  }, []);

  // Start attendance camera
  const startAttendanceCamera = async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user', // Front camera for selfie
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setAttendanceCameraStream(stream);
      setAttendanceType(type);
      setAttendanceCameraModal(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  // Stop attendance camera
  const stopAttendanceCamera = () => {
    console.log('=== FRONTEND: stopAttendanceCamera called ===');
    console.log('Current attendanceType:', attendanceType);
    if (attendanceCameraStream) {
      attendanceCameraStream.getTracks().forEach(track => track.stop());
      setAttendanceCameraStream(null);
    }
    setAttendanceCameraModal(false);
    // Don't clear attendanceType here - we need it for the submit button condition
    // setAttendanceType(null); // Commented out - keep type for button rendering
    console.log('Camera stopped, keeping attendanceType:', attendanceType);
  };

  // Capture attendance photo
  const captureAttendancePhoto = () => {
    console.log('=== FRONTEND: captureAttendancePhoto called ===');
    if (!attendanceVideoRef.current) {
      console.error('No video ref available!');
      return;
    }
    
    const video = attendanceVideoRef.current;
    console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    console.log('Converting canvas to blob...');
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Failed to create blob from canvas!');
        toast.error('Failed to capture photo');
        return;
      }
      console.log('Blob created, size:', blob.size, 'type:', blob.type);
      const file = new File([blob], `attendance_${Date.now()}.jpg`, { type: 'image/jpeg' });
      console.log('File created:', file.name, file.size, file.type);
      setAttendanceImage(file);
      console.log('attendanceImage state set, attendanceType:', attendanceType);
      stopAttendanceCamera();
      toast.success('Photo captured successfully!');
    }, 'image/jpeg', 0.8);
  };

  // Handle check-in
  const handleCheckIn = async () => {
    console.log('=== FRONTEND: handleCheckIn called ===');
    console.log('attendanceImage:', attendanceImage);
    console.log('attendanceType:', attendanceType);
    console.log('todayAttendance:', todayAttendance);
    
    if (!attendanceImage) {
      console.error('No attendance image!');
      toast.error('Please capture your photo first');
      return;
    }

    console.log('handleCheckIn proceeding, attendanceImage type:', typeof attendanceImage, attendanceImage instanceof File ? 'File' : 'Blob');
    setAttendanceLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      if (!token) {
        toast.error('Not authenticated. Please login again.');
        setAttendanceLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('image', attendanceImage);
      console.log('FormData created, image file:', attendanceImage.name, attendanceImage.size);
      
      // Get location if available
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('Location obtained:', position.coords);
            formData.append('latitude', position.coords.latitude);
            formData.append('longitude', position.coords.longitude);
            submitCheckIn(formData, token);
          },
          (error) => {
            console.log('Location not available:', error);
            // Location not available, submit without it
            submitCheckIn(formData, token);
          },
          { timeout: 5000 }
        );
      } else {
        console.log('Geolocation not supported');
        submitCheckIn(formData, token);
      }
    } catch (error) {
      console.error('Error during check-in:', error);
      toast.error('Failed to check in: ' + error.message);
      setAttendanceLoading(false);
    }
  };

  const submitCheckIn = async (formData, token) => {
    try {
      console.log('=== FRONTEND: submitCheckIn called ===');
      console.log('API_URL:', API_URL);
      console.log('Full URL:', `${API_URL}/api/supervisor/attendance/checkin`);
      console.log('Token exists:', !!token, token ? token.substring(0, 20) + '...' : 'none');
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? `File: ${pair[1].name}, size: ${pair[1].size}` : pair[1]));
      }
      
      console.log('About to send fetch request...');
      const response = await fetch(`${API_URL}/api/supervisor/attendance/checkin`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData, browser will set it with boundary
        },
        body: formData
      });
      console.log('Fetch request sent, response received');
      
      console.log('Check-in response status:', response.status);
      console.log('Check-in response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Check-in failed with status:', response.status, 'Error:', errorText);
        toast.error(`Check-in failed: ${response.status} ${response.statusText}`);
        setAttendanceLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('Check-in response data:', data);
      
      if (data.success) {
        toast.success('Check-in successful!');
        // Clear image and type only after successful check-in
        setAttendanceImage(null);
        setAttendanceType(null);
        console.log('Check-in successful, cleared attendanceImage and attendanceType');
        
        // Update state directly from response if available
        if (data.attendance) {
          console.log('Updating attendance state from check-in response:', data.attendance);
          // Ensure we have the correct data structure
          const attendanceData = {
            id: data.attendance.id,
            date: data.attendance.date,
            check_in_time: data.attendance.check_in_time,
            check_in_image: data.attendance.check_in_image,
            check_in_latitude: data.attendance.check_in_latitude,
            check_in_longitude: data.attendance.check_in_longitude,
            check_out_time: data.attendance.check_out_time || null,
            check_out_image: data.attendance.check_out_image || null,
            status: data.attendance.status || 'checked_in'
          };
          console.log('Setting today attendance to:', attendanceData);
          setTodayAttendance(attendanceData);
        } else {
          console.log('No attendance in response, will fetch');
        }
        
        // Always refresh to get latest data and update history
        // Reset refs to ensure fetch can proceed
        fetchingTodayAttendance.current = false;
        fetchingAttendanceHistory.current = false;
        // Use a small delay to ensure backend has saved the data
        setTimeout(() => {
          console.log('Fetching attendance after check-in...');
          // Force fetch by resetting refs again
          fetchingTodayAttendance.current = false;
          fetchingAttendanceHistory.current = false;
          fetchTodayAttendance();
          fetchAttendanceHistory();
        }, 800);
      } else {
        toast.error(data.message || 'Failed to check in');
        console.error('Check-in failed:', data);
      }
    } catch (error) {
      console.error('Error submitting check-in:', error);
      console.error('Error stack:', error.stack);
      toast.error('Failed to check in: ' + error.message);
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Handle check-out
  const handleCheckOut = async () => {
    if (!attendanceImage) {
      toast.error('Please capture your photo first');
      return;
    }

    setAttendanceLoading(true);
    try {
      const token = localStorage.getItem('gogig_supervisor_access');
      const formData = new FormData();
      formData.append('image', attendanceImage);
      
      // Get location if available
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            formData.append('latitude', position.coords.latitude);
            formData.append('longitude', position.coords.longitude);
            submitCheckOut(formData, token);
          },
          () => {
            // Location not available, submit without it
            submitCheckOut(formData, token);
          }
        );
      } else {
        submitCheckOut(formData, token);
      }
    } catch (error) {
      console.error('Error during check-out:', error);
      toast.error('Failed to check out');
      setAttendanceLoading(false);
    }
  };

  const submitCheckOut = async (formData, token) => {
    try {
      const response = await fetch(`${API_URL}/api/supervisor/attendance/checkout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Check-out successful!');
        setAttendanceImage(null);
        setAttendanceType(null);
        
        // Check-out response doesn't include attendance object, so we must fetch
        console.log('Check-out successful, fetching updated attendance...');
        
        // Always refresh to get latest data and update history
        fetchingTodayAttendance.current = false;
        fetchingAttendanceHistory.current = false;
        // Use a small delay to ensure backend has saved the data
        setTimeout(() => {
          console.log('Fetching attendance after check-out...');
          fetchTodayAttendance();
          fetchAttendanceHistory();
        }, 500);
      } else {
        toast.error(data.message || 'Failed to check out');
        console.error('Check-out failed:', data);
      }
    } catch (error) {
      console.error('Error submitting check-out:', error);
      toast.error('Failed to check out: ' + error.message);
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Track if we've fetched attendance for the current view
  const attendanceFetchedForView = useRef(false);
  
  // Fetch attendance when view changes to attendance
  useEffect(() => {
    if (currentView === 'attendance') {
      // Only fetch if we haven't fetched for this view yet, or if view changed
      if (!attendanceFetchedForView.current) {
        attendanceFetchedForView.current = true;
        fetchTodayAttendance();
        fetchAttendanceHistory();
      }
    } else {
      // Reset the flag when leaving attendance view
      attendanceFetchedForView.current = false;
    }
  }, [currentView, fetchTodayAttendance, fetchAttendanceHistory]);

  // Setup video stream for attendance camera
  useEffect(() => {
    if (attendanceCameraModal && attendanceVideoRef.current && attendanceCameraStream) {
      attendanceVideoRef.current.srcObject = attendanceCameraStream;
    }
    return () => {
      if (attendanceCameraStream) {
        attendanceCameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [attendanceCameraModal, attendanceCameraStream]);

  // Nav items for Admin-style sidebar (same order and visibility as before)
  const supervisorNavItems = [
    ...(supervisor?.role !== 'admin_supervisor' ? [{ key: 'candidates', label: 'Candidates', icon: <FaUsers />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'my-candidates', label: 'My Candidates', icon: <FaUsers />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'supervisors', label: 'Supervisors', icon: <FaUsers />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'calling-report', label: 'Calling Report', icon: <FaFileAlt />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'my-report', label: 'My Report', icon: <FaFileAlt />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'agreed-to-join-report', label: 'Agreed To Join', icon: <FaFileAlt />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'campus-drive', label: 'Campus Drive', icon: <FaUserGraduate />, type: 'view' }] : []),
    ...(supervisor?.role === 'admin_supervisor' ? [{ key: 'all-jobs', label: 'All Jobs', icon: <FaBriefcase />, type: 'view' }] : []),
    { key: 'work-diary', label: 'Work Diary', icon: <FaFileAlt />, type: 'link' },
    { key: 'job-interest', label: 'Job Interest', icon: <FaBriefcase />, type: 'job-interest' },
    { key: 'attendance', label: 'Attendance', icon: <FaClock />, type: 'view' },
    ...((supervisor?.role !== 'admin_supervisor' || currentView === 'my-candidates') ? [{ key: 'upload-csv', label: 'Upload CSV', icon: <FaUpload />, type: 'action' }] : []),
  ];

  return (
    <div className="admin-layout supervisor-dashboard-root" style={{ display: 'block', minHeight: '100vh' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Toaster
        position={isMobile ? "top-center" : "top-right"}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#0f172a',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
            borderRadius: '12px',
            padding: '16px 20px',
          },
          success: { style: { background: '#f0fdfa', color: '#0f766e' } },
          error: { style: { background: '#fef2f2', color: '#991b1b' } },
        }}
      />

      {/* Mobile-specific styles */}
      <style jsx>{`
        @media (max-width: 768px) {
          .gogig-modal-bg {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 16px;
          }
          
          .gogig-modal {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
            position: relative;
            max-height: 90vh;
            overflow-y: auto;
          }
        }
      `}</style>

      {/* Drawer backdrop */}
      {sidebarOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 998,
          }}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed drawer (not in flex flow) so it never reserves horizontal space */}
      <div
        className="sidebar-container supervisor-sidebar-drawer"
        aria-hidden={!sidebarOpen}
        style={{
          position: 'fixed',
          top: 0,
          left: sidebarOpen ? 0 : -280,
          width: 280,
          height: '100vh',
          zIndex: 999,
          transition: 'left 0.28s ease, box-shadow 0.28s ease',
          overflow: 'hidden',
          boxShadow: sidebarOpen ? '8px 0 32px rgba(0,0,0,0.2)' : 'none',
          pointerEvents: sidebarOpen ? 'auto' : 'none'
        }}
      >
        <aside className="admin-sidebar" style={{ width: '100%', height: '100%', position: 'relative' }}>
          {sidebarOpen && (
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              style={{
                position: 'absolute', top: 20, right: 20, width: 32, height: 32, borderRadius: '50%',
                border: 'none', background: 'rgba(255,255,255,0.9)', color: '#0d9488', cursor: 'pointer',
                zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          )}
          <div className="admin-sidebar-brand">
            <img src="/logo512.png" alt="GoGig Logo" className="admin-sidebar-brand-logo" />
            <span className="admin-sidebar-brand-title">GoGig Supervisor</span>
            <span className="admin-sidebar-brand-badge">Dashboard</span>
            {supervisor?.name && (
              <div style={{
                marginTop: 12,
                padding: '10px 12px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.12)',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
                  {supervisor.name}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 4, lineHeight: 1.35 }}>
                  {supervisor.company_name
                    || supervisor.companies?.[0]?.company_name
                    || (supervisor.company_id ? `Company #${supervisor.company_id}` : 'No company assigned')}
                </div>
              </div>
            )}
          </div>
          <nav className="admin-sidebar-nav">
            {supervisorNavItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`admin-sidebar-nav-btn ${item.type === 'view' && currentView === item.key ? 'active' : ''}`}
                onClick={() => {
                  if (item.type === 'view') { setCurrentView(item.key); setSidebarOpen(false); }
                  else if (item.type === 'link') { navigate('/supervisor/work-diaries'); setSidebarOpen(false); }
                  else if (item.type === 'job-interest') { navigate('/supervisor/job-interest'); setSidebarOpen(false); }
                  else if (item.type === 'action') { setShowUploadModal(true); setSidebarOpen(false); }
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <button type="button" className="admin-sidebar-logout" onClick={handleLogout}>Logout</button>
        </aside>
      </div>

      {/* Main content — full width (sidebar is off-canvas, not beside this in flex) */}
      <div
        className="admin-main supervisor-main-fullwidth"
        style={{
          maxWidth: '100%',
          width: '100%',
          margin: 0,
          padding: isMobile ? '20px 16px 28px' : '24px 28px 32px',
          minWidth: 0,
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'flex-start', 
          marginBottom: isMobile ? 24 : 32,
          gap: isMobile ? 16 : 20,
          width: '100%',
          minWidth: 0,
          overflow: 'visible'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12, 
            flex: 1,
            minWidth: 0,
            width: '100%',
            maxWidth: '100%'
          }}>
            <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  color: '#0f172a',
                  fontSize: 20,
                  cursor: 'pointer',
                  padding: 10,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                }}
              >
                <FaBars />
              </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                fontSize: isMobile ? 24 : (isTablet ? 28 : 32), 
                fontWeight: 800, 
                color: '#0f172a', 
                margin: 0, 
                marginBottom: isMobile ? 4 : 8,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {currentView === 'candidates' ? 'Candidate Management' 
                  : currentView === 'my-candidates' ? 'My Candidates'
                  : currentView === 'attendance' ? 'My Attendance'
                  : currentView === 'calling-report' ? 'Calling Report'
                  : currentView === 'my-report' ? 'My Report'
                  : currentView === 'agreed-to-join-report' ? 'Agreed To Join Report'
                  : currentView === 'campus-drive' ? 'Campus Drive'
                  : currentView === 'all-jobs' ? 'All Jobs'
                  : 'Supervisor Management'}
              </h1>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                marginBottom: isMobile ? 6 : 8
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: '#ecfeff',
                  border: '1px solid #a5f3fc',
                  color: '#0e7490',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 700
                }}>
                  {supervisor?.name || 'Supervisor'}
                </span>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#334155',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 600,
                  maxWidth: '100%'
                }} title={supervisor?.establishment_code ? `Est: ${supervisor.establishment_code}` : undefined}>
                  {supervisor?.company_name
                    || supervisor?.companies?.[0]?.company_name
                    || (supervisor?.company_id ? `Company #${supervisor.company_id}` : 'No company assigned')}
                </span>
              </div>
              <p style={{ 
                fontSize: isMobile ? 14 : 16, 
                color: '#64748b', 
                margin: 0,
                display: isMobile ? 'none' : 'block',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}>
                {currentView === 'candidates' 
                  ? 'Manage and view all candidates uploaded by you'
                  : currentView === 'my-candidates'
                  ? 'View and manage your own imported candidates'
                  : currentView === 'attendance'
                  ? 'Mark your daily attendance with check-in and check-out'
                  : currentView === 'calling-report'
                  ? 'Supervisor uploads and job applicants to call (excluding employed and contract-matched)'
                  : currentView === 'my-report'
                  ? 'Candidates updated by you from calling report (excluding agreed to join)'
                  : currentView === 'agreed-to-join-report'
                  ? 'Candidates marked as agreed to join by you'
                  : currentView === 'campus-drive'
                  ? 'Manage campus drive events and placements'
                  : currentView === 'all-jobs'
                  ? 'View all posted jobs and applied candidates'
                  : 'Manage and view all supervisors in the system'
                }
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: 10,
            alignItems: 'stretch',
            flexDirection: 'column',
            width: isMobile ? '100%' : ((currentView === 'candidates' || currentView === 'my-candidates') ? 420 : 220),
            minWidth: isMobile ? 'auto' : ((currentView === 'candidates' || currentView === 'my-candidates') ? 420 : 220),
            maxWidth: isMobile ? '100%' : ((currentView === 'candidates' || currentView === 'my-candidates') ? 420 : 220),
            flexShrink: 0,
            alignSelf: isMobile ? 'stretch' : 'flex-start',
            overflow: 'visible',
            boxSizing: 'border-box'
          }}>
            {isCallingReportView ? (
              <button
                type="button"
                onClick={fetchCallingReport}
                disabled={callingReportLoading}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #2ad2c9 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 600,
                  cursor: callingReportLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontSize: isMobile ? 14 : 15,
                  width: '100%',
                  height: 44,
                  flexShrink: 0,
                  opacity: callingReportLoading ? 0.7 : 1
                }}
              >
                {callingReportLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 18,
                      height: 18,
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Loading...
                  </span>
                ) : (
                  <>Refresh</>
                )}
              </button>
            ) : currentView === 'all-jobs' ? (
              <button
                type="button"
                onClick={fetchAllJobs}
                disabled={jobsLoading}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #2ad2c9 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 600,
                  cursor: jobsLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontSize: isMobile ? 14 : 15,
                  width: '100%',
                  height: 44,
                  opacity: jobsLoading ? 0.7 : 1
                }}
              >
                {jobsLoading ? 'Loading...' : 'Refresh'}
              </button>
            ) : currentView === 'campus-drive' ? (
              <button
                type="button"
                onClick={() => setCampusDriveModalOpen(true)}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #2ad2c9 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontSize: isMobile ? 14 : 15,
                  width: '100%',
                  height: 44,
                  flexShrink: 0
                }}
              >
                <FaUserGraduate /> Add Campus Drive
              </button>
            ) : (currentView === 'candidates' || currentView === 'my-candidates') ? (
              <>
                <button
                  type="button"
                  onClick={fetchRegistrationLink}
                  style={{
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    fontSize: isMobile ? 14 : 15,
                    width: '100%',
                    height: 44,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    margin: 0,
                    outline: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <FaLink style={{ fontSize: isMobile ? 14 : 15, flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Get Registration Link</span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/supervisor/job-interest')}
                  style={{
                    padding: '10px 16px',
                    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    fontSize: isMobile ? 14 : 15,
                    width: '100%',
                    height: 44,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    margin: 0,
                    outline: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <FaBriefcase style={{ fontSize: isMobile ? 14 : 15, flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Job Interest</span>
                </button>
                
                <button
                  type="button"
                  onClick={downloadTemplate}
                  style={{
                    padding: '10px 16px',
                    background: '#f8fafc',
                    color: '#667eea',
                    border: '2px solid #667eea',
                    borderRadius: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    fontSize: isMobile ? 14 : 15,
                    width: '100%',
                    height: 44,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    margin: 0,
                    outline: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <FaDownload style={{ fontSize: isMobile ? 14 : 15, flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Download Template</span>
                </button>
                
                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 10, width: '100%' }}>
                  <button
                    type="button"
                    onClick={() => setShowCreateCandidateModal(true)}
                    style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: '#fff',
                      border: '2px solid transparent',
                      borderRadius: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: isMobile ? 14 : 15,
                      flex: 1,
                      minWidth: 100,
                      minHeight: 48,
                      boxSizing: 'border-box',
                      margin: 0,
                      outline: 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FaUsers style={{ fontSize: isMobile ? 14 : 15, flexShrink: 0 }} />
                    <span>Add Candidate</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(true)}
                    style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: '2px solid transparent',
                      borderRadius: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: isMobile ? 14 : 15,
                      flex: 1,
                      minWidth: 100,
                      minHeight: 48,
                      boxSizing: 'border-box',
                      margin: 0,
                      outline: 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FaUpload style={{ fontSize: isMobile ? 14 : 15, flexShrink: 0 }} />
                    <span>Upload CSV</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPendingAccountsModal(true);
                      fetchPendingCandidates(1, '');
                    }}
                    style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: '#fff',
                      border: '2px solid transparent',
                      borderRadius: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: isMobile ? 14 : 15,
                      flex: 1,
                      minWidth: 100,
                      minHeight: 48,
                      boxSizing: 'border-box',
                      margin: 0,
                      outline: 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FaUserCheck style={{ fontSize: isMobile ? 14 : 15, flexShrink: 0 }} />
                    <span>Pending Accounts</span>
                  </button>
                </div>
              </>
            ) : currentView === 'supervisors' ? (
              <button
                type="button"
                onClick={() => openSupervisorModal()}
                style={{
                  padding: 0,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: '2px solid transparent',
                  borderRadius: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontSize: isMobile ? 14 : 16,
                  width: '100%',
                  height: 44,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box',
                  margin: 0,
                  outline: 'none'
                }}
              >
                <span style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1 }}>
                  <FaUsers style={{ fontSize: isMobile ? 14 : 16, flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>Add Supervisor</span>
                </span>
              </button>
            ) : null}
          </div>
        </div>

        {/* Search Bar - Only for Candidates Views */}
        {(currentView === 'candidates' || currentView === 'my-candidates') && (
          <div style={{ marginBottom: isMobile ? 20 : 24 }}>
            <div style={{ position: 'relative', maxWidth: isMobile ? '100%' : 400, width: '100%' }}>
              <FaSearch style={{ 
                position: 'absolute', 
                left: isMobile ? 12 : 16, 
                top: '50%', 
                transform: 'translateY(-50%)', 
                color: '#999',
                fontSize: isMobile ? 16 : 18,
                zIndex: 1
              }} />
              <input
                type="text"
                placeholder={isMobile ? "Search by name, email, mobile..." : "Search candidates by name, email, mobile, or any field..."}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{
                  width: '100%',
                  padding: isMobile ? `12px ${searchInput ? 40 : 12}px 12px 40px` : `16px ${searchInput ? 40 : 16}px 16px 48px`,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  fontSize: isMobile ? 14 : 16,
                  outline: 'none',
                  transition: 'border-color 0.3s',
                  background: '#fff'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setCurrentPage(1);
                  }}
                  style={{
                    position: 'absolute',
                    right: isMobile ? 8 : 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    zIndex: 1
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#666'}
                  onMouseLeave={(e) => e.target.style.color = '#999'}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Candidates Table - Only for Candidates Views */}
        {(currentView === 'candidates' || currentView === 'my-candidates') && (
          <div style={{ 
            background: '#fff', 
            borderRadius: 16, 
            boxShadow: '0 4px 20px rgba(102,126,234,0.1)', 
            border: '1.5px solid #e5e7eb', 
            overflow: 'hidden',
            maxHeight: isMobile ? '60vh' : '70vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
          {loading ? (
            <div style={{ padding: isMobile ? 20 : 40, textAlign: 'center' }}>
              <div className="spinner" style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
              <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
            </div>
          ) : isMobile ? (
            // Mobile Card View
            <div style={{ padding: isMobile ? 12 : 16 }}>
              {candidates.length > 0 ? (
                candidates.map((candidate, idx) => (
                  <div key={candidate.id} style={{
                    background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ 
                          fontSize: 16, 
                          fontWeight: 700, 
                          color: '#667eea', 
                          margin: '0 0 4px 0' 
                        }}>
                          {candidate.name}
                        </h3>
                        <p style={{ 
                          fontSize: 14, 
                          color: '#666', 
                          margin: '0 0 4px 0' 
                        }}>
                          {candidate.email}
                        </p>
                        <p style={{ 
                          fontSize: 14, 
                          color: '#666', 
                          margin: 0 
                        }}>
                          {candidate.mobile}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="gogig-card-btn-outline"
                          style={{ 
                            padding: '6px 8px', 
                            fontSize: 12,
                            background: '#f8fafc',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          title="Edit"
                          onClick={() => handleEditClick(candidate)}
                        >
                          <FaEdit style={{ color: '#667eea', fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                    
                                         <div style={{ 
                       display: 'grid', 
                       gridTemplateColumns: '1fr 1fr', 
                       gap: 8, 
                       fontSize: 12,
                       color: '#666'
                     }}>
                       <div><strong>Establishment:</strong> {candidate.establishment_name || '-'}</div>
                       <div><strong>Job Role:</strong> {candidate.job_role || '-'}</div>
                       <div><strong>Stipend:</strong> {candidate.stipend ? `₹${candidate.stipend}` : '-'}</div>
                       <div><strong>Location:</strong> {candidate.location || '-'}</div>
                       <div><strong>Status:</strong> {candidate.status || '-'}</div>
                       <div><strong>Referral Code:</strong> {candidate.referral_code || '-'}</div>
                       <div><strong>Created:</strong> {new Date(candidate.created_at).toLocaleDateString()}</div>
                     </div>
                     
                     {/* Mobile Action Buttons */}
                     <div style={{ 
                       display: 'flex', 
                       gap: 8, 
                       marginTop: 12,
                       justifyContent: 'flex-end'
                     }}>
                       <button
                         style={{ 
                           padding: '8px 12px', 
                           fontSize: 12,
                           background: '#f8fafc',
                           border: '1px solid #e5e7eb',
                           borderRadius: '6px',
                           cursor: 'pointer',
                           transition: 'all 0.2s ease',
                           display: 'flex',
                           alignItems: 'center',
                           gap: 4
                         }}
                         title="View Details"
                         onMouseOver={(e) => {
                           e.target.style.background = '#e0e7ff';
                           e.target.style.borderColor = '#667eea';
                         }}
                         onMouseOut={(e) => {
                           e.target.style.background = '#f8fafc';
                           e.target.style.borderColor = '#e5e7eb';
                         }}
                       >
                         <FaEye style={{ color: '#667eea' }} />
                         View
                       </button>
                       <button
                         style={{ 
                           padding: '8px 12px', 
                           fontSize: 12,
                           background: '#f8fafc',
                           border: '1px solid #e5e7eb',
                           borderRadius: '6px',
                           cursor: 'pointer',
                           transition: 'all 0.2s ease',
                           display: 'flex',
                           alignItems: 'center',
                           gap: 4
                         }}
                         title="Edit"
                         onClick={() => handleEditClick(candidate)}
                         onMouseOver={(e) => {
                           e.target.style.background = '#e0e7ff';
                           e.target.style.borderColor = '#667eea';
                         }}
                         onMouseOut={(e) => {
                           e.target.style.background = '#f8fafc';
                           e.target.style.borderColor = '#e5e7eb';
                         }}
                       >
                         <FaEdit style={{ color: '#667eea' }} />
                         Edit
                       </button>
                       <button
                         style={{ 
                           padding: '8px 12px', 
                           fontSize: 12,
                           background: '#f8fafc',
                           border: '1px solid #e5e7eb',
                           borderRadius: '6px',
                           cursor: 'pointer',
                           transition: 'all 0.2s ease',
                           display: 'flex',
                           alignItems: 'center',
                           gap: 4
                         }}
                         title="Manage Documents"
                         onClick={() => handleManageDocuments(candidate)}
                         onMouseOver={(e) => {
                           e.target.style.background = '#e0f2fe';
                           e.target.style.borderColor = '#0ea5e9';
                         }}
                         onMouseOut={(e) => {
                           e.target.style.background = '#f8fafc';
                           e.target.style.borderColor = '#e5e7eb';
                         }}
                       >
                         <FaFileAlt style={{ color: '#0ea5e9' }} />
                         Docs
                       </button>
                     </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  padding: isMobile ? 20 : 40, 
                  textAlign: 'center', 
                  color: '#666', 
                  fontSize: isMobile ? 14 : 16 
                }}>
                  {search ? 'No candidates found matching your search.' : 'No candidates found. Upload a CSV file to get started.'}
                </div>
              )}
              
              {/* Mobile Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  padding: '16px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  alignItems: 'center'
                }}>
                  <div style={{ 
                    fontSize: 14, 
                    color: '#666', 
                    fontWeight: 600 
                  }}>
                    Page {currentPage} of {totalPages} ({totalCandidates} total candidates)
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: 12, 
                    width: '100%',
                    justifyContent: 'center'
                  }}>
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '10px 20px',
                        background: currentPage === 1 ? '#f3f4f6' : '#667eea',
                        color: currentPage === 1 ? '#9ca3af' : '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        flex: 1,
                        maxWidth: 120
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage >= totalPages}
                      style={{
                        padding: '10px 20px',
                        background: currentPage >= totalPages ? '#f3f4f6' : '#667eea',
                        color: currentPage >= totalPages ? '#9ca3af' : '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                        flex: 1,
                        maxWidth: 120
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              </div>
            ) : (
              // Desktop Table View
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div className="admin-table-sticky-scroll admin-table-sticky-scroll--brand" style={{ flex: 1, minHeight: 0, width: '100%' }}>
              <table style={{ 
                width: 'max-content', 
                minWidth: '100%',
                borderCollapse: 'separate', 
                borderSpacing: 0,
                fontSize: '13px'
              }}>
                            <thead>
              <tr>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 60, borderRight: '1px solid rgba(255,255,255,0.1)' }}>S.No</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 140, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Establishment</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Month</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Punch Code</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 110, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Employee Code</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Remark</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Contract</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Apprentice</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Name</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Mobile</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 180, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Email</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Aadhar</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>DOB</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 70, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Gender</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Father</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Mother</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Category</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 150, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Address</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>District</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>State</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Pincode</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Qualification</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Institute</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Specialization</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Bank</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Account No</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>IFSC</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Seeded</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Beni ID</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Join Date</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Stipend</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Job Role</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 150, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Est. Address</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Insurance</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80, borderRight: '1px solid rgba(255,255,255,0.1)' }}>ID Card</th>
                <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Location</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Status</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 120, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Referral Code</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100, borderRight: '1px solid rgba(255,255,255,0.1)' }}>Created At</th>
                <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100 }}>Actions</th>
              </tr>
            </thead>
                                  <tbody>
                    {candidates.length > 0 ? (
                      candidates.map((candidate, idx) => (
                        <tr
                          key={candidate.id}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            background: (() => {
                              const inactiveStatuses = ['termination', 'approved', 'expired', 'completed'];
                              const isInactive = candidate.contract_status && inactiveStatuses.includes(candidate.contract_status.toLowerCase().trim());
                              return isInactive ? '#fee2e2' : (idx % 2 === 0 ? '#fff' : '#fafbfc');
                            })(),
                            transition: 'background-color 0.2s ease',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = '#f0f4ff';
                          }}
                          onMouseOut={(e) => {
                            const inactiveStatuses = ['termination', 'approved', 'expired', 'completed'];
                            const isInactive = candidate.contract_status && inactiveStatuses.includes(candidate.contract_status.toLowerCase().trim());
                            e.currentTarget.style.background = isInactive ? '#fee2e2' : (idx % 2 === 0 ? '#fff' : '#fafbfc');
                          }}
                        >
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{idx + 1}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.establishment_name || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.month || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.punching_code || candidate.punching_employee_code || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.employee_code || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>
                            {candidate.contract_status ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#fee2e2', 
                                color: '#991b1b', 
                                borderRadius: 4, 
                                fontSize: 11, 
                                fontWeight: 600,
                                display: 'inline-block'
                              }}>
                                {candidate.contract_status}
                              </span>
                            ) : (candidate.remark || '-')}
                          </td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.contract_code || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.apprentice_code || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 600, fontSize: 13, borderRight: '1px solid #f3f4f6', color: '#667eea' }}>{candidate.name}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.mobile}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.email}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.aadhar_no || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.date_of_birth || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.gender || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.father_name || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.mother_name || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.category || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.address || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.district || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.state || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.pincode || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.qualification || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.institute_name || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.specialization || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.bank_name || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.account_no || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.ifsc_code || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.seeded_account || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.beni_id_status || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.date_of_joining || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6', fontWeight: 600, color: '#059669' }}>{candidate.stipend ? `₹${candidate.stipend}` : '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.job_role || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.establishment_address || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.insurance || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.id_card || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.location || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>
                            {candidate.status === 'documents_pending' ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#fef2f2', 
                                color: '#dc2626', 
                                borderRadius: 6, 
                                fontSize: 11, 
                                fontWeight: 600,
                                display: 'inline-block'
                              }}>
                                <FaExclamationTriangle style={{ marginRight: 4, fontSize: 10 }} />
                                Documents Pending
                              </span>
                            ) : candidate.status === 'contract not generated' ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#fef3c7', 
                                color: '#92400e', 
                                borderRadius: 6, 
                                fontSize: 11, 
                                fontWeight: 600,
                                display: 'inline-block'
                              }}>
                                Contract Not Generated
                              </span>
                            ) : candidate.status ? (
                              <span style={{ 
                                padding: '4px 8px', 
                                background: '#dcfce7', 
                                color: '#166534', 
                                borderRadius: 6, 
                                fontSize: 11, 
                                fontWeight: 600,
                                display: 'inline-block'
                              }}>
                                {candidate.status}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: 11 }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>{candidate.referral_code || '-'}</td>
                          <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13, borderRight: '1px solid #f3f4f6' }}>
                            {new Date(candidate.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                              <button
                                className="gogig-card-btn-outline"
                                style={{ 
                                  padding: '8px 12px', 
                                  fontSize: 12,
                                  background: '#f8fafc',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                title="View Details"
                                onMouseOver={(e) => {
                                  e.target.style.background = '#e0e7ff';
                                  e.target.style.borderColor = '#667eea';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = '#f8fafc';
                                  e.target.style.borderColor = '#e5e7eb';
                                }}
                              >
                                <FaEye style={{ color: '#667eea' }} />
                              </button>
                              <button
                                className="gogig-card-btn-outline"
                                style={{ 
                                  padding: '8px 12px', 
                                  fontSize: 12,
                                  background: '#f8fafc',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                title="Edit"
                                onClick={() => handleEditClick(candidate)}
                                onMouseOver={(e) => {
                                  e.target.style.background = '#e0e7ff';
                                  e.target.style.borderColor = '#667eea';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = '#f8fafc';
                                  e.target.style.borderColor = '#e5e7eb';
                                }}
                              >
                                <FaEdit style={{ color: '#667eea' }} />
                              </button>
                              <button
                                className="gogig-card-btn-outline"
                                style={{ 
                                  padding: '8px 12px', 
                                  fontSize: 12,
                                  background: '#f8fafc',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                title="Manage Documents"
                                onClick={() => handleManageDocuments(candidate)}
                                onMouseOver={(e) => {
                                  e.target.style.background = '#e0f2fe';
                                  e.target.style.borderColor = '#0ea5e9';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = '#f8fafc';
                                  e.target.style.borderColor = '#e5e7eb';
                                }}
                              >
                                <FaFileAlt style={{ color: '#0ea5e9' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="37" style={{ padding: 40, textAlign: 'center', color: '#666', fontSize: 14 }}>
                          No candidates found. Upload a CSV file to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Desktop Pagination */}
              {totalPages > 1 && (
                <div style={{ 
                  padding: '16px 20px',
                  borderTop: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12
                }}>
                  <div style={{ 
                    fontSize: 14, 
                    color: '#666', 
                    fontWeight: 600 
                  }}>
                    Page {currentPage} of {totalPages} ({totalCandidates} total candidates)
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: 12, 
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '10px 20px',
                        background: currentPage === 1 ? '#f3f4f6' : '#667eea',
                        color: currentPage === 1 ? '#9ca3af' : '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage >= totalPages}
                      style={{
                        padding: '10px 20px',
                        background: currentPage >= totalPages ? '#f3f4f6' : '#667eea',
                        color: currentPage >= totalPages ? '#9ca3af' : '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              </div>
            )
            }
          </div>
        )}

        {/* Calling Report - Admin-style UI */}
        {isCallingReportView && (
          <CallingReportErrorBoundary>
          <div className="admin-table-wrap admin-table-wrap--sticky-head" style={{ width: '100%' }}>
            {/* Filters: date, qualification, state, district, source */}
            <div style={{ padding: isMobile ? '12px 16px' : '12px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <button
                type="button"
                onClick={() => setCallingReportFiltersOpen(!callingReportFiltersOpen)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: isMobile ? '12px 14px' : '8px 12px', border: '1px solid #e2e8f0',
                  borderRadius: 8, background: '#fff', color: '#0f172a', fontWeight: 600, fontSize: isMobile ? 15 : 14, cursor: 'pointer', width: isMobile ? '100%' : 'auto', minHeight: isMobile ? 48 : undefined
                }}
              >
                Filters {callingReportFiltersOpen ? '▼' : '▶'} {[callingReportDateFrom, callingReportDateTo, callingReportQualification, callingReportCompanyFilter, callingReportStatus, callingReportState, callingReportDistrict, callingReportSource].filter(Boolean).length ? `(${[callingReportDateFrom, callingReportDateTo, callingReportQualification, callingReportCompanyFilter, callingReportStatus, callingReportState, callingReportDistrict, callingReportSource].filter(Boolean).length} applied)` : ''}
              </button>
              {callingReportFiltersOpen && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 12 : 16, marginTop: 12, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 140 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Date applied From</label>
                    <input
                      type="date"
                      value={callingReportDateFrom}
                      onChange={(e) => setCallingReportDateFrom(e.target.value)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 140, width: isMobile ? '100%' : undefined }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 140 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Date applied To</label>
                    <input
                      type="date"
                      value={callingReportDateTo}
                      onChange={(e) => setCallingReportDateTo(e.target.value)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 140, width: isMobile ? '100%' : undefined }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 160 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Qualification</label>
                    <select
                      value={callingReportQualification}
                      onChange={(e) => setCallingReportQualification(e.target.value)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 160, width: isMobile ? '100%' : undefined, background: '#fff' }}
                    >
                      {QUALIFICATION_OPTIONS.map((o) => (
                        <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative', width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 220 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Company</label>
                    <input
                      type="text"
                      placeholder="Search company..."
                      value={callingReportCompanyFilterDropdownOpen
                        ? callingReportCompanyFilterSearch
                        : (callingReportCompanies.find((co) => String(co.id) === String(callingReportCompanyFilter))?.company_name || '')
                      }
                      onChange={(e) => {
                        setCallingReportCompanyFilterSearch(e.target.value);
                        if (!e.target.value) setCallingReportCompanyFilter('');
                      }}
                      onFocus={() => {
                        setCallingReportCompanyFilterDropdownOpen(true);
                        setCallingReportCompanyFilterSearch(callingReportCompanyFilterSearch || (callingReportCompanies.find((co) => String(co.id) === String(callingReportCompanyFilter))?.company_name || ''));
                        if (callingReportCompanies.length === 0) fetchCallingReportCompanies();
                      }}
                      onBlur={() => setTimeout(() => setCallingReportCompanyFilterDropdownOpen(false), 200)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 220, width: isMobile ? '100%' : undefined }}
                    />
                    {callingReportCompanyFilterDropdownOpen && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: 260, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10 }}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => { setCallingReportCompanyFilter(''); setCallingReportCompanyFilterSearch(''); setCallingReportCompanyFilterDropdownOpen(false); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setCallingReportCompanyFilter(''); setCallingReportCompanyFilterSearch(''); setCallingReportCompanyFilterDropdownOpen(false); } }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}
                        >
                          All companies
                        </div>
                        {callingReportCompanies
                          .filter((co) => !callingReportCompanyFilterSearch || (co.company_name || '').toLowerCase().includes(callingReportCompanyFilterSearch.toLowerCase()))
                          .map((co) => (
                            <div
                              key={co.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => { setCallingReportCompanyFilter(String(co.id)); setCallingReportCompanyFilterSearch(''); setCallingReportCompanyFilterDropdownOpen(false); }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setCallingReportCompanyFilter(String(co.id)); setCallingReportCompanyFilterSearch(''); setCallingReportCompanyFilterDropdownOpen(false); } }}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                            >
                              {co.company_name || `Company #${co.id}`}
                            </div>
                          ))}
                        {callingReportCompanies.length > 0 && callingReportCompanies.filter((co) => !callingReportCompanyFilterSearch || (co.company_name || '').toLowerCase().includes(callingReportCompanyFilterSearch.toLowerCase())).length === 0 && (
                          <div style={{ padding: '8px 12px', fontSize: 12, color: '#64748b' }}>No matching companies</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 180 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Status</label>
                    <select
                      value={isAgreedToJoinReport ? 'agreed to join' : callingReportStatus}
                      onChange={(e) => setCallingReportStatus(e.target.value)}
                      disabled={isAgreedToJoinReport}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 180, width: isMobile ? '100%' : undefined, background: '#fff' }}
                    >
                      {!isAgreedToJoinReport && <option value="">All statuses</option>}
                      <option value="applied">applied</option>
                      <option value="interview scheduled">interview scheduled</option>
                      <option value="selected">selected</option>
                      <option value="rejected">rejected</option>
                      <option value="agreed to join">agreed to join</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative', width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 180 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>State</label>
                    <input
                      type="text"
                      placeholder="Search state..."
                      value={callingReportStateDropdownOpen ? callingReportStateSearch : (callingReportState || '')}
                      onChange={(e) => { setCallingReportStateSearch(e.target.value); if (!e.target.value) setCallingReportState(''); }}
                      onFocus={() => { setCallingReportStateDropdownOpen(true); setCallingReportStateSearch(callingReportStateSearch || callingReportState); }}
                      onBlur={() => setTimeout(() => setCallingReportStateDropdownOpen(false), 200)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 180, width: isMobile ? '100%' : undefined }}
                    />
                    {callingReportStateDropdownOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: 220, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10 }}>
                      {INDIAN_STATES_DISTRICTS.states
                        .filter((s) => !callingReportStateSearch || s.state.toLowerCase().includes(callingReportStateSearch.toLowerCase()))
                        .map((s) => (
                          <div
                            key={s.state}
                            role="button"
                            tabIndex={0}
                            onClick={() => { setCallingReportState(s.state); setCallingReportStateSearch(''); setCallingReportDistrict(''); setCallingReportDistrictSearch(''); setCallingReportStateDropdownOpen(false); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setCallingReportState(s.state); setCallingReportStateSearch(''); setCallingReportDistrict(''); setCallingReportStateDropdownOpen(false); } }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                          >
                            {s.state}
                          </div>
                        ))}
                    </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative', width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 180 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>District</label>
                    <input
                      type="text"
                      placeholder={callingReportState ? 'Search district...' : 'Select state first or search all'}
                      value={callingReportDistrictDropdownOpen ? callingReportDistrictSearch : (callingReportDistrict || '')}
                      onChange={(e) => { setCallingReportDistrictSearch(e.target.value); if (!e.target.value) setCallingReportDistrict(''); }}
                      onFocus={() => { setCallingReportDistrictDropdownOpen(true); setCallingReportDistrictSearch(callingReportDistrictSearch || callingReportDistrict); }}
                      onBlur={() => setTimeout(() => setCallingReportDistrictDropdownOpen(false), 200)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 180, width: isMobile ? '100%' : undefined }}
                    />
                    {callingReportDistrictDropdownOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: 220, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10 }}>
                      {(() => {
                        const districtList = callingReportState
                          ? (INDIAN_STATES_DISTRICTS.states.find((s) => s.state === callingReportState)?.districts || [])
                          : INDIAN_STATES_DISTRICTS.states.flatMap((s) => s.districts.map((d) => ({ state: s.state, district: d })));
                        const filtered = districtList.filter((item) => {
                          const d = typeof item === 'string' ? item : item.district;
                          return !callingReportDistrictSearch || d.toLowerCase().includes(callingReportDistrictSearch.toLowerCase());
                        });
                        return filtered.map((item) => {
                          const d = typeof item === 'string' ? item : item.district;
                          const key = typeof item === 'string' ? d : `${item.state}-${item.district}`;
                          return (
                            <div
                              key={key}
                              role="button"
                              tabIndex={0}
                            onClick={() => { setCallingReportDistrict(d); setCallingReportDistrictSearch(''); setCallingReportDistrictDropdownOpen(false); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setCallingReportDistrict(d); setCallingReportDistrictSearch(''); setCallingReportDistrictDropdownOpen(false); } }}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                            >
                              {d}
                            </div>
                          );
                        });
                      })()}
                    </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : 140 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Source of data</label>
                    <select
                      value={callingReportSource}
                      onChange={(e) => setCallingReportSource(e.target.value)}
                      style={{ padding: isMobile ? '12px 10px' : '8px 10px', fontSize: isMobile ? 16 : 13, border: '1px solid #e2e8f0', borderRadius: 8, minWidth: isMobile ? '100%' : 140, width: isMobile ? '100%' : undefined, background: '#fff' }}
                    >
                      <option value="">All</option>
                      <option value="whatsapp">Whatsapp applied</option>
                      <option value="uploaded">Supervisor upload</option>
                      <option value="applied">Applied</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCallingReportDateFrom(''); setCallingReportDateTo(''); setCallingReportQualification('');
                      setCallingReportCompanyFilter('');
                      setCallingReportStatus('');
                      setCallingReportCompanyFilterSearch(''); setCallingReportCompanyFilterDropdownOpen(false);
                      setCallingReportState(''); setCallingReportDistrict(''); setCallingReportSource('');
                      setCallingReportStateSearch(''); setCallingReportDistrictSearch('');
                    }}
                    style={{ padding: isMobile ? '12px 14px' : '8px 14px', fontSize: isMobile ? 15 : 13, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b', cursor: 'pointer', fontWeight: 500, minHeight: isMobile ? 48 : undefined, width: isMobile ? '100%' : undefined }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
            {/* Toolbar: search + page size (Admin search bar style) */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobile ? 12 : 16,
              alignItems: 'center',
              padding: isMobile ? '12px 16px' : '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              background: '#fff',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <form onSubmit={(e) => { e.preventDefault(); }} style={{ flex: isMobile ? '0 0 auto' : '1 1 320px', maxWidth: isMobile ? '100%' : 480, width: isMobile ? '100%' : undefined }}>
                <div className="admin-search-bar" style={{ maxWidth: '100%' }}>
                  <input
                    type="text"
                    placeholder="Search name, mobile, email..."
                    value={callingReportSearchInput}
                    onChange={(e) => setCallingReportSearchInput(e.target.value)}
                    aria-label="Search calling report"
                  />
                  <button type="button" className="search-btn" title="Search" style={{ pointerEvents: 'none' }}>
                    <FaSearch />
                  </button>
                </div>
              </form>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : undefined }}>
                <div ref={callingReportColumnsMenuRef} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setCallingReportColumnsMenuOpen((o) => !o)}
                    title="Show or hide table columns"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: isMobile ? '10px 14px' : '8px 14px',
                      border: callingReportColumnsMenuOpen ? '1px solid #22c55e' : '1px solid #e2e8f0',
                      borderRadius: 8,
                      background: callingReportColumnsMenuOpen ? '#f0fdf4' : '#fff',
                      color: '#0f172a',
                      fontWeight: 600,
                      fontSize: isMobile ? 15 : 14,
                      cursor: 'pointer',
                      minHeight: isMobile ? 44 : undefined,
                      boxShadow: callingReportColumnsMenuOpen ? '0 0 0 2px rgba(34,197,94,0.15)' : 'none'
                    }}
                  >
                    <FaColumns style={{ color: '#22c55e' }} />
                    Columns
                    {callingReportHiddenColumnCount > 0 && (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 700,
                        background: '#fef3c7',
                        color: '#92400e',
                        padding: '2px 7px',
                        borderRadius: 999
                      }}>
                        {callingReportHiddenColumnCount} hidden
                      </span>
                    )}
                  </button>
                  {callingReportColumnsMenuOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        zIndex: 50,
                        width: isMobile ? 'min(100vw - 32px, 320px)' : 300,
                        maxHeight: 420,
                        overflowY: 'auto',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        boxShadow: '0 10px 40px rgba(15,23,42,0.12)',
                        padding: '12px 0'
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div style={{ padding: '0 14px 10px', borderBottom: '1px solid #f1f5f9', marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Table columns</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Toggle columns on or off. Name always stays visible.</div>
                      </div>
                      {CALLING_REPORT_COLUMNS.map((col) => {
                        const visible = isCallingReportColVisible(col.key);
                        const disabled = !!col.required;
                        return (
                          <label
                            key={col.key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '10px 14px',
                              cursor: disabled ? 'default' : 'pointer',
                              background: visible ? 'transparent' : '#f8fafc',
                              opacity: disabled ? 0.65 : 1
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={visible}
                              disabled={disabled}
                              onChange={() => setCallingReportColumnVisible(col.key, !visible)}
                              style={{ width: 16, height: 16, accentColor: '#22c55e', cursor: disabled ? 'not-allowed' : 'pointer' }}
                            />
                            <span style={{ flex: 1, fontSize: 13, fontWeight: visible ? 600 : 500, color: visible ? '#0f172a' : '#94a3b8' }}>
                              {col.label}
                              {col.editable && <span style={{ fontWeight: 500, color: '#64748b', marginLeft: 4 }}>(editable)</span>}
                            </span>
                            {visible ? <FaEye size={12} color="#22c55e" /> : <FaEyeSlash size={12} color="#94a3b8" />}
                          </label>
                        );
                      })}
                      <div style={{ padding: '10px 14px 4px', borderTop: '1px solid #f1f5f9', marginTop: 4, display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={showAllCallingReportColumns}
                          disabled={callingReportHiddenColumnCount === 0}
                          style={{
                            flex: 1,
                            padding: '8px 10px',
                            fontSize: 12,
                            fontWeight: 600,
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            background: callingReportHiddenColumnCount === 0 ? '#f8fafc' : '#fff',
                            color: callingReportHiddenColumnCount === 0 ? '#94a3b8' : '#0f172a',
                            cursor: callingReportHiddenColumnCount === 0 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Show all
                        </button>
                        <button
                          type="button"
                          onClick={() => setCallingReportColumnsMenuOpen(false)}
                          style={{
                            padding: '8px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            border: 'none',
                            borderRadius: 8,
                            background: '#22c55e',
                            color: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', fontWeight: 500 }}>Rows per page</span>
                <select
                  value={callingReportLimit}
                  onChange={(e) => { setCallingReportLimit(Number(e.target.value)); setCallingReportPage(1); }}
                  style={{
                    padding: isMobile ? '10px 12px' : '8px 12px',
                    fontSize: isMobile ? 16 : 14,
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    background: '#fff',
                    cursor: 'pointer',
                    color: '#0f172a',
                    fontWeight: 500,
                    minHeight: isMobile ? 44 : undefined
                  }}
                >
                  {[10, 25, 50, 100].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              {!callingReportLoading && callingReportTotal > 0 && (
                <span style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', width: isMobile ? '100%' : undefined }}>
                  {callingReportTotal} candidate{callingReportTotal !== 1 ? 's' : ''} total
                </span>
              )}
            </div>
            {callingReportLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                <div className="admin-spinner" />
              </div>
            ) : callingReportRows.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                {isMyReportView
                  ? 'No candidates in My Report yet. Update status/remarks/callback date/company to make entries appear here.'
                  : 'No candidates in the calling report. Employed candidates and those matching contract uploads are excluded.'}
              </div>
            ) : (
              <div className="admin-table-sticky-scroll admin-table-sticky-scroll--light" style={{ width: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff', minWidth: 900 }}>
                  <thead>
                    <tr>
                      {CALLING_REPORT_COLUMNS.map((col) => (
                        <CallingReportCol
                          key={col.key}
                          columnVisibility={callingReportColumnVisibility}
                          colKey={col.key}
                          as="th"
                          style={{ ...crThStyle, minWidth: col.minWidth }}
                        >
                          {col.label}
                          {col.editable && <span style={{ opacity: 0.85, fontWeight: 500 }}> (editable)</span>}
                        </CallingReportCol>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {callingReportRows.map((row, idx) => {
                      const isEditingJoining = callingReportEditing?.id === row.id && callingReportEditing?.field === 'joining_date';
                      const isEditingCode = callingReportEditing?.id === row.id && callingReportEditing?.field === 'employee_code';
                      const isEditingRemarks = callingReportEditing?.id === row.id && callingReportEditing?.field === 'remarks';
                      const isEditingCompany = callingReportEditing?.id === row.id && callingReportEditing?.field === 'company';
                      const joiningDisplay = toCallingReportDateInputValue(row.joining_date);
                      const companySearchLower = (callingReportCompanySearch || '').toLowerCase();
                      const filteredCompanies = companySearchLower
                        ? callingReportCompanies.filter(co =>
                            (co.company_name || '').toLowerCase().includes(companySearchLower) ||
                            (co.establishment_code || '').toLowerCase().includes(companySearchLower) ||
                            (co.email || '').toLowerCase().includes(companySearchLower)
                          )
                        : callingReportCompanies;
                      const companyDetailsTitle = Array.isArray(row.company_details) && row.company_details.length
                        ? row.company_details.map((co) => {
                            const parts = [co.company_name || 'Unnamed company'];
                            if (co.establishment_code) parts.push(`Code: ${co.establishment_code}`);
                            if (co.email) parts.push(`Email: ${co.email}`);
                            return parts.join(' | ');
                          }).join('\n')
                        : (row.company_name || '');
                      const editBtnStyle = {
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, padding: 0, border: '1px solid #e2e8f0', borderRadius: 8,
                        background: '#f8fafc', color: '#22c55e', cursor: 'pointer', flexShrink: 0
                      };
                      const editBtnHover = (e, over) => {
                        e.currentTarget.style.background = over ? '#f0fdfa' : '#f8fafc';
                        e.currentTarget.style.borderColor = over ? '#22c55e' : '#e2e8f0';
                      };
                      const crTd = { padding: '8px 10px', fontSize: 13, verticalAlign: 'middle' };
                      const crTdEllipsis = (text, maxW = 140) => (
                        <div style={{ maxWidth: maxW, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text && text !== '-' ? text : undefined}>
                          {text || '-'}
                        </div>
                      );
                      const jobsAppliedList = (Array.isArray(row.jobs_applied) ? row.jobs_applied : [])
                        .map((j) => (j == null ? '' : (typeof j === 'string' ? j : (j.job_title || j.title || JSON.stringify(j)))))
                        .filter(Boolean);
                      const latestJobApplied = jobsAppliedList[0] || null;
                      const moreJobsCount = jobsAppliedList.length > 1 ? jobsAppliedList.length - 1 : 0;
                      const jobsAppliedTitle = jobsAppliedList.length ? jobsAppliedList.join('\n') : undefined;
                      return (
                        <tr key={row.id} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="name" style={crTd}>{crTdEllipsis(row.name || '-', 120)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="mobile" style={crTd}>{row.mobile || '-'}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="email" style={crTd}>{crTdEllipsis(row.email || '-', 160)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="qualification" style={crTd}>{crTdEllipsis(row.qualification || '-', 120)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="district" style={crTd}>{crTdEllipsis(row.district || '-', 100)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="state" style={crTd}>{crTdEllipsis(row.state || '-', 100)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="source" style={crTd}>{crTdEllipsis(row.source || '-', 100)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="supervisor" style={crTd}>{crTdEllipsis(row.supervisor_name || '-', 110)}</CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="jobs_applied" style={{ ...crTd, maxWidth: 220, width: 220 }}>
                            {latestJobApplied ? (
                              <div style={{ maxWidth: 210 }} title={jobsAppliedTitle}>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: '#0f172a' }}>
                                  {latestJobApplied}
                                </div>
                                {moreJobsCount > 0 && (
                                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                    +{moreJobsCount} older job{moreJobsCount > 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="date" style={{ ...crTd, whiteSpace: 'nowrap' }}>
                            {row.is_whatsapp && row.created_at
                              ? <span title="WhatsApp applied">{new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} <span style={{ color: '#0d9488', fontWeight: 500 }}>(WhatsApp)</span></span>
                              : row.applied_at
                              ? <span title="Date applied to job">{new Date(row.applied_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} <span style={{ color: '#64748b', fontWeight: 500 }}>(Applied)</span></span>
                              : row.created_at
                                ? <span title="Date of upload">{new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} <span style={{ color: '#64748b', fontWeight: 500 }}>(Uploaded)</span></span>
                                : '-'}
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="status" style={{ ...crTd, minWidth: 150 }}>
                            <select
                              value={row.calling_status || ''}
                              onChange={(e) => saveCallingReportCell(row.id, 'calling_status', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                borderRadius: 8,
                                border: '1px solid #e2e8f0',
                                fontSize: 12,
                                fontWeight: 600,
                                background: row.calling_status === 'selected' ? '#dcfce7' : row.calling_status === 'rejected' ? '#fee2e2' : '#fef3c7',
                                color: row.calling_status === 'selected' ? '#166534' : row.calling_status === 'rejected' ? '#991b1b' : '#92400e',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="">-</option>
                              <option value="applied">applied</option>
                              <option value="interview scheduled">interview scheduled</option>
                              <option value="selected">selected</option>
                              <option value="rejected">rejected</option>
                              <option value="agreed to join">agreed to join</option>
                            </select>
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="callback_date" style={{ ...crTd, minWidth: 130 }}>
                            <input
                              type="date"
                              value={row.callback_date ? String(row.callback_date).slice(0, 10) : ''}
                              onChange={(e) => saveCallingReportCell(row.id, 'callback_date', e.target.value)}
                              style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8, outline: 'none' }}
                            />
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="company" style={{ ...crTd, minWidth: 140, position: 'relative' }}>
                            {isEditingCompany ? (
                              <div style={{ position: 'relative', minWidth: 220 }}>
                                <input
                                  type="text"
                                  placeholder="Search companies..."
                                  value={callingReportCompanySearch}
                                  onChange={(e) => setCallingReportCompanySearch(e.target.value)}
                                  onFocus={() => callingReportCompanies.length === 0 && fetchCallingReportCompanies()}
                                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #22c55e', borderRadius: 8, outline: 'none', marginBottom: 6 }}
                                />
                                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                                    onClick={() => { saveCallingReportCell(row.id, 'company_id', null); setCallingReportCompanySearch(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { saveCallingReportCell(row.id, 'company_id', null); setCallingReportCompanySearch(''); } }}
                                  >
                                    — Clear company —
                                  </div>
                                  {filteredCompanies.slice(0, 50).map((co) => (
                                    <div
                                      key={co.id}
                                      role="button"
                                      tabIndex={0}
                                      style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}
                                      onClick={() => { saveCallingReportCell(row.id, 'company_id', co.id); setCallingReportCompanySearch(''); }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { saveCallingReportCell(row.id, 'company_id', co.id); setCallingReportCompanySearch(''); } }}
                                    >
                                      {co.company_name || 'Unnamed'}{co.establishment_code ? ` (${co.establishment_code})` : ''}
                                    </div>
                                  ))}
                                  {filteredCompanies.length === 0 && callingReportCompanies.length > 0 && (
                                    <div style={{ padding: 10, color: '#64748b', fontSize: 12 }}>No companies match</div>
                                  )}
                                  {callingReportCompanies.length === 0 && !callingReportCompanySearch && (
                                    <div style={{ padding: 10, color: '#64748b', fontSize: 12 }}>Type to search or loading...</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minHeight: 24 }}>
                                <div style={{ flex: 1, maxWidth: 200 }} title={companyDetailsTitle}>
                                  {Array.isArray(row.company_details) && row.company_details.length > 1 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                      {row.company_details.slice(0, 3).map((co, i) => (
                                        <div key={`${row.id}-co-${i}`} style={{ fontSize: 12, fontWeight: i === 0 ? 600 : 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {co.company_name || 'Unnamed'}
                                        </div>
                                      ))}
                                      {row.company_details.length > 3 ? (
                                        <div style={{ fontSize: 11, color: '#64748b' }}>+{row.company_details.length - 3} more</div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{row.company_name || '-'}</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  title="Edit company"
                                  style={editBtnStyle}
                                  onMouseOver={(e) => editBtnHover(e, true)}
                                  onMouseOut={(e) => editBtnHover(e, false)}
                                  onClick={() => { setCallingReportEditing({ id: row.id, field: 'company' }); setCallingReportCompanySearch(''); fetchCallingReportCompanies(); }}
                                >
                                  <FaEdit size={12} />
                                </button>
                              </div>
                            )}
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="remarks" style={{ ...crTd, maxWidth: 200, minWidth: 130 }}>
                            {isEditingRemarks ? (
                              <textarea
                                key={`remarks-edit-${row.id}`}
                                value={callingReportEditValue}
                                onChange={(e) => setCallingReportEditValue(e.target.value)}
                                onBlur={() => saveCallingReportCell(row.id, 'remarks', callingReportEditValue)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveCallingReportCell(row.id, 'remarks', callingReportEditValue); } if (e.key === 'Escape') setCallingReportEditing(null); }}
                                autoFocus
                                placeholder="Remarks"
                                rows={2}
                                dir="ltr"
                                style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #22c55e', borderRadius: 8, resize: 'vertical', minHeight: 40, outline: 'none', direction: 'ltr' }}
                              />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 22 }}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }} title={row.remarks || ''}>{row.remarks || '-'}</span>
                                <button type="button" onClick={() => { setCallingReportEditing({ id: row.id, field: 'remarks' }); setCallingReportEditValue(row.remarks || ''); }} title="Edit remarks" style={editBtnStyle} onMouseOver={(e) => editBtnHover(e, true)} onMouseOut={(e) => editBtnHover(e, false)}>
                                  <FaEdit size={12} />
                                </button>
                              </div>
                            )}
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="joining_date" style={{ ...crTd, minWidth: 96 }}>
                            {isEditingJoining ? (
                              <input
                                type="date"
                                value={callingReportEditValue}
                                onChange={(e) => setCallingReportEditValue(e.target.value)}
                                onBlur={() => saveCallingReportCell(row.id, 'joining_date', callingReportEditValue)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveCallingReportCell(row.id, 'joining_date', callingReportEditValue); if (e.key === 'Escape') setCallingReportEditing(null); }}
                                autoFocus
                                style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #22c55e', borderRadius: 8, outline: 'none' }}
                              />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 22 }}>
                                <span style={{ flex: 1 }}>{formatCallingReportDateDisplay(row.joining_date)}</span>
                                <button type="button" onClick={() => { setCallingReportEditing({ id: row.id, field: 'joining_date' }); setCallingReportEditValue(joiningDisplay || ''); }} title="Edit joining date" style={editBtnStyle} onMouseOver={(e) => editBtnHover(e, true)} onMouseOut={(e) => editBtnHover(e, false)}>
                                  <FaEdit size={12} />
                                </button>
                              </div>
                            )}
                          </CallingReportCol>
                          <CallingReportCol columnVisibility={callingReportColumnVisibility} colKey="employee_code" style={{ ...crTd, minWidth: 88 }}>
                            {isEditingCode ? (
                              <input
                                type="text"
                                value={callingReportEditValue}
                                onChange={(e) => setCallingReportEditValue(e.target.value)}
                                onBlur={() => saveCallingReportCell(row.id, 'employee_code', callingReportEditValue)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveCallingReportCell(row.id, 'employee_code', callingReportEditValue); if (e.key === 'Escape') setCallingReportEditing(null); }}
                                autoFocus
                                placeholder="Employee code"
                                style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #22c55e', borderRadius: 8, outline: 'none' }}
                              />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 22 }}>
                                <span style={{ flex: 1 }}>{row.employee_code || '-'}</span>
                                <button type="button" onClick={() => { setCallingReportEditing({ id: row.id, field: 'employee_code' }); setCallingReportEditValue(row.employee_code || ''); }} title="Edit employee code" style={editBtnStyle} onMouseOver={(e) => editBtnHover(e, true)} onMouseOut={(e) => editBtnHover(e, false)}>
                                  <FaEdit size={12} />
                                </button>
                              </div>
                            )}
                          </CallingReportCol>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!callingReportLoading && (callingReportRows.length > 0 || callingReportTotal > 0) && (
              <div className="admin-pagination-wrap" style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: isMobile ? 12 : 16,
                marginTop: isMobile ? 12 : 20,
                padding: isMobile ? '12px 16px' : '16px 20px',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <span style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', textAlign: isMobile ? 'center' : 'left', width: isMobile ? '100%' : undefined }}>
                  Showing {callingReportTotal === 0 ? 0 : (callingReportPage - 1) * callingReportLimit + 1}–{Math.min(callingReportPage * callingReportLimit, callingReportTotal)} of {callingReportTotal} candidates
                </span>
                <div className="pagination-buttons" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 6, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : undefined }}>
                  <button
                    type="button"
                    onClick={() => setCallingReportPage(p => Math.max(1, p - 1))}
                    disabled={callingReportPage <= 1}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      background: callingReportPage <= 1 ? '#f8fafc' : '#fff',
                      color: callingReportPage <= 1 ? '#94a3b8' : '#475569',
                      fontWeight: 500,
                      cursor: callingReportPage <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: 14
                    }}
                  >
                    ‹ Previous
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {Array.from({ length: Math.min(5, callingReportTotalPages || 1) }, (_, i) => {
                      const listTotalPages = callingReportTotalPages || 1;
                      let pageNum;
                      if (listTotalPages <= 5) pageNum = i + 1;
                      else if (callingReportPage <= 3) pageNum = i + 1;
                      else if (callingReportPage >= listTotalPages - 2) pageNum = listTotalPages - 4 + i;
                      else pageNum = callingReportPage - 2 + i;
                      if (pageNum < 1) return null;
                      const isActive = callingReportPage === pageNum;
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setCallingReportPage(pageNum)}
                          style={{
                            minWidth: 36,
                            height: 36,
                            padding: '0 10px',
                            borderRadius: 8,
                            border: isActive ? 'none' : '1px solid #e2e8f0',
                            background: isActive ? 'linear-gradient(135deg, #22c55e 0%, #2ad2c9 100%)' : '#fff',
                            color: isActive ? '#fff' : '#475569',
                            fontWeight: isActive ? 600 : 500,
                            cursor: 'pointer',
                            fontSize: 14
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCallingReportPage(p => Math.min(callingReportTotalPages || 1, p + 1))}
                    disabled={callingReportPage >= (callingReportTotalPages || 1)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      background: callingReportPage >= (callingReportTotalPages || 1) ? '#f8fafc' : '#fff',
                      color: callingReportPage >= (callingReportTotalPages || 1) ? '#94a3b8' : '#475569',
                      fontWeight: 500,
                      cursor: callingReportPage >= (callingReportTotalPages || 1) ? 'not-allowed' : 'pointer',
                      fontSize: 14
                    }}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
          </CallingReportErrorBoundary>
        )}

        {/* Campus Drive - Admin supervisor only */}
        {currentView === 'campus-drive' && (
          <div className="admin-table-wrap admin-table-wrap--sticky-head" style={{ width: '100%' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 12 : 16, alignItems: 'center', padding: isMobile ? '12px 16px' : '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexDirection: isMobile ? 'column' : 'row' }}>
              <form onSubmit={(e) => e.preventDefault()} style={{ flex: isMobile ? '0 0 auto' : '1 1 280px', maxWidth: isMobile ? '100%' : 400, width: isMobile ? '100%' : undefined }}>
                <div className="admin-search-bar" style={{ maxWidth: '100%' }}>
                  <input
                    type="text"
                    placeholder="Search by drive name, college, POC, state..."
                    value={campusDriveSearchInput}
                    onChange={(e) => setCampusDriveSearchInput(e.target.value)}
                    aria-label="Search campus drives"
                  />
                  <button type="button" className="search-btn" style={{ pointerEvents: 'none' }}><FaSearch /></button>
                </div>
              </form>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: isMobile ? '100%' : undefined, justifyContent: isMobile ? 'space-between' : undefined }}>
                <span style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', fontWeight: 500 }}>Rows per page</span>
                <select
                  value={campusDriveLimit}
                  onChange={(e) => { setCampusDriveLimit(Number(e.target.value)); setCampusDrivePage(1); }}
                  style={{ padding: isMobile ? '10px 12px' : '8px 12px', fontSize: isMobile ? 16 : 14, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', minHeight: isMobile ? 44 : undefined }}
                >
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {!campusDriveLoading && <span style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', width: isMobile ? '100%' : undefined }}>{campusDriveTotal} drive{campusDriveTotal !== 1 ? 's' : ''} total</span>}
            </div>
            {campusDriveLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: isMobile ? 24 : 40 }}><div className="admin-spinner" /></div>
            ) : campusDriveRows.length === 0 ? (
              <div style={{ padding: isMobile ? 24 : 40, textAlign: 'center', color: '#64748b', fontSize: isMobile ? 14 : 16 }}>No campus drives found. Add one using the button above.</div>
            ) : isMobile ? (
              <div style={{ padding: 12 }}>
                {campusDriveRows.map((row, idx) => (
                  <div key={row.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, color: '#0f172a' }}>{row.campus_drive_name || '-'}</div>
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>{row.college_name || '-'} · {row.college_type || '-'}</div>
                    {(row.poc_name || row.poc_email || row.poc_mobile) && <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>POC: {row.poc_name || '-'} {row.poc_email ? ` · ${row.poc_email}` : ''} {row.poc_mobile ? ` · ${row.poc_mobile}` : ''}</div>}
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>{row.state || '-'} / {row.district || '-'}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b' }}>
                      <span>{row.drive_date ? new Date(row.drive_date).toLocaleDateString('en-IN') : '-'}</span>
                      <span>Participated: {row.total_participated ?? '-'} · Joined: {row.total_joined ?? '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-table-sticky-scroll admin-table-sticky-scroll--light">
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff', minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Drive Name</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>College</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>College Type</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>POC Name</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>POC Contact</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>State / District</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Drive Date</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Participated</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campusDriveRows.map((row, idx) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.campus_drive_name || '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.college_name || '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.college_type || '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.poc_name || '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.poc_email || '-'}<br />{row.poc_mobile || ''}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.state || '-'} / {row.district || '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.drive_date ? new Date(row.drive_date).toLocaleDateString('en-IN') : '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.total_participated ?? '-'}</td>
                        <td style={{ padding: 16, fontSize: 14 }}>{row.total_joined ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!campusDriveLoading && campusDriveTotal > 0 && (
              <div className="admin-pagination-wrap" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: isMobile ? 12 : 16, padding: isMobile ? 12 : 16, borderTop: '1px solid #e2e8f0', background: '#fff', flexDirection: isMobile ? 'column' : 'row' }}>
                <span style={{ fontSize: isMobile ? 13 : 14, color: '#64748b', textAlign: isMobile ? 'center' : 'left', width: isMobile ? '100%' : undefined }}>
                  Showing {((campusDrivePage - 1) * campusDriveLimit) + 1}–{Math.min(campusDrivePage * campusDriveLimit, campusDriveTotal)} of {campusDriveTotal}
                </span>
                <div className="pagination-buttons" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : undefined }}>
                  <button type="button" disabled={campusDrivePage <= 1} onClick={() => setCampusDrivePage(1)} style={{ padding: isMobile ? '10px 12px' : '8px 12px', minHeight: isMobile ? 44 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, background: campusDrivePage <= 1 ? '#f8fafc' : '#fff', cursor: campusDrivePage <= 1 ? 'not-allowed' : 'pointer', fontSize: isMobile ? 15 : 14 }}>First</button>
                  <button type="button" disabled={campusDrivePage <= 1} onClick={() => setCampusDrivePage(p => Math.max(1, p - 1))} style={{ padding: isMobile ? '10px 12px' : '8px 12px', minHeight: isMobile ? 44 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, background: campusDrivePage <= 1 ? '#f8fafc' : '#fff', cursor: campusDrivePage <= 1 ? 'not-allowed' : 'pointer', fontSize: isMobile ? 15 : 14 }}>Prev</button>
                  <span style={{ padding: '0 8px', fontSize: isMobile ? 14 : 14 }}>Page {campusDrivePage} of {campusDriveTotalPages || 1}</span>
                  <button type="button" disabled={campusDrivePage >= (campusDriveTotalPages || 1)} onClick={() => setCampusDrivePage(p => Math.min(campusDriveTotalPages || 1, p + 1))} style={{ padding: isMobile ? '10px 12px' : '8px 12px', minHeight: isMobile ? 44 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, background: campusDrivePage >= (campusDriveTotalPages || 1) ? '#f8fafc' : '#fff', cursor: campusDrivePage >= (campusDriveTotalPages || 1) ? 'not-allowed' : 'pointer', fontSize: isMobile ? 15 : 14 }}>Next</button>
                  <button type="button" disabled={campusDrivePage >= (campusDriveTotalPages || 1)} onClick={() => setCampusDrivePage(campusDriveTotalPages || 1)} style={{ padding: isMobile ? '10px 12px' : '8px 12px', minHeight: isMobile ? 44 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, background: campusDrivePage >= (campusDriveTotalPages || 1) ? '#f8fafc' : '#fff', cursor: campusDrivePage >= (campusDriveTotalPages || 1) ? 'not-allowed' : 'pointer', fontSize: isMobile ? 15 : 14 }}>Last</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* All Jobs & Applied Candidates - Admin supervisor only */}
        {currentView === 'all-jobs' && supervisor?.role === 'admin_supervisor' && (
          <>
            <div className="admin-table-wrap admin-table-wrap--sticky-head" style={{ width: '100%' }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: isMobile ? '12px 16px' : '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                background: '#fff'
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#0f172a' }}>All jobs</h2>
                  {supervisorHiddenJobCount > 0 && (
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                      {supervisorHiddenJobCount} hidden job{supervisorHiddenJobCount !== 1 ? 's' : ''} — turn on &quot;Show hidden jobs&quot; to restore them
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  {supervisorHiddenJobCount > 0 && (
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        background: showHiddenSupervisorJobs ? '#f0fdf4' : '#fff',
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#0f172a',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={showHiddenSupervisorJobs}
                        onChange={(e) => setShowHiddenSupervisorJobs(e.target.checked)}
                        style={{ width: 16, height: 16, accentColor: '#22c55e' }}
                      />
                      <FaEyeSlash style={{ color: '#64748b' }} />
                      Show hidden jobs ({supervisorHiddenJobCount})
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setSupervisorJobFormError('');
                      setSupervisorJobFormSuccess('');
                      setShowSupervisorJobForm(true);
                    }}
                    style={{
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FaPlus /> Post new job
                  </button>
                </div>
              </div>
              {jobsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="admin-spinner" /></div>
              ) : jobs.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 16 }}>
                  No jobs found yet. Use &quot;Post new job&quot; to add one.
                </div>
              ) : supervisorVisibleJobs.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 16 }}>
                  All jobs are hidden. Enable &quot;Show hidden jobs&quot; above to view and unhide them.
                </div>
              ) : isMobile ? (
                <div style={{ padding: isMobile ? '12px 16px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {supervisorVisibleJobs.map((job) => {
                    const hidden = isSupervisorJobHidden(job);
                    return (
                      <div
                        key={job.id}
                        style={{
                          background: hidden ? '#f8fafc' : '#fff',
                          border: hidden ? '1px dashed #cbd5e1' : '1px solid #e2e8f0',
                          borderRadius: 12,
                          padding: 16,
                          opacity: hidden ? 0.85 : 1
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{job.title || '-'}</h4>
                          {hidden && (
                            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap' }}>
                              Hidden
                            </span>
                          )}
                        </div>
                        <p style={{ margin: '0 0 6px', fontSize: 13, color: '#64748b' }}>{job.company_name || '-'}</p>
                        <p style={{ margin: '0 0 6px', fontSize: 13, color: '#64748b' }}>{job.location || '-'}</p>
                        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#059669', fontWeight: 600 }}>
                          {(job.stipend_min != null || job.salary_min != null) && (job.stipend_max != null || job.salary_max != null)
                            ? `₹${(job.stipend_min ?? job.salary_min ?? 0).toLocaleString()} - ₹${(job.stipend_max ?? job.salary_max ?? 0).toLocaleString()}`
                            : 'Stipend not specified'}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <button type="button" onClick={() => handleCopyJobShareLink(job)} style={{ padding: '8px 12px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                            Share link
                          </button>
                          <button type="button" onClick={() => handleViewJobApplicants(job)} style={{ padding: '8px 12px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                            Applicants ({jobApplicantCounts[job.id] ?? 0})
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSupervisorJobVisibility(job, !hidden)}
                            style={{
                              padding: '8px 12px',
                              background: hidden ? '#0ea5e9' : '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            {hidden ? <><FaEye /> Unhide</> : <><FaEyeSlash /> Hide</>}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-table-sticky-scroll admin-table-sticky-scroll--light">
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff', minWidth: 900 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Job Title</th>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Company</th>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Location</th>
                        <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.875rem' }}>Stipend</th>
                        <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, fontSize: '0.875rem' }}>Applicants</th>
                        <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, fontSize: '0.875rem' }}>Visibility</th>
                        <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 700, fontSize: '0.875rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supervisorVisibleJobs.map((job, idx) => {
                        const hidden = isSupervisorJobHidden(job);
                        return (
                        <tr
                          key={job.id}
                          style={{
                            borderBottom: '1px solid #e2e8f0',
                            background: hidden ? '#f1f5f9' : (idx % 2 === 0 ? '#fff' : '#f8fafc'),
                            opacity: hidden ? 0.9 : 1
                          }}
                        >
                          <td style={{ padding: 16, fontSize: 14, fontWeight: 600 }}>{job.title || '-'}</td>
                          <td style={{ padding: 16, fontSize: 14 }}>{job.company_name || '-'}</td>
                          <td style={{ padding: 16, fontSize: 14 }}>{job.location || '-'}</td>
                          <td style={{ padding: 16, fontSize: 14 }}>
                            {(job.stipend_min != null || job.salary_min != null) && (job.stipend_max != null || job.salary_max != null)
                              ? `₹${(job.stipend_min ?? job.salary_min ?? 0).toLocaleString()} - ₹${(job.stipend_max ?? job.salary_max ?? 0).toLocaleString()}`
                              : '-'}
                          </td>
                          <td style={{ padding: 16, fontSize: 14, textAlign: 'center' }}>
                            <span style={{ padding: '4px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, background: '#e0e7ff', color: '#4338ca' }}>
                              {jobApplicantCounts[job.id] ?? 0}
                            </span>
                          </td>
                          <td style={{ padding: 16, textAlign: 'center' }}>
                            {hidden ? (
                              <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>Hidden</span>
                            ) : (
                              <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534' }}>Visible</span>
                            )}
                          </td>
                          <td style={{ padding: 16, textAlign: 'center' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleCopyJobShareLink(job)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#0d9488',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  cursor: 'pointer'
                                }}
                              >
                                Share link
                              </button>
                              <button
                                type="button"
                                onClick={() => handleViewJobApplicants(job)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#22c55e',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                <FaUsers /> View ({jobApplicantCounts[job.id] ?? 0})
                              </button>
                              <button
                                type="button"
                                title={hidden ? 'Show this job in your list again' : 'Hide this job from your list (you can restore later)'}
                                onClick={() => handleSupervisorJobVisibility(job, !hidden)}
                                style={{
                                  padding: '6px 12px',
                                  background: hidden ? '#0ea5e9' : '#ef4444',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 8,
                                  fontWeight: 600,
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                {hidden ? <><FaEye /> Unhide</> : <><FaEyeSlash /> Hide</>}
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {showSupervisorJobForm && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1100,
                  padding: 20
                }}
                onClick={(e) => {
                  if (e.target === e.currentTarget && !submittingSupervisorJob) {
                    setShowSupervisorJobForm(false);
                    setSupervisorJobFormError('');
                    setSupervisorJobFormSuccess('');
                    resetSupervisorJobFormFields();
                  }
                }}
              >
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: 24,
                    maxWidth: 600,
                    width: '100%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: 0 }}>Post new job</h3>
                    <button
                      type="button"
                      disabled={submittingSupervisorJob}
                      onClick={() => {
                        setShowSupervisorJobForm(false);
                        setSupervisorJobFormError('');
                        setSupervisorJobFormSuccess('');
                        resetSupervisorJobFormFields();
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: 24,
                        color: '#6b7280',
                        cursor: submittingSupervisorJob ? 'not-allowed' : 'pointer',
                        padding: 0,
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FaTimes />
                    </button>
                  </div>

                  <form onSubmit={handleSupervisorJobSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        Company <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div ref={jobPostCompanyTriggerRef} style={{ position: 'relative' }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <FaSearch style={{ position: 'absolute', left: 14, color: '#9ca3af', fontSize: 14, pointerEvents: 'none' }} />
                          <input
                            type="text"
                            autoComplete="off"
                            aria-label="Search and select company"
                            placeholder="Search by name, establishment code, or email..."
                            value={jobPostCompanyDropdownOpen
                              ? jobPostCompanySearch
                              : (jobPostCompanies.find((c) => String(c.id) === String(supervisorJobForm.company_id))?.company_name || '')}
                            onChange={(e) => {
                              setJobPostCompanySearch(e.target.value);
                              if (!e.target.value) {
                                setSupervisorJobForm((prev) => ({ ...prev, company_id: '' }));
                              }
                            }}
                            onFocus={() => {
                              setJobPostCompanyDropdownOpen(true);
                              const name = jobPostCompanies.find((c) => String(c.id) === String(supervisorJobForm.company_id))?.company_name || '';
                              setJobPostCompanySearch(jobPostCompanySearch || name);
                            }}
                            onBlur={() => {
                              setTimeout(() => setJobPostCompanyDropdownOpen(false), 180);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 16px 12px 40px',
                              border: '2px solid #e5e7eb',
                              borderRadius: 8,
                              fontSize: 14,
                              background: '#fff',
                              outline: 'none',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      </div>
                      <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#64748b' }}>
                        Type to filter, then choose a company from the list.
                      </p>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        Company logo <span style={{ color: '#64748b', fontWeight: 500 }}>(optional)</span>
                      </label>
                      <p style={{ margin: '0 0 8px 0', fontSize: 12, color: '#64748b' }}>
                        Shown on the public job share page. Max 2MB, images only.
                      </p>
                      <input
                        ref={supervisorJobLogoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={(e) => setSupervisorJobLogoFile(e.target.files?.[0] || null)}
                        style={{ fontSize: 14, width: '100%' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        Job title <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={supervisorJobForm.title}
                        onChange={handleSupervisorJobFormChange}
                        required
                        placeholder="e.g. Apprentice Electrician"
                        style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        Location <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={supervisorJobForm.location}
                        onChange={handleSupervisorJobFormChange}
                        required
                        placeholder="e.g. Mumbai, Maharashtra"
                        style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Age criteria <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="age_criteria"
                          value={supervisorJobForm.age_criteria}
                          onChange={handleSupervisorJobFormChange}
                          required
                          placeholder="e.g. 18-25"
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Min stipend (₹) <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="number"
                          name="stipend_min"
                          value={supervisorJobForm.stipend_min}
                          onChange={handleSupervisorJobFormChange}
                          required
                          min="0"
                          placeholder="5000"
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                          Max stipend (₹) <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="number"
                          name="stipend_max"
                          value={supervisorJobForm.stipend_max}
                          onChange={handleSupervisorJobFormChange}
                          required
                          min="0"
                          placeholder="10000"
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        Job description <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <textarea
                        name="description"
                        value={supervisorJobForm.description}
                        onChange={handleSupervisorJobFormChange}
                        required
                        placeholder="Enter detailed job description..."
                        rows={6}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14,
                          outline: 'none',
                          resize: 'vertical',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                        Qualifications <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {JOB_POST_QUALIFICATIONS.map((qual) => (
                          <button
                            key={qual}
                            type="button"
                            onClick={() => handleSupervisorJobQualToggle(qual)}
                            style={{
                              padding: '8px 16px',
                              border: `2px solid ${supervisorJobForm.qualification.includes(qual) ? '#667eea' : '#e5e7eb'}`,
                              borderRadius: 20,
                              fontSize: 13,
                              fontWeight: 600,
                              background: supervisorJobForm.qualification.includes(qual) ? '#667eea' : '#fff',
                              color: supervisorJobForm.qualification.includes(qual) ? '#fff' : '#374151',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {qual}
                          </button>
                        ))}
                      </div>
                      {supervisorJobForm.qualification.length === 0 && (
                        <p style={{ fontSize: 12, color: '#ef4444', marginTop: 8, margin: 0 }}>Select at least one qualification</p>
                      )}
                    </div>

                    {supervisorJobFormError && (
                      <div style={{ padding: '12px 16px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 14 }}>
                        {supervisorJobFormError}
                      </div>
                    )}
                    {supervisorJobFormSuccess && (
                      <div style={{ padding: '12px 16px', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 14 }}>
                        {supervisorJobFormSuccess}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                      <button
                        type="button"
                        disabled={submittingSupervisorJob}
                        onClick={() => {
                          setShowSupervisorJobForm(false);
                          setSupervisorJobFormError('');
                          setSupervisorJobFormSuccess('');
                          resetSupervisorJobFormFields();
                        }}
                        style={{
                          padding: '12px 24px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: submittingSupervisorJob ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submittingSupervisorJob}
                        style={{
                          padding: '12px 24px',
                          background: submittingSupervisorJob ? '#9ca3af' : '#667eea',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: submittingSupervisorJob ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        {submittingSupervisorJob ? 'Posting...' : 'Post job'}
                      </button>
                    </div>
                  </form>
                </div>
                {jobPostCompanyDropdownOpen && jobPostCompanyMenuPos && (
                  <div
                    role="listbox"
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      position: 'fixed',
                      top: jobPostCompanyMenuPos.top,
                      left: jobPostCompanyMenuPos.left,
                      width: jobPostCompanyMenuPos.width,
                      maxHeight: jobPostCompanyMenuPos.maxHeight,
                      overflowY: 'auto',
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                      zIndex: 12000
                    }}
                  >
                    {jobPostCompanies
                      .filter((co) => {
                        const q = (jobPostCompanySearch || '').toLowerCase().trim();
                        if (!q) return true;
                        return (
                          (co.company_name || '').toLowerCase().includes(q)
                          || (co.establishment_code || '').toLowerCase().includes(q)
                          || (co.email || '').toLowerCase().includes(q)
                        );
                      })
                      .map((co) => (
                        <div
                          key={co.id}
                          role="option"
                          aria-selected={String(supervisorJobForm.company_id) === String(co.id)}
                          tabIndex={0}
                          onClick={() => {
                            setSupervisorJobForm((prev) => ({ ...prev, company_id: String(co.id) }));
                            setJobPostCompanySearch('');
                            setJobPostCompanyDropdownOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSupervisorJobForm((prev) => ({ ...prev, company_id: String(co.id) }));
                              setJobPostCompanySearch('');
                              setJobPostCompanyDropdownOpen(false);
                            }
                          }}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            fontSize: 14,
                            borderBottom: '1px solid #f1f5f9',
                            background: String(supervisorJobForm.company_id) === String(co.id) ? '#f0f4ff' : '#fff'
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{co.company_name || `Company #${co.id}`}</div>
                          {(co.establishment_code || co.email) && (
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                              {[co.establishment_code, co.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                      ))}
                    {jobPostCompanies.length > 0
                      && jobPostCompanies.filter((co) => {
                        const q = (jobPostCompanySearch || '').toLowerCase().trim();
                        if (!q) return true;
                        return (
                          (co.company_name || '').toLowerCase().includes(q)
                          || (co.establishment_code || '').toLowerCase().includes(q)
                          || (co.email || '').toLowerCase().includes(q)
                        );
                      }).length === 0 && (
                        <div style={{ padding: '12px 14px', fontSize: 13, color: '#64748b' }}>No matching companies</div>
                      )}
                    {jobPostCompanies.length === 0 && (
                      <div style={{ padding: '12px 14px', fontSize: 13, color: '#64748b' }}>Loading companies…</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Job Applicants Modal (All Jobs view) */}
        {showJobApplicantsModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
              <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#374151' }}>Applicants for {selectedJob?.title}</h3>
                <button onClick={() => { setShowJobApplicantsModal(false); setSelectedJob(null); setJobApplicants([]); }} style={{ background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer', padding: 0 }}>×</button>
              </div>
              <div style={{ padding: 20, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {jobApplicantsLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><div className="admin-spinner" /></div>
                ) : jobApplicants.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No applicants for this job.</div>
                ) : (
                  <div className="admin-table-sticky-scroll admin-table-sticky-scroll--light admin-table-sticky-scroll--modal" style={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Mobile</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Qualification</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Course</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Applied Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobApplicants.map((app, i) => (
                          <tr key={app.id} style={{ borderBottom: '1px solid #e5e7eb', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: 12 }}>{app.name || '-'}</td>
                            <td style={{ padding: 12 }}>{app.email || '-'}</td>
                            <td style={{ padding: 12 }}>{app.mobile || '-'}</td>
                            <td style={{ padding: 12 }}>{app.qualification || '-'}</td>
                            <td style={{ padding: 12 }}>{app.course || '-'}</td>
                            <td style={{ padding: 12 }}>
                              <select
                                value={app.status || 'applied'}
                                onChange={(e) => handleApplicantStatusChange(app.id, e.target.value)}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  border: '1px solid #e5e7eb',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: app.status === 'selected' ? '#dcfce7' : app.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                                  color: app.status === 'selected' ? '#166534' : app.status === 'rejected' ? '#991b1b' : '#92400e',
                                  cursor: 'pointer',
                                  minWidth: 140
                                }}
                              >
                                <option value="applied">applied</option>
                                <option value="interview scheduled">interview scheduled</option>
                                <option value="selected">selected</option>
                                <option value="rejected">rejected</option>
                                <option value="agreed to join">agreed to join</option>
                              </select>
                            </td>
                            <td style={{ padding: 12 }}>{app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div style={{ padding: 20, borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                <button onClick={() => { setShowJobApplicantsModal(false); setSelectedJob(null); setJobApplicants([]); }} style={{ padding: '10px 20px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Campus Drive Modal */}
        {campusDriveModalOpen && (
          <div className="gogig-modal-bg" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 12 : 16, overflowY: 'auto' }} onClick={() => !campusDriveSubmitting && setCampusDriveModalOpen(false)}>
            <div className="gogig-modal" style={{ background: '#fff', borderRadius: isMobile ? 12 : 16, maxWidth: 560, width: '100%', maxHeight: isMobile ? 'none' : '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', margin: isMobile ? 'auto 0 24px' : undefined }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: isMobile ? 16 : 24, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 20, fontWeight: 700 }}>Add Campus Drive</h2>
                <button type="button" onClick={() => !campusDriveSubmitting && setCampusDriveModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, minWidth: 44, minHeight: 44 }}><FaTimes size={20} /></button>
              </div>
              <div style={{ padding: isMobile ? 16 : 24, display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 16 }}>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Campus Drive Name *</label><input type="text" value={campusDriveForm.campus_drive_name} onChange={e => setCampusDriveForm(f => ({ ...f, campus_drive_name: e.target.value }))} placeholder="e.g. GoGig Placement Drive 2025" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>College Name *</label><input type="text" value={campusDriveForm.college_name} onChange={e => setCampusDriveForm(f => ({ ...f, college_name: e.target.value }))} placeholder="College / Institute name" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>College Type *</label><select value={campusDriveForm.college_type} onChange={e => setCampusDriveForm(f => ({ ...f, college_type: e.target.value }))} style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', minHeight: isMobile ? 48 : undefined }}><option value="">Select</option><option value="ITI">ITI</option><option value="Diploma">Diploma</option><option value="Graduation">Graduation</option><option value="10th">10th</option><option value="12th">12th</option></select></div>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>POC Name</label><input type="text" value={campusDriveForm.poc_name} onChange={e => setCampusDriveForm(f => ({ ...f, poc_name: e.target.value }))} placeholder="Point of contact name" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>POC Email</label><input type="email" value={campusDriveForm.poc_email} onChange={e => setCampusDriveForm(f => ({ ...f, poc_email: e.target.value }))} placeholder="email@college.edu" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                  <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>POC Mobile</label><input type="text" value={campusDriveForm.poc_mobile} onChange={e => setCampusDriveForm(f => ({ ...f, poc_mobile: e.target.value }))} placeholder="10-digit mobile" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                </div>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>POC Designation</label><input type="text" value={campusDriveForm.poc_designation} onChange={e => setCampusDriveForm(f => ({ ...f, poc_designation: e.target.value }))} placeholder="e.g. Placement Officer" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>State</label>
                  <input type="text" placeholder="Search state..." value={campusDriveStateDropdownOpen ? campusDriveStateSearch : campusDriveForm.state} onChange={e => { setCampusDriveStateSearch(e.target.value); if (!e.target.value) setCampusDriveForm(f => ({ ...f, state: '' })); }} onFocus={() => { setCampusDriveStateDropdownOpen(true); setCampusDriveStateSearch(campusDriveStateSearch || campusDriveForm.state); }} onBlur={() => setTimeout(() => setCampusDriveStateDropdownOpen(false), 200)} style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} />
                  {campusDriveStateDropdownOpen && <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10 }}>{INDIAN_STATES_DISTRICTS.states.filter(s => !campusDriveStateSearch || s.state.toLowerCase().includes(campusDriveStateSearch.toLowerCase())).map(s => <div key={s.state} role="button" tabIndex={0} style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f1f5f9' }} onClick={() => { setCampusDriveForm(f => ({ ...f, state: s.state })); setCampusDriveStateSearch(''); setCampusDriveStateDropdownOpen(false); setCampusDriveForm(f => ({ ...f, district: '' })); }}>{s.state}</div>)}</div>}
                </div>
                <div style={{ position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>District</label>
                  <input type="text" placeholder="Search district..." value={campusDriveDistrictDropdownOpen ? campusDriveDistrictSearch : campusDriveForm.district} onChange={e => { setCampusDriveDistrictSearch(e.target.value); if (!e.target.value) setCampusDriveForm(f => ({ ...f, district: '' })); }} onFocus={() => { setCampusDriveDistrictDropdownOpen(true); setCampusDriveDistrictSearch(campusDriveDistrictSearch || campusDriveForm.district); }} onBlur={() => setTimeout(() => setCampusDriveDistrictDropdownOpen(false), 200)} style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} />
                  {campusDriveDistrictDropdownOpen && <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10 }}>{(campusDriveForm.state ? (INDIAN_STATES_DISTRICTS.states.find(s => s.state === campusDriveForm.state)?.districts || []) : INDIAN_STATES_DISTRICTS.states.flatMap(s => s.districts)).filter(d => !campusDriveDistrictSearch || d.toLowerCase().includes(campusDriveDistrictSearch.toLowerCase())).map(d => <div key={d} role="button" tabIndex={0} style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f1f5f9' }} onClick={() => { setCampusDriveForm(f => ({ ...f, district: d })); setCampusDriveDistrictSearch(''); setCampusDriveDistrictDropdownOpen(false); }}>{d}</div>)}</div>}
                </div>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Address of College</label><textarea value={campusDriveForm.college_address} onChange={e => setCampusDriveForm(f => ({ ...f, college_address: e.target.value }))} placeholder="Full address" rows={3} style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, resize: 'vertical', minHeight: isMobile ? 80 : undefined }} /></div>
                <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Date of Drive</label><input type="date" value={campusDriveForm.drive_date} onChange={e => setCampusDriveForm(f => ({ ...f, drive_date: e.target.value }))} style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Total Participated</label><input type="number" min={0} value={campusDriveForm.total_participated} onChange={e => setCampusDriveForm(f => ({ ...f, total_participated: e.target.value }))} placeholder="0" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                  <div><label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Total Joined</label><input type="number" min={0} value={campusDriveForm.total_joined} onChange={e => setCampusDriveForm(f => ({ ...f, total_joined: e.target.value }))} placeholder="0" style={{ width: '100%', padding: isMobile ? '12px 14px' : '10px 12px', fontSize: isMobile ? 16 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, minHeight: isMobile ? 48 : undefined }} /></div>
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button type="button" onClick={() => !campusDriveSubmitting && setCampusDriveModalOpen(false)} style={{ padding: isMobile ? '14px 20px' : '10px 20px', minHeight: isMobile ? 48 : undefined, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: isMobile ? 16 : undefined, width: isMobile ? '100%' : undefined }}>Cancel</button>
                  <button type="button" onClick={submitCampusDrive} disabled={campusDriveSubmitting} style={{ padding: isMobile ? '14px 20px' : '10px 20px', minHeight: isMobile ? 48 : undefined, background: 'linear-gradient(135deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', border: 'none', borderRadius: 8, cursor: campusDriveSubmitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: isMobile ? 16 : undefined, width: isMobile ? '100%' : undefined }}>{campusDriveSubmitting ? 'Adding...' : 'Add Campus Drive'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'attendance' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Today's Attendance Card */}
            <div style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(102,126,234,0.1)',
              border: '1.5px solid #e5e7eb',
              padding: isMobile ? 20 : 32
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 700,
                  color: '#667eea',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <FaClock style={{ fontSize: isMobile ? 20 : 24 }} />
                  Today's Attendance
                </h2>
                <button
                  onClick={() => {
                    // Reset refs to allow fresh fetch
                    fetchingTodayAttendance.current = false;
                    fetchingAttendanceHistory.current = false;
                    fetchTodayAttendance();
                    fetchAttendanceHistory();
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                  title="Refresh attendance"
                >
                  <FaCheck />
                  Refresh
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 20 }}>
                  {/* Check-in Status */}
                  <div style={{
                    padding: isMobile ? 16 : 20,
                    background: todayAttendance && todayAttendance.check_in_time ? '#dcfce7' : '#fef2f2',
                    borderRadius: isMobile ? 12 : 12,
                    border: `2px solid ${todayAttendance && todayAttendance.check_in_time ? '#10b981' : '#ef4444'}`
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: isMobile ? 'flex-start' : 'center', 
                      marginBottom: isMobile ? 16 : 12,
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? 12 : 0
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, flex: 1 }}>
                        <FaSignInAlt style={{ 
                          fontSize: isMobile ? 28 : 24, 
                          color: todayAttendance && todayAttendance.check_in_time ? '#10b981' : '#ef4444',
                          flexShrink: 0
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 600, color: '#1f2937' }}>
                            Check-In
                          </h3>
                          <p style={{ margin: '4px 0 0 0', fontSize: isMobile ? 13 : 14, color: '#6b7280', wordBreak: 'break-word' }}>
                            {todayAttendance && todayAttendance.check_in_time 
                              ? (() => {
                                  const istTime = formatTimeIST(todayAttendance.check_in_time);
                                  console.log('✅ Displaying check-in time:', todayAttendance.check_in_time, '→', istTime);
                                  return `Completed at ${istTime}`;
                                })()
                              : 'Not checked in yet'
                            }
                          </p>
                        </div>
                      </div>
                      {todayAttendance && todayAttendance.check_in_image && (
                        <img 
                          src={`${API_URL}${todayAttendance.check_in_image}`}
                          alt="Check-in"
                          style={{
                            width: isMobile ? 70 : 60,
                            height: isMobile ? 70 : 60,
                            borderRadius: 8,
                            objectFit: 'cover',
                            border: '2px solid #e5e7eb',
                            flexShrink: 0
                          }}
                        />
                      )}
                    </div>
                    {(!todayAttendance || !todayAttendance.check_in_time) && !attendanceImage && (
                      <button
                        onClick={() => startAttendanceCamera('checkin')}
                        disabled={attendanceLoading}
                        style={{
                          width: '100%',
                          padding: isMobile ? '14px 20px' : '12px 24px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: isMobile ? 15 : 16,
                          fontWeight: 600,
                          cursor: attendanceLoading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          opacity: attendanceLoading ? 0.7 : 1,
                          minHeight: isMobile ? 48 : 44,
                          touchAction: 'manipulation'
                        }}
                      >
                        <FaCamera style={{ fontSize: isMobile ? 18 : 16 }} />
                        {isMobile ? 'Capture & Check In' : 'Capture Photo & Check In'}
                      </button>
                    )}
                  </div>

                  {/* Check-out Status */}
                  {todayAttendance && todayAttendance.check_in_time && (
                    <div style={{
                      padding: isMobile ? 16 : 20,
                      background: todayAttendance.check_out_time ? '#dcfce7' : '#fef2f2',
                      borderRadius: isMobile ? 12 : 12,
                      border: `2px solid ${todayAttendance.check_out_time ? '#10b981' : '#ef4444'}`
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: isMobile ? 'flex-start' : 'center', 
                        marginBottom: isMobile ? 16 : 12,
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? 12 : 0
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, flex: 1 }}>
                          <FaSignOut style={{ 
                            fontSize: isMobile ? 28 : 24, 
                            color: todayAttendance.check_out_time ? '#10b981' : '#ef4444',
                            flexShrink: 0
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 600, color: '#1f2937' }}>
                              Check-Out
                            </h3>
                            <p style={{ margin: '4px 0 0 0', fontSize: isMobile ? 13 : 14, color: '#6b7280', wordBreak: 'break-word' }}>
                              {todayAttendance.check_out_time 
                                ? (() => {
                                    const istTime = formatTimeIST(todayAttendance.check_out_time);
                                    console.log('✅ Displaying check-out time:', todayAttendance.check_out_time, '→', istTime);
                                    return `Completed at ${istTime}`;
                                  })()
                                : 'Not checked out yet'
                              }
                            </p>
                          </div>
                        </div>
                        {todayAttendance.check_out_image && (
                          <img 
                            src={`${API_URL}${todayAttendance.check_out_image}`}
                            alt="Check-out"
                            style={{
                              width: isMobile ? 70 : 60,
                              height: isMobile ? 70 : 60,
                              borderRadius: 8,
                              objectFit: 'cover',
                              border: '2px solid #e5e7eb',
                              flexShrink: 0
                            }}
                          />
                        )}
                      </div>
                      {!todayAttendance.check_out_time && (
                        <button
                          onClick={() => startAttendanceCamera('checkout')}
                          disabled={attendanceLoading}
                          style={{
                            width: '100%',
                            padding: isMobile ? '14px 20px' : '12px 24px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: isMobile ? 15 : 16,
                            fontWeight: 600,
                            cursor: attendanceLoading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            opacity: attendanceLoading ? 0.7 : 1,
                            minHeight: isMobile ? 48 : 44,
                            touchAction: 'manipulation'
                          }}
                        >
                          <FaCamera style={{ fontSize: isMobile ? 18 : 16 }} />
                          {isMobile 
                            ? (attendanceImage && attendanceType === 'checkout' ? 'Photo Ready - Check Out' : 'Capture & Check Out')
                            : (attendanceImage && attendanceType === 'checkout' ? 'Photo Captured - Click to Check Out' : 'Capture Photo & Check Out')
                          }
                        </button>
                      )}
                    </div>
                  )}

                  {/* Submit buttons for captured images */}
                  {attendanceImage && attendanceType === 'checkin' && (!todayAttendance || !todayAttendance.check_in_time) && (
                    <div style={{ 
                      marginTop: 16,
                      padding: 16,
                      background: '#f0fdf4',
                      border: '2px solid #10b981',
                      borderRadius: 12
                    }}>
                      <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#166534', fontWeight: 600 }}>
                        ✓ Photo captured! Ready to check in.
                      </p>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button
                          onClick={handleCheckIn}
                          disabled={attendanceLoading}
                          style={{
                            flex: 1,
                            padding: '12px 24px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: attendanceLoading ? 'not-allowed' : 'pointer',
                            opacity: attendanceLoading ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8
                          }}
                        >
                          {attendanceLoading ? (
                            <>
                              <div style={{ width: 16, height: 16, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FaCheck />
                              Submit Check-In
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setAttendanceImage(null);
                            setAttendanceType(null);
                            toast('Photo cleared. You can capture a new photo.', { icon: 'ℹ️' });
                          }}
                          style={{
                            padding: '12px 24px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {attendanceImage && attendanceType === 'checkout' && todayAttendance?.check_in_time && !todayAttendance?.check_out_time && (
                    <div style={{ 
                      marginTop: 16,
                      padding: 16,
                      background: '#fef2f2',
                      border: '2px solid #ef4444',
                      borderRadius: 12
                    }}>
                      <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#991b1b', fontWeight: 600 }}>
                        ✓ Photo captured! Ready to check out.
                      </p>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button
                          onClick={handleCheckOut}
                          disabled={attendanceLoading}
                          style={{
                            flex: 1,
                            padding: '12px 24px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: attendanceLoading ? 'not-allowed' : 'pointer',
                            opacity: attendanceLoading ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8
                          }}
                        >
                          {attendanceLoading ? (
                            <>
                              <div style={{ width: 16, height: 16, border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FaCheck />
                              Submit Check-Out
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setAttendanceImage(null);
                            setAttendanceType(null);
                            toast('Photo cleared. You can capture a new photo.', { icon: 'ℹ️' });
                          }}
                          style={{
                            padding: '12px 24px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
            </div>

            {/* Attendance History */}
            <div style={{
              background: '#fff',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(102,126,234,0.1)',
              border: '1.5px solid #e5e7eb',
              padding: isMobile ? 20 : 32
            }}>
              <h2 style={{
                fontSize: isMobile ? 20 : 24,
                fontWeight: 700,
                color: '#667eea',
                margin: '0 0 24px 0'
              }}>
                Attendance History ({new Date().toLocaleString('default', { month: 'long', year: 'numeric' })})
              </h2>

              {attendanceLoading ? (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <div className="spinner" style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                </div>
              ) : attendanceHistory.length > 0 ? (
                <div className="admin-table-sticky-scroll admin-table-sticky-scroll--light" style={{ maxHeight: 'min(52vh, 420px)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>Check-In</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>Check-Out</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>Images</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceHistory.map((att) => (
                        <tr key={att.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: 14, color: '#374151' }}>
                            {formatDateIST(att.date)}
                          </td>
                          <td style={{ padding: '12px', fontSize: 14, color: '#374151' }}>
                            {(() => {
                              const istTime = formatTimeIST(att.check_in_time);
                              console.log('✅ Displaying history check-in:', att.check_in_time, '→', istTime);
                              return istTime;
                            })()}
                          </td>
                          <td style={{ padding: '12px', fontSize: 14, color: '#374151' }}>
                            {(() => {
                              const istTime = formatTimeIST(att.check_out_time);
                              console.log('✅ Displaying history check-out:', att.check_out_time, '→', istTime);
                              return istTime;
                            })()}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600,
                              background: att.status === 'checked_out' ? '#dcfce7' : att.status === 'checked_in' ? '#fef3c7' : '#fee2e2',
                              color: att.status === 'checked_out' ? '#166534' : att.status === 'checked_in' ? '#92400e' : '#991b1b'
                            }}>
                              {att.status === 'checked_out' ? 'Complete' : att.status === 'checked_in' ? 'Checked In' : 'Absent'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {att.check_in_image && (
                                <img 
                                  src={`${API_URL}${att.check_in_image}`}
                                  alt="Check-in"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 6,
                                    objectFit: 'cover',
                                    border: '1px solid #e5e7eb',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => window.open(`${API_URL}${att.check_in_image}`, '_blank')}
                                />
                              )}
                              {att.check_out_image && (
                                <img 
                                  src={`${API_URL}${att.check_out_image}`}
                                  alt="Check-out"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 6,
                                    objectFit: 'cover',
                                    border: '1px solid #e5e7eb',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => window.open(`${API_URL}${att.check_out_image}`, '_blank')}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <FaClock style={{ fontSize: 48, opacity: 0.5, marginBottom: 16 }} />
                  <p style={{ fontSize: 16, margin: 0 }}>No attendance records found for this month</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Attendance Camera Modal */}
        {attendanceCameraModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}>
            <div style={{
              width: '100%',
              maxWidth: isMobile ? '100%' : 640,
              background: '#000',
              borderRadius: 12,
              overflow: 'hidden',
              position: 'relative'
            }}>
              <video
                ref={attendanceVideoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
              <div style={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 12
              }}>
                <button
                  type="button"
                  onClick={captureAttendancePhoto}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    border: '4px solid #fff',
                    background: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                >
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: '#fff',
                    border: '2px solid #667eea'
                  }} />
                </button>
              </div>
              <button
                type="button"
                onClick={stopAttendanceCamera}
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20
                }}
              >
                <FaTimes />
              </button>
            </div>
            <p style={{ color: '#fff', marginTop: 16, textAlign: 'center', fontSize: 14 }}>
              Position yourself in the frame and tap the button to capture
            </p>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="gogig-modal-bg" onClick={() => setShowUploadModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '90%' : 600, 
              maxWidth: isMobile ? '95%' : 800,
              maxHeight: isMobile ? '90vh' : '80vh',
              overflowY: 'auto'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ color: '#667eea', fontWeight: 700, fontSize: 24, marginBottom: 20 }}>
                Upload Candidates CSV
              </h3>
              
              <div style={{ marginBottom: 20 }}>
                <p style={{ color: '#666', marginBottom: 16 }}>
                  Upload a CSV using the exact same column names as the dashboard. Required: <strong>Name</strong>, <strong>Email</strong>, <strong>Mobile</strong>. All other columns are optional but will show on the dashboard when filled.
                </p>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                  <ul style={{ color: '#666', marginBottom: 16, paddingLeft: 20, fontSize: 14 }}>
                    <li><strong>Name</strong>, <strong>Email</strong>, <strong>Mobile</strong> — required</li>
                    <li><strong>Establishment</strong>, <strong>Month</strong>, <strong>Punch Code</strong>, <strong>Employee Code</strong>, <strong>Remark</strong>, <strong>Contract</strong>, <strong>Apprentice</strong></li>
                    <li><strong>Aadhar</strong>, <strong>DOB</strong>, <strong>Gender</strong>, <strong>Father</strong>, <strong>Mother</strong>, <strong>Category</strong>, <strong>Address</strong>, <strong>District</strong>, <strong>State</strong>, <strong>Pincode</strong></li>
                    <li><strong>Qualification</strong>, <strong>Institute</strong>, <strong>Specialization</strong>, <strong>Bank</strong>, <strong>Account No</strong>, <strong>IFSC</strong>, <strong>Seeded</strong>, <strong>Beni ID</strong></li>
                    <li><strong>Join Date</strong>, <strong>Stipend</strong>, <strong>Job Role</strong>, <strong>Est. Address</strong>, <strong>Insurance</strong>, <strong>ID Card</strong>, <strong>Location</strong>, <strong>Referral Code</strong></li>
                    <li>Optional linking columns: <strong>Alternate Mobile</strong>, <strong>Course</strong>, <strong>Company ID</strong>, <strong>Establishment Code</strong></li>
                    <li>Older names still work (e.g. Emp Code, Punching Employee Code, Date of Birth)</li>
                  </ul>
                  <div style={{ 
                    padding: 12, 
                    background: '#fef3c7', 
                    borderRadius: 8, 
                    marginTop: 12,
                    border: '1px solid #fbbf24'
                  }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                      <strong>Note:</strong> Download the template below so headers match the dashboard. Wrong/missing headers leave those columns blank. Import is data-only — create accounts later in Pending Accounts.
                    </p>
                  </div>
                </div>
                
                <button
                  type="button"
                  className="gogig-card-btn-outline"
                  onClick={downloadTemplate}
                  style={{
                    padding: '8px 16px',
                    background: '#f8fafc',
                    color: '#667eea',
                    border: '2px solid #667eea',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <FaDownload />
                  Download Template
                </button>
              </div>

              <form onSubmit={handleCsvUpload}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                    Select CSV File *
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e1e5e9',
                      borderRadius: 8,
                      fontSize: 16,
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
                  />
                </div>

                {uploadResult && (
                  <div style={{
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 16,
                    background: uploadResult.imported > 0 ? '#dcfce7' : '#fee2e2',
                    color: uploadResult.imported > 0 ? '#166534' : '#991b1b',
                    border: `1px solid ${uploadResult.imported > 0 ? '#bbf7d0' : '#fecaca'}`,
                    maxHeight: 280,
                    overflowY: 'auto'
                  }}>
                    <strong>{uploadResult.message}</strong>
                    {(uploadResult.imported != null || uploadResult.failed != null) && (
                      <div style={{ marginTop: 8, fontSize: 13 }}>
                        Imported: <strong>{uploadResult.imported || 0}</strong>
                        {uploadResult.totalRows != null && <> / {uploadResult.totalRows}</>}
                        {uploadResult.failed > 0 && <> · Failed: <strong>{uploadResult.failed}</strong></>}
                      </div>
                    )}
                    {uploadResult.errorSummary && (
                      <div style={{ marginTop: 8, fontSize: 12, opacity: 0.95 }}>
                        {[
                          uploadResult.errorSummary.missingRequired > 0 && `Missing required: ${uploadResult.errorSummary.missingRequired}`,
                          uploadResult.errorSummary.invalidMobile > 0 && `Invalid mobile: ${uploadResult.errorSummary.invalidMobile}`,
                          uploadResult.errorSummary.invalidEmail > 0 && `Invalid email: ${uploadResult.errorSummary.invalidEmail}`,
                          uploadResult.errorSummary.duplicates > 0 && `Duplicates: ${uploadResult.errorSummary.duplicates}`,
                          uploadResult.errorSummary.other > 0 && `Other: ${uploadResult.errorSummary.other}`,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {uploadResult.headerWarnings && uploadResult.headerWarnings.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <strong>Header warnings:</strong>
                        <ul style={{ margin: 4, paddingLeft: 20, fontSize: 12 }}>
                          {uploadResult.headerWarnings.map((w, idx) => (
                            <li key={`hw-${idx}`}>{w}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <strong>Row errors{uploadResult.totalErrors > uploadResult.errors.length ? ` (showing ${uploadResult.errors.length} of ${uploadResult.totalErrors})` : ''}:</strong>
                        <ul style={{ margin: 4, paddingLeft: 20, fontSize: 12 }}>
                          {uploadResult.errors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="submit"
                    className="gogig-card-btn"
                    disabled={uploading}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.7 : 1
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Upload CSV'}
                  </button>
                  <button
                    type="button"
                    className="gogig-card-btn-outline"
                    onClick={() => setShowUploadModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#f8fafc',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pending Accounts Modal */}
        {showPendingAccountsModal && (
          <div className="gogig-modal-bg" onClick={() => setShowPendingAccountsModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '95%' : 1000, 
              maxWidth: isMobile ? '98%' : 1400,
              maxHeight: '90vh', 
              overflowY: 'auto'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h3 style={{ color: '#667eea', fontWeight: 700, fontSize: 24, margin: 0 }}>
                  Pending Account Creation
                </h3>
                <button
                  onClick={() => setShowPendingAccountsModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: 24,
                    color: '#666',
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <div style={{ marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <input
                    type="text"
                    placeholder="Search by name, email, or mobile..."
                    value={pendingSearch}
                    onChange={(e) => {
                      setPendingSearch(e.target.value);
                      fetchPendingCandidates(1, e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e1e5e9',
                      borderRadius: 8,
                      fontSize: 16
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={selectAllPending}
                  className="gogig-card-btn-outline"
                  style={{
                    padding: '12px 24px',
                    background: '#f8fafc',
                    color: '#667eea',
                    border: '2px solid #667eea',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {selectedPendingIds.size === pendingCandidates.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  type="button"
                  onClick={() => handleCreateAccounts()}
                  disabled={selectedPendingIds.size === 0 || creatingAccounts}
                  className="gogig-card-btn"
                  style={{
                    padding: '12px 24px',
                    background: selectedPendingIds.size === 0 ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: selectedPendingIds.size === 0 ? 'not-allowed' : 'pointer',
                    opacity: creatingAccounts ? 0.7 : 1
                  }}
                >
                  {creatingAccounts ? 'Creating...' : `Create Accounts (${selectedPendingIds.size})`}
                </button>
                <button
                  type="button"
                  onClick={handleCreateAllAccounts}
                  disabled={pendingTotal === 0 || creatingAccounts}
                  className="gogig-card-btn"
                  style={{
                    padding: '12px 24px',
                    background: pendingTotal === 0 ? '#ccc' : '#f59e0b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: pendingTotal === 0 ? 'not-allowed' : 'pointer',
                    opacity: creatingAccounts ? 0.7 : 1
                  }}
                >
                  {creatingAccounts ? 'Creating...' : `Create All (${pendingTotal})`}
                </button>
              </div>

              <div style={{ 
                background: '#f8fafc', 
                padding: 16, 
                borderRadius: 8, 
                marginBottom: 20,
                border: '1px solid #e5e7eb'
              }}>
                <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
                  <strong>Total Pending:</strong> {pendingTotal} candidates | 
                  <strong> Selected:</strong> {selectedPendingIds.size} candidates
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#92400e' }}>
                  These candidates were imported via CSV but don't have accounts yet. Select candidates and create accounts to enable login.
                </p>
              </div>

              {pendingCandidates.length > 0 ? (
                <div className="admin-table-sticky-scroll admin-table-sticky-scroll--brand" style={{ maxHeight: 'min(52vh, 480px)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>
                          <input
                            type="checkbox"
                            checked={selectedPendingIds.size === pendingCandidates.length && pendingCandidates.length > 0}
                            onChange={selectAllPending}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Mobile</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Qualification</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: 14, fontWeight: 600 }}>Institute</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingCandidates.map((candidate, idx) => (
                        <tr key={candidate.id} style={{ 
                          borderBottom: '1px solid #f3f4f6',
                          background: idx % 2 === 0 ? '#fff' : '#fafbfc'
                        }}>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedPendingIds.has(candidate.id)}
                              onChange={() => togglePendingSelection(candidate.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '12px', fontSize: 14 }}>{candidate.name}</td>
                          <td style={{ padding: '12px', fontSize: 14 }}>{candidate.email}</td>
                          <td style={{ padding: '12px', fontSize: 14 }}>{candidate.mobile}</td>
                          <td style={{ padding: '12px', fontSize: 14 }}>{candidate.qualification || '-'}</td>
                          <td style={{ padding: '12px', fontSize: 14 }}>{candidate.institute_name || '-'}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <button
                              onClick={() => handleCreateAccounts([candidate.id])}
                              disabled={creatingAccounts}
                              style={{
                                padding: '6px 12px',
                                background: '#667eea',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                cursor: creatingAccounts ? 'not-allowed' : 'pointer',
                                fontSize: 12,
                                fontWeight: 600
                              }}
                            >
                              Create Account
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {pendingTotalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
                      <button
                        onClick={() => {
                          const newPage = Math.max(1, pendingPage - 1);
                          setPendingPage(newPage);
                          fetchPendingCandidates(newPage, pendingSearch);
                        }}
                        disabled={pendingPage === 1}
                        style={{
                          padding: '8px 16px',
                          background: pendingPage === 1 ? '#e5e7eb' : '#667eea',
                          color: pendingPage === 1 ? '#9ca3af' : '#fff',
                          border: 'none',
                          borderRadius: 8,
                          cursor: pendingPage === 1 ? 'not-allowed' : 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: 14, color: '#666' }}>
                        Page {pendingPage} of {pendingTotalPages}
                      </span>
                      <button
                        onClick={() => {
                          const newPage = Math.min(pendingTotalPages, pendingPage + 1);
                          setPendingPage(newPage);
                          fetchPendingCandidates(newPage, pendingSearch);
                        }}
                        disabled={pendingPage === pendingTotalPages}
                        style={{
                          padding: '8px 16px',
                          background: pendingPage === pendingTotalPages ? '#e5e7eb' : '#667eea',
                          color: pendingPage === pendingTotalPages ? '#9ca3af' : '#fff',
                          border: 'none',
                          borderRadius: 8,
                          cursor: pendingPage === pendingTotalPages ? 'not-allowed' : 'pointer',
                          fontWeight: 600
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                  <p style={{ fontSize: 16, margin: 0 }}>No pending candidates found.</p>
                  <p style={{ fontSize: 14, margin: '8px 0 0 0' }}>All imported candidates have accounts created.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Candidate Modal with Document Upload */}
        {showCreateCandidateModal && (
          <div className="gogig-modal-bg" onClick={() => setShowCreateCandidateModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '95%' : 900, 
              maxWidth: isMobile ? '98%' : 1400, 
              maxHeight: '90vh', 
              overflowY: 'auto',
              padding: isMobile ? '16px' : '24px'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ color: '#22c55e', fontWeight: 700, fontSize: 24, marginBottom: 20 }}>
                Add New Candidate
              </h3>
              <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
                Fill in candidate details. <strong style={{ color: '#ef4444' }}>At least one document is mandatory.</strong>
              </p>

              <form onSubmit={handleCreateCandidate}>
                {/* Basic Information Section */}
                <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '2px solid #e5e7eb' }}>
                  <h4 style={{ color: '#667eea', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Basic Information</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#333', fontSize: 14 }}>
                        Name <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={candidateForm.name}
                        onChange={(e) => setCandidateForm({...candidateForm, name: e.target.value})}
                        required
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#333', fontSize: 14 }}>
                        Email <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="email"
                        value={candidateForm.email}
                        onChange={(e) => setCandidateForm({...candidateForm, email: e.target.value})}
                        required
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#333', fontSize: 14 }}>
                        Mobile <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="tel"
                        value={candidateForm.mobile}
                        onChange={(e) => setCandidateForm({...candidateForm, mobile: e.target.value})}
                        required
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#333', fontSize: 14 }}>
                        Password
                      </label>
                      <input
                        type="password"
                        value={candidateForm.password}
                        onChange={(e) => setCandidateForm({...candidateForm, password: e.target.value})}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 14 }}
                        placeholder="Leave empty for default password"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#333', fontSize: 14 }}>
                        Qualification
                      </label>
                      <input
                        type="text"
                        value={candidateForm.qualification}
                        onChange={(e) => setCandidateForm({...candidateForm, qualification: e.target.value})}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#333', fontSize: 14 }}>
                        Course
                      </label>
                      <input
                        type="text"
                        value={candidateForm.course}
                        onChange={(e) => setCandidateForm({...candidateForm, course: e.target.value})}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 14 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Documents Section - Mandatory */}
                <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '2px solid #e5e7eb' }}>
                  <h4 style={{ color: '#667eea', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                    Documents <span style={{ color: '#ef4444', fontSize: 14, fontWeight: 400 }}>(At least one required)</span>
                  </h4>
                  <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 16 }}>
                    Upload documents in image (JPG, PNG, WEBP), PDF, or DOCX format. Maximum file size: 10MB.
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
                    {/* Aadhar */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        Aadhar Card
                      </label>
                      {newCandidateDocuments.aadhar ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <FaFileAlt style={{ color: '#667eea', fontSize: 16 }} />
                          <span style={{ flex: 1, fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {newCandidateDocuments.aadhar.name}
                          </span>
                          <button type="button" onClick={() => handleRemoveNewCandidateDocument('aadhar')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                            <FaTrash style={{ fontSize: 10 }} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-aadhar-upload" accept="image/*,.pdf,.doc,.docx" onChange={(e) => handleNewCandidateDocumentChange('aadhar', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-aadhar-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('aadhar')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>

                    {/* School Certificate */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        School Certificate
                      </label>
                      {newCandidateDocuments.school_certificate ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <FaFileAlt style={{ color: '#667eea', fontSize: 16 }} />
                          <span style={{ flex: 1, fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {newCandidateDocuments.school_certificate.name}
                          </span>
                          <button type="button" onClick={() => handleRemoveNewCandidateDocument('school_certificate')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                            <FaTrash style={{ fontSize: 10 }} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-school-certificate-upload" accept="image/*,.pdf,.doc,.docx" onChange={(e) => handleNewCandidateDocumentChange('school_certificate', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-school-certificate-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('school_certificate')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Educational Certificate */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        Educational Certificate
                      </label>
                      {newCandidateDocuments.educational_certificate ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <FaFileAlt style={{ color: '#667eea', fontSize: 16 }} />
                          <span style={{ flex: 1, fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {newCandidateDocuments.educational_certificate.name}
                          </span>
                          <button type="button" onClick={() => handleRemoveNewCandidateDocument('educational_certificate')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                            <FaTrash style={{ fontSize: 10 }} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-educational-certificate-upload" accept="image/*,.pdf,.doc,.docx" onChange={(e) => handleNewCandidateDocumentChange('educational_certificate', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-educational-certificate-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('educational_certificate')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Bank Passbook */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        Bank Passbook
                      </label>
                      {newCandidateDocuments.bank_passbook ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <FaFileAlt style={{ color: '#667eea', fontSize: 16 }} />
                          <span style={{ flex: 1, fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {newCandidateDocuments.bank_passbook.name}
                          </span>
                          <button type="button" onClick={() => handleRemoveNewCandidateDocument('bank_passbook')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                            <FaTrash style={{ fontSize: 10 }} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-bank-passbook-upload" accept="image/*,.pdf" onChange={(e) => handleNewCandidateDocumentChange('bank_passbook', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-bank-passbook-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('bank_passbook')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Candidate Image */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        Candidate Photo
                      </label>
                      {newCandidateDocuments.candidate_image ? (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            {newCandidateDocuments.candidate_image.type.startsWith('image/') && (
                              <img src={URL.createObjectURL(newCandidateDocuments.candidate_image)} alt="Candidate" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', border: '2px solid #e5e7eb' }} />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {newCandidateDocuments.candidate_image.name}
                              </div>
                            </div>
                            <button type="button" onClick={() => handleRemoveNewCandidateDocument('candidate_image')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                              <FaTrash style={{ fontSize: 10 }} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-candidate-image-upload" accept="image/*" onChange={(e) => handleNewCandidateDocumentChange('candidate_image', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-candidate-image-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('candidate_image')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>

                    {/* PAN Card */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        PAN Card
                      </label>
                      {newCandidateDocuments.pancard ? (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {newCandidateDocuments.pancard.name}
                              </div>
                            </div>
                            <button type="button" onClick={() => handleRemoveNewCandidateDocument('pancard')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                              <FaTrash style={{ fontSize: 10 }} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-pancard-upload" accept="image/*,.pdf" onChange={(e) => handleNewCandidateDocumentChange('pancard', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-pancard-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('pancard')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Signature */}
                    <div style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                        Signature
                      </label>
                      {newCandidateDocuments.signature ? (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            {newCandidateDocuments.signature.type?.startsWith('image/') && (
                              <img src={URL.createObjectURL(newCandidateDocuments.signature)} alt="Signature" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', border: '2px solid #e5e7eb' }} />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {newCandidateDocuments.signature.name}
                              </div>
                            </div>
                            <button type="button" onClick={() => handleRemoveNewCandidateDocument('signature')} style={{ background: '#fee2e2', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', color: '#dc2626' }}>
                              <FaTrash style={{ fontSize: 10 }} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 6, marginTop: 6 }}>
                          <input type="file" id="new-signature-upload" accept="image/*,.pdf" onChange={(e) => handleNewCandidateDocumentChange('signature', e.target.files[0])} style={{ display: 'none' }} />
                          <label htmlFor="new-signature-upload" style={{ flex: 1, padding: '8px', border: '2px dashed #cbd5e1', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f8fafc', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <FaUpload style={{ fontSize: 10 }} /> Upload
                          </label>
                          <button type="button" onClick={() => startCandidateCamera('signature')} style={{ padding: '8px', border: '2px solid #667eea', borderRadius: 6, textAlign: 'center', cursor: 'pointer', background: '#f0f4ff', fontSize: 11, color: '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600 }}>
                            <FaCamera style={{ fontSize: 10 }} /> Capture
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="submit"
                    className="gogig-card-btn"
                    disabled={creatingCandidate}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: creatingCandidate ? 'not-allowed' : 'pointer',
                      opacity: creatingCandidate ? 0.7 : 1
                    }}
                  >
                    {creatingCandidate ? 'Creating...' : 'Create Candidate'}
                  </button>
                  <button
                    type="button"
                    className="gogig-card-btn-outline"
                    onClick={() => setShowCreateCandidateModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#f8fafc',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Candidate Camera Modal */}
        {showCandidateCameraModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.9)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ width: '100%', maxWidth: isMobile ? '100%' : 640, background: '#000', borderRadius: 12, overflow: 'hidden', position: 'relative' }}>
              <video ref={candidateVideoRef} autoPlay playsInline style={{ width: '100%', height: 'auto', display: 'block' }} />
              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 12 }}>
                <button type="button" onClick={captureCandidatePhoto} style={{ width: 64, height: 64, borderRadius: '50%', border: '4px solid #fff', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fff', border: '2px solid #667eea' }} />
                </button>
              </div>
              <button type="button" onClick={stopCandidateCamera} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                <FaTimes />
              </button>
            </div>
            <p style={{ color: '#fff', marginTop: 16, textAlign: 'center', fontSize: 14 }}>Position document/yourself in the frame and tap the button to capture</p>
          </div>
        )}

        {/* Edit Candidate Modal */}
        {showEditModal && editingCandidate && (
          <div className="gogig-modal-bg" onClick={() => setShowEditModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '95%' : 800, 
              maxWidth: isMobile ? '98%' : 1200, 
              maxHeight: '90vh', 
              overflowY: 'auto',
              padding: isMobile ? '16px' : '24px'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: isMobile ? 16 : 20 
              }}>
                <h3 style={{ 
                  color: '#667eea', 
                  fontWeight: 700, 
                  fontSize: isMobile ? 18 : 24,
                  margin: 0,
                  flex: 1,
                  marginRight: 16
                }}>
                  Edit: {editingCandidate.name}
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: isMobile ? 20 : 24,
                    cursor: 'pointer',
                    color: '#666',
                    padding: 4,
                    borderRadius: 4
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleEditSubmit}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
                  gap: isMobile ? 12 : 16 
                }}>
                  {/* Basic Information */}
                  <div style={{ gridColumn: '1 / -1', marginBottom: isMobile ? 16 : 20 }}>
                    <h4 style={{ 
                      color: '#667eea', 
                      fontWeight: 600, 
                      marginBottom: isMobile ? 8 : 12,
                      fontSize: isMobile ? 16 : 18
                    }}>Basic Information</h4>
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: 4, 
                      fontWeight: 600, 
                      color: '#333',
                      fontSize: isMobile ? 13 : 14
                    }}>Establishment Name</label>
                    <input
                      type="text"
                      value={editForm.establishment_name}
                      onChange={(e) => handleEditInputChange('establishment_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: isMobile ? '10px 12px' : '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: isMobile ? 14 : 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Month</label>
                    <input
                      type="text"
                      value={editForm.month}
                      onChange={(e) => handleEditInputChange('month', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Punch Code</label>
                    <input
                      type="text"
                      value={editForm.punching_code}
                      onChange={(e) => handleEditInputChange('punching_code', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Employee Code</label>
                    <input
                      type="text"
                      value={editForm.employee_code}
                      onChange={(e) => handleEditInputChange('employee_code', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Remark</label>
                    <input
                      type="text"
                      value={editForm.remark}
                      onChange={(e) => handleEditInputChange('remark', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Contract Code</label>
                    <input
                      type="text"
                      value={editForm.contract_code}
                      onChange={(e) => handleEditInputChange('contract_code', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Apprentice Code</label>
                    <input
                      type="text"
                      value={editForm.apprentice_code}
                      onChange={(e) => handleEditInputChange('apprentice_code', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Aadhar No</label>
                    <input
                      type="text"
                      value={editForm.aadhar_no}
                      onChange={(e) => handleEditInputChange('aadhar_no', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Date of Birth</label>
                    <input
                      type="date"
                      value={editForm.date_of_birth}
                      onChange={(e) => handleEditInputChange('date_of_birth', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Gender</label>
                    <select
                      value={editForm.gender}
                      onChange={(e) => handleEditInputChange('gender', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Father Name</label>
                    <input
                      type="text"
                      value={editForm.father_name}
                      onChange={(e) => handleEditInputChange('father_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Mother Name</label>
                    <input
                      type="text"
                      value={editForm.mother_name}
                      onChange={(e) => handleEditInputChange('mother_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Category</label>
                    <input
                      type="text"
                      value={editForm.category}
                      onChange={(e) => handleEditInputChange('category', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Address</label>
                    <textarea
                      value={editForm.address}
                      onChange={(e) => handleEditInputChange('address', e.target.value)}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14,
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>District</label>
                    <input
                      type="text"
                      value={editForm.district}
                      onChange={(e) => handleEditInputChange('district', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>State</label>
                    <input
                      type="text"
                      value={editForm.state}
                      onChange={(e) => handleEditInputChange('state', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Pincode</label>
                    <input
                      type="text"
                      value={editForm.pincode}
                      onChange={(e) => handleEditInputChange('pincode', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Qualification</label>
                    <input
                      type="text"
                      value={editForm.qualification}
                      onChange={(e) => handleEditInputChange('qualification', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Institute Name</label>
                    <input
                      type="text"
                      value={editForm.institute_name}
                      onChange={(e) => handleEditInputChange('institute_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Specialization</label>
                    <input
                      type="text"
                      value={editForm.specialization}
                      onChange={(e) => handleEditInputChange('specialization', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Bank Name</label>
                    <input
                      type="text"
                      value={editForm.bank_name}
                      onChange={(e) => handleEditInputChange('bank_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Account No</label>
                    <input
                      type="text"
                      value={editForm.account_no}
                      onChange={(e) => handleEditInputChange('account_no', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>IFSC Code</label>
                    <input
                      type="text"
                      value={editForm.ifsc_code}
                      onChange={(e) => handleEditInputChange('ifsc_code', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Seeded Account</label>
                    <input
                      type="text"
                      value={editForm.seeded_account}
                      onChange={(e) => handleEditInputChange('seeded_account', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Beni ID Status</label>
                    <input
                      type="text"
                      value={editForm.beni_id_status}
                      onChange={(e) => handleEditInputChange('beni_id_status', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Date of Joining</label>
                    <input
                      type="date"
                      value={editForm.date_of_joining}
                      onChange={(e) => handleEditInputChange('date_of_joining', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Stipend</label>
                    <input
                      type="number"
                      value={editForm.stipend}
                      onChange={(e) => handleEditInputChange('stipend', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Job Role</label>
                    <input
                      type="text"
                      value={editForm.job_role}
                      onChange={(e) => handleEditInputChange('job_role', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Establishment Address</label>
                    <textarea
                      value={editForm.establishment_address}
                      onChange={(e) => handleEditInputChange('establishment_address', e.target.value)}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14,
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Insurance</label>
                    <select
                      value={editForm.insurance}
                      onChange={(e) => handleEditInputChange('insurance', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>ID Card</label>
                    <select
                      value={editForm.id_card}
                      onChange={(e) => handleEditInputChange('id_card', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Location</label>
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={(e) => handleEditInputChange('location', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Status</label>
                                         <select
                       value={editForm.status}
                       onChange={(e) => handleEditInputChange('status', e.target.value)}
                       style={{
                         width: '100%',
                         padding: '8px 12px',
                         border: '1px solid #d1d5db',
                         borderRadius: 6,
                         fontSize: 14
                       }}
                     >
                       <option value="">Select Status</option>
                       {candidateStatuses.map(status => (
                         <option key={status} value={status}>{status}</option>
                       ))}
                     </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Referral Code</label>
                    <input
                      type="text"
                      value={editForm.referral_code}
                      onChange={(e) => handleEditInputChange('referral_code', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 8 : 12, 
                  marginTop: isMobile ? 20 : 24 
                }}>
                  <button
                    type="submit"
                    className="gogig-card-btn"
                    style={{
                      flex: 1,
                      padding: isMobile ? '14px 20px' : '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: isMobile ? 14 : 16
                    }}
                  >
                    Update Candidate
                  </button>
                  <button
                    type="button"
                    className="gogig-card-btn-outline"
                    onClick={() => setShowEditModal(false)}
                    style={{
                      flex: 1,
                      padding: isMobile ? '14px 20px' : '12px 24px',
                      background: '#f8fafc',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: isMobile ? 14 : 16
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Document Management Modal */}
        {showDocumentModal && selectedCandidate && (
          <div className="gogig-modal-bg" onClick={() => setShowDocumentModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '100%' : 800, 
              maxWidth: isMobile ? '100%' : 1000, 
              maxHeight: isMobile ? '100vh' : '90vh', 
              overflowY: 'auto',
              background: '#fff',
              borderRadius: isMobile ? 0 : 16,
              boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3)',
              margin: isMobile ? 0 : 'auto',
              position: isMobile ? 'fixed' : 'relative',
              top: isMobile ? 0 : 'auto',
              left: isMobile ? 0 : 'auto',
              right: isMobile ? 0 : 'auto',
              bottom: isMobile ? 0 : 'auto'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: isMobile ? '16px 20px' : '24px 24px 0 24px',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: isMobile ? 16 : 24,
                background: isMobile ? '#f8fafc' : 'transparent',
                borderRadius: isMobile ? '0 0 12px 12px' : 0
              }}>
                <h3 style={{ 
                  fontSize: isMobile ? 16 : 24, 
                  fontWeight: 700, 
                  color: '#667eea', 
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 8 : 12,
                  flex: 1
                }}>
                  <FaFileAlt style={{ fontSize: isMobile ? 16 : 20 }} />
                  <span style={{ fontSize: isMobile ? 14 : 'inherit' }}>
                    {isMobile ? 'Documents' : `Manage Documents: ${selectedCandidate.name}`}
                  </span>
                </h3>
                <button
                  onClick={() => setShowDocumentModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: isMobile ? 18 : 24,
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: isMobile ? 8 : 4,
                    borderRadius: isMobile ? 6 : 0
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <div style={{ padding: isMobile ? '0 20px 20px 20px' : '0 24px 24px 24px' }}>
                {/* Document Upload Form */}
                <div style={{ 
                  background: isMobile ? '#fff' : '#f8fafc', 
                  padding: isMobile ? 16 : 20, 
                  borderRadius: isMobile ? 8 : 12, 
                  marginBottom: isMobile ? 16 : 24,
                  border: isMobile ? '1px solid #e5e7eb' : '1px solid #e5e7eb',
                  boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                }}>
                  <h4 style={{ 
                    fontSize: isMobile ? 16 : 16, 
                    fontWeight: 600, 
                    color: '#374151', 
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <FaUpload style={{ color: '#667eea', fontSize: isMobile ? 14 : 16 }} />
                    Upload New Document
                  </h4>
                  
                  <form onSubmit={handleDocumentUpload} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 20 : 16 }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontSize: isMobile ? 14 : 14, 
                        fontWeight: 600, 
                        color: '#374151', 
                        marginBottom: isMobile ? 12 : 8 
                      }}>
                        Document Type *
                      </label>
                      <select
                        value={documentForm.document_type}
                        onChange={e => setDocumentForm({ ...documentForm, document_type: e.target.value })}
                        style={{
                          width: '100%',
                          padding: isMobile ? '16px 20px' : '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: isMobile ? 12 : 8,
                          fontSize: isMobile ? 16 : 14,
                          background: '#fff',
                          transition: 'border-color 0.2s ease',
                          outline: 'none',
                          appearance: 'none',
                          backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '16px',
                          paddingRight: '40px'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        required
                      >
                        <option value="">Select Document Type *</option>
                        <option value="aadhar">🆔 Aadhar Card</option>
                        <option value="school_certificate">🎓 School Certificate</option>
                        <option value="educational_certificate">📜 Educational Certificate (Graduate/ITI/Diploma)</option>
                        <option value="bank_passbook">🏦 Bank Passbook (Front Page)</option>
                        <option value="candidate_image">📷 Candidate Photo</option>
                        <option value="pancard">🪪 PAN Card</option>
                        <option value="signature">✍️ Signature</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontSize: isMobile ? 14 : 14, 
                        fontWeight: 600, 
                        color: '#374151', 
                        marginBottom: isMobile ? 12 : 8 
                      }}>
                        Document File *
                      </label>
                      
                      {isMobile ? (
                        // Mobile-optimized file upload
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div style={{
                            border: '2px dashed #d1d5db',
                            borderRadius: 12,
                            padding: 20,
                            textAlign: 'center',
                            background: '#f9fafb',
                            transition: 'all 0.2s ease'
                          }}>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={e => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                              style={{
                                position: 'absolute',
                                opacity: 0,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer'
                              }}
                              required
                            />
                            <div style={{ pointerEvents: 'none' }}>
                              <FaFileAlt style={{ fontSize: 32, color: '#9ca3af', marginBottom: 8 }} />
                              <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                                {documentForm.file ? documentForm.file.name : 'Tap to choose file'}
                              </div>
                              <div style={{ fontSize: 12, color: '#6b7280' }}>
                                or use camera to scan
                              </div>
                            </div>
                          </div>
                          
                          <button
                            type="button"
                            onClick={startCamera}
                            style={{
                              padding: '16px 20px',
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 12,
                              fontSize: 16,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 12,
                              transition: 'all 0.2s ease',
                              boxShadow: '0 4px 16px rgba(14,165,233,0.3)'
                            }}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                          >
                            <FaCamera style={{ fontSize: 18 }} />
                            Scan with Camera
                          </button>
                        </div>
                      ) : (
                        // Desktop file upload
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={e => setDocumentForm({ ...documentForm, file: e.target.files[0] })}
                            style={{
                              flex: 1,
                              padding: '12px 16px',
                              border: '2px solid #e5e7eb',
                              borderRadius: 8,
                              fontSize: 14,
                              background: '#fff',
                              transition: 'border-color 0.2s ease',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            required
                          />
                        </div>
                      )}
                      
                      <p style={{ 
                        fontSize: isMobile ? 12 : 12, 
                        color: '#6b7280', 
                        margin: isMobile ? '12px 0 0 0' : '8px 0 0 0',
                        textAlign: isMobile ? 'center' : 'left'
                      }}>
                        Supported formats: JPEG, PNG, GIF, PDF (Max 10MB)
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={uploadingDocument}
                      style={{
                        padding: isMobile ? '16px 24px' : '12px 24px',
                        background: uploadingDocument ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: isMobile ? 12 : 8,
                        fontSize: isMobile ? 16 : 14,
                        fontWeight: 600,
                        cursor: uploadingDocument ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: uploadingDocument ? 'none' : '0 4px 16px rgba(102,126,234,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8
                      }}
                      onMouseOver={(e) => !uploadingDocument && (e.target.style.transform = 'translateY(-2px)')}
                      onMouseOut={(e) => !uploadingDocument && (e.target.style.transform = 'translateY(0)')}
                    >
                      {uploadingDocument ? (
                        <>
                          <div style={{
                            width: 16,
                            height: 16,
                            border: '2px solid #fff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Upload Document
                        </>
                      )}
                    </button>
                  </form>
                </div>

                {/* Existing Documents */}
                <div>
                  <h4 style={{ 
                    fontSize: isMobile ? 16 : 16, 
                    fontWeight: 600, 
                    color: '#374151', 
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <FaFileAlt style={{ color: '#667eea', fontSize: isMobile ? 14 : 16 }} />
                    Existing Documents
                  </h4>
                  
                  {candidateDocuments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 12 }}>
                      {candidateDocuments.map((doc) => (
                        <div key={doc.id} style={{
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: isMobile ? 12 : 8,
                          padding: isMobile ? 16 : 16,
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          justifyContent: 'space-between',
                          alignItems: isMobile ? 'stretch' : 'center',
                          gap: isMobile ? 12 : 0,
                          boxShadow: isMobile ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: isMobile ? 15 : 14, 
                              fontWeight: 600, 
                              color: '#374151',
                              textTransform: 'capitalize',
                              marginBottom: isMobile ? 8 : 4,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}>
                              {doc.document_type === 'aadhar' && '🆔'}
                              {doc.document_type === 'school_certificate' && '🎓'}
                              {doc.document_type === 'graduation_certificate' && '📜'}
                              {doc.document_type === 'iti_diploma_certificate' && '🔧'}
                              {doc.document_type === 'pancard' && '🪪'}
                              {doc.document_type === 'signature' && '✍️'}
                              {doc.document_type.replace('_', ' ')}
                            </div>
                            <div style={{ 
                              fontSize: isMobile ? 13 : 12, 
                              color: '#6b7280',
                              marginBottom: isMobile ? 8 : 4,
                              wordBreak: 'break-word'
                            }}>
                              {doc.file_name}
                            </div>
                            <div style={{ 
                              fontSize: isMobile ? 12 : 12, 
                              color: '#9ca3af'
                            }}>
                              Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            gap: isMobile ? 8 : 8,
                            flexDirection: isMobile ? 'row' : 'row',
                            justifyContent: isMobile ? 'stretch' : 'flex-end'
                          }}>
                            <button
                              onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                              style={{
                                padding: isMobile ? '12px 16px' : '8px 12px',
                                background: '#10b981',
                                color: '#fff',
                                border: 'none',
                                borderRadius: isMobile ? 8 : 6,
                                fontSize: isMobile ? 14 : 12,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: isMobile ? 8 : 4,
                                transition: 'all 0.2s ease',
                                flex: isMobile ? 1 : 'none',
                                fontWeight: isMobile ? 600 : 'normal'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#059669'}
                              onMouseOut={(e) => e.target.style.background = '#10b981'}
                            >
                              <FaDownload />
                              {isMobile ? 'Download' : 'Download'}
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              style={{
                                padding: isMobile ? '12px 16px' : '8px 12px',
                                background: '#ef4444',
                                color: '#fff',
                                border: 'none',
                                borderRadius: isMobile ? 8 : 6,
                                fontSize: isMobile ? 14 : 12,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: isMobile ? 8 : 4,
                                transition: 'all 0.2s ease',
                                flex: isMobile ? 1 : 'none',
                                fontWeight: isMobile ? 600 : 'normal'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#dc2626'}
                              onMouseOut={(e) => e.target.style.background = '#ef4444'}
                            >
                              <FaTrash />
                              {isMobile ? 'Delete' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: isMobile ? 32 : 40, 
                      color: '#6b7280',
                      background: '#f9fafb',
                      borderRadius: isMobile ? 12 : 8,
                      border: '1px solid #e5e7eb'
                    }}>
                      <FaFileAlt style={{ fontSize: isMobile ? 40 : 48, marginBottom: 16, opacity: 0.5 }} />
                      <p style={{ fontSize: isMobile ? 14 : 14, margin: 0, lineHeight: 1.5 }}>
                        No documents uploaded yet.<br />
                        Upload documents using the form above.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Camera Modal for Mobile Document Scanning */}
        {showCameraModal && (
          <div className="gogig-modal-bg" onClick={stopCamera}>
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: isMobile ? 0 : 20
            }} onClick={e => e.stopPropagation()}>
              <div style={{
                background: isMobile ? 'transparent' : '#fff',
                borderRadius: isMobile ? 0 : 16,
                padding: isMobile ? 0 : 20,
                maxWidth: isMobile ? '100%' : '90%',
                maxHeight: isMobile ? '100%' : '90%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: isMobile ? 0 : 20,
                width: isMobile ? '100%' : 'auto',
                height: isMobile ? '100%' : 'auto'
              }}>
                {!isMobile && (
                  <h3 style={{ 
                    fontSize: 18, 
                    fontWeight: 700, 
                    color: '#374151', 
                    margin: 0,
                    textAlign: 'center'
                  }}>
                    Scan Document
                  </h3>
                )}
                
                <div style={{
                  position: 'relative',
                  borderRadius: isMobile ? 0 : 12,
                  overflow: 'hidden',
                  border: isMobile ? 'none' : '2px solid #e5e7eb',
                  width: isMobile ? '100%' : 'auto',
                  height: isMobile ? '100%' : 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <video
                    id="camera-video"
                    autoPlay
                    playsInline
                    style={{
                      width: isMobile ? '100%' : '100%',
                      maxWidth: isMobile ? 'none' : 400,
                      height: isMobile ? '100%' : 'auto',
                      display: 'block',
                      objectFit: isMobile ? 'cover' : 'contain'
                    }}
                    ref={video => {
                      if (video && cameraStream) {
                        video.srcObject = cameraStream;
                      }
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: isMobile ? '20%' : 0,
                    left: isMobile ? '10%' : 0,
                    right: isMobile ? '10%' : 0,
                    bottom: isMobile ? '30%' : 0,
                    border: '3px solid #667eea',
                    borderRadius: isMobile ? 16 : 8,
                    pointerEvents: 'none',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                  }} />
                  
                  {/* Mobile overlay text */}
                  {isMobile && (
                    <div style={{
                      position: 'absolute',
                      top: 20,
                      left: 20,
                      right: 20,
                      textAlign: 'center',
                      color: '#fff',
                      background: 'rgba(0, 0, 0, 0.7)',
                      padding: '12px 16px',
                      borderRadius: 12,
                      fontSize: 16,
                      fontWeight: 600
                    }}>
                      📄 Position document within the frame
                    </div>
                  )}
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  gap: isMobile ? 16 : 12,
                  position: isMobile ? 'absolute' : 'relative',
                  bottom: isMobile ? 40 : 'auto',
                  left: isMobile ? 20 : 'auto',
                  right: isMobile ? 20 : 'auto',
                  width: isMobile ? 'calc(100% - 40px)' : 'auto'
                }}>
                  <button
                    onClick={capturePhoto}
                    style={{
                      padding: isMobile ? '16px 24px' : '12px 24px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: isMobile ? 12 : 8,
                      fontSize: isMobile ? 16 : 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? 12 : 8,
                      transition: 'all 0.2s ease',
                      flex: isMobile ? 1 : 'none',
                      boxShadow: '0 4px 16px rgba(16,185,129,0.3)'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    <FaCamera style={{ fontSize: isMobile ? 18 : 16 }} />
                    {isMobile ? 'Capture' : 'Capture'}
                  </button>
                  <button
                    onClick={stopCamera}
                    style={{
                      padding: isMobile ? '16px 24px' : '12px 24px',
                      background: isMobile ? 'rgba(107, 114, 128, 0.9)' : '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: isMobile ? 12 : 8,
                      fontSize: isMobile ? 16 : 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flex: isMobile ? 1 : 'none'
                    }}
                    onMouseOver={(e) => e.target.style.background = isMobile ? 'rgba(75, 85, 99, 0.9)' : '#4b5563'}
                    onMouseOut={(e) => e.target.style.background = isMobile ? 'rgba(107, 114, 128, 0.9)' : '#6b7280'}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Supervisor Candidates Modal */}
        {showSupervisorCandidatesModal && selectedSupervisorForCandidates && (
          <div className="gogig-modal-bg" onClick={() => setShowSupervisorCandidatesModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '95%' : 1200, 
              maxWidth: isMobile ? '95%' : '90%',
              maxHeight: isMobile ? '90vh' : '90vh',
              overflowY: 'auto'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 24,
                paddingBottom: 16,
                borderBottom: '2px solid #e5e7eb'
              }}>
                <div>
                  <h3 style={{ 
                    color: '#667eea', 
                    fontWeight: 700, 
                    fontSize: isMobile ? 20 : 24, 
                    margin: 0,
                    marginBottom: 4
                  }}>
                    Candidates Imported by {selectedSupervisorForCandidates.name}
                  </h3>
                  <p style={{ 
                    color: '#6b7280', 
                    fontSize: 14, 
                    margin: 0 
                  }}>
                    Email: {selectedSupervisorForCandidates.email} | Role: {selectedSupervisorForCandidates.role}
                  </p>
                </div>
                <button
                  onClick={() => setShowSupervisorCandidatesModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px 8px',
                    borderRadius: 4
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              {/* Search */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ position: 'relative' }}>
                  <FaSearch style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    fontSize: 14
                  }} />
                  <input
                    type="text"
                    placeholder="Search candidates by name, email, or mobile..."
                    value={supervisorCandidatesSearch}
                    onChange={(e) => {
                      setSupervisorCandidatesSearch(e.target.value);
                      setSupervisorCandidatesPage(1);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      outline: 'none',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              {/* Candidates List */}
              {supervisorCandidatesLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: 40 
                }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #f3f4f6',
                    borderTop: '4px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                </div>
              ) : supervisorCandidates.length > 0 ? (
                <div>
                  <div style={{ 
                    background: '#fff', 
                    borderRadius: 12, 
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    marginBottom: 20
                  }}>
                    <div className="admin-table-sticky-scroll admin-table-sticky-scroll--brand" style={{ maxHeight: isMobile ? '60vh' : '65vh' }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '13px'
                      }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Name</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Email</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Mobile</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Aadhar</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Employee Code</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Qualification</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Institute</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Status</th>
                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 700, fontSize: 12 }}>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supervisorCandidates.map((candidate, idx) => (
                            <tr key={candidate.id} style={{ 
                              borderBottom: '1px solid #f3f4f6',
                              background: idx % 2 === 0 ? '#fff' : '#fafbfc'
                            }}>
                              <td style={{ padding: '12px', fontSize: 12, fontWeight: 600 }}>{candidate.name || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>{candidate.email || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>{candidate.mobile || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>{candidate.aadhar_no || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>{candidate.employee_code || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>{candidate.qualification || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>{candidate.institute_name || 'N/A'}</td>
                              <td style={{ padding: '12px', fontSize: 12 }}>
                                {candidate.status ? (
                                  <span style={{
                                    padding: '4px 8px',
                                    borderRadius: 12,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    background: candidate.status === 'Active' ? '#d1fae5' : '#fee2e2',
                                    color: candidate.status === 'Active' ? '#065f46' : '#991b1b'
                                  }}>
                                    {candidate.status}
                                  </span>
                                ) : 'N/A'}
                              </td>
                              <td style={{ padding: '12px', fontSize: 12 }}>
                                {candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {supervisorCandidatesTotalPages > 1 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      gap: 12,
                      marginTop: 20
                    }}>
                      <button
                        onClick={() => setSupervisorCandidatesPage(Math.max(1, supervisorCandidatesPage - 1))}
                        disabled={supervisorCandidatesPage === 1}
                        style={{
                          padding: '8px 16px',
                          background: supervisorCandidatesPage === 1 ? '#f3f4f6' : '#667eea',
                          color: supervisorCandidatesPage === 1 ? '#9ca3af' : '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: supervisorCandidatesPage === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Previous
                      </button>
                      
                      <span style={{ fontSize: 14, color: '#374151', fontWeight: 600 }}>
                        Page {supervisorCandidatesPage} of {supervisorCandidatesTotalPages}
                      </span>
                      
                      <button
                        onClick={() => setSupervisorCandidatesPage(Math.min(supervisorCandidatesTotalPages, supervisorCandidatesPage + 1))}
                        disabled={supervisorCandidatesPage === supervisorCandidatesTotalPages}
                        style={{
                          padding: '8px 16px',
                          background: supervisorCandidatesPage === supervisorCandidatesTotalPages ? '#f3f4f6' : '#667eea',
                          color: supervisorCandidatesPage === supervisorCandidatesTotalPages ? '#9ca3af' : '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: supervisorCandidatesPage === supervisorCandidatesTotalPages ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ 
                  padding: 40, 
                  textAlign: 'center',
                  color: '#6b7280',
                  background: '#f9fafb',
                  borderRadius: 12,
                  border: '1px solid #e5e7eb'
                }}>
                  <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                  <p style={{ fontSize: 16, margin: 0, fontWeight: 600 }}>No candidates found</p>
                  <p style={{ fontSize: 14, margin: '8px 0 0 0', opacity: 0.7 }}>
                    {supervisorCandidatesSearch ? 'Try adjusting your search' : 'This supervisor has not imported any candidates yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Supervisor Management Modal */}
        {showSupervisorModal && (
          <div className="gogig-modal-bg" onClick={() => setShowSupervisorModal(false)}>
            <div className="gogig-modal" style={{ 
              minWidth: isMobile ? '90%' : 500, 
              maxWidth: isMobile ? '95%' : 600,
              maxHeight: isMobile ? '90vh' : '80vh',
              overflowY: 'auto'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ color: '#667eea', fontWeight: 700, fontSize: 24, marginBottom: 20 }}>
                {editingSupervisor ? 'Edit Supervisor' : 'Add New Supervisor'}
              </h3>
              
              <form onSubmit={editingSupervisor ? handleEditSupervisor : handleCreateSupervisor}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Name</label>
                    <input
                      type="text"
                      required
                      value={supervisorForm.name}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Email</label>
                    <input
                      type="email"
                      required
                      value={supervisorForm.email}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, email: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Mobile</label>
                    <input
                      type="tel"
                      required
                      value={supervisorForm.mobile}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, mobile: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Password</label>
                    <input
                      type="password"
                      required={!editingSupervisor}
                      value={supervisorForm.password}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={editingSupervisor ? 'Leave blank to keep current password' : ''}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#333' }}>Role</label>
                    <select
                      value={supervisorForm.role}
                      onChange={(e) => setSupervisorForm(prev => ({ ...prev, role: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        background: '#fff'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <option value="supervisor">Supervisor</option>
                      <option value="admin_supervisor">Admin Supervisor</option>
                    </select>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: 12, 
                  marginTop: 24,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <button
                    type="submit"
                    className="gogig-card-btn"
                    disabled={supervisorLoading}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: supervisorLoading ? 'not-allowed' : 'pointer',
                      opacity: supervisorLoading ? 0.7 : 1
                    }}
                  >
                    {supervisorLoading ? 'Saving...' : (editingSupervisor ? 'Update Supervisor' : 'Create Supervisor')}
                  </button>
                  <button
                    type="button"
                    className="gogig-card-btn-outline"
                    onClick={() => setShowSupervisorModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#f8fafc',
                      color: '#667eea',
                      border: '2px solid #667eea',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Supervisor Management Table */}
        {currentView === 'supervisors' && (
          <div style={{ 
            background: '#fff', 
            borderRadius: 16, 
            boxShadow: '0 2px 16px rgba(34,197,94,0.07)', 
            border: '1.5px solid #e5e7eb', 
            overflow: 'hidden',
            width: '100%'
          }}>
            {supervisorLoading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                padding: 40 
              }}>
                <div style={{
                  width: 40,
                  height: 40,
                  border: '4px solid #f3f4f6',
                  borderTop: '4px solid #667eea',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
              </div>
            ) : supervisors.length > 0 ? (
              <div className="admin-table-sticky-scroll admin-table-sticky-scroll--brand" style={{ width: '100%', maxHeight: 'min(72vh, calc(100vh - 260px))' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'separate', 
                  borderSpacing: 0,
                  fontSize: '13px',
                  minWidth: '800px'
                }}>
                <thead>
                  <tr>
                    <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 60 }}>ID</th>
                    <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120 }}>Name</th>
                    <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 150 }}>Email</th>
                    <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: 13, minWidth: 120 }}>Mobile</th>
                    <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 90 }}>Candidates</th>
                    <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100 }}>Role</th>
                    <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 80 }}>Status</th>
                    <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 100 }}>Created</th>
                    <th style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: 13, minWidth: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {supervisors.map((supervisor, idx) => (
                    <tr key={supervisor.id} style={{ 
                      borderBottom: '1px solid #f3f4f6',
                      background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                      transition: 'background-color 0.2s ease'
                    }}>
                      <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13 }}>{supervisor.id}</td>
                      <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13 }}>{supervisor.name}</td>
                      <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13 }}>{supervisor.email}</td>
                      <td style={{ padding: '16px 12px', textAlign: 'left', fontSize: 13 }}>{supervisor.mobile}</td>
                      <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13 }}>
                        {supervisor.candidate_count != null ? Number(supervisor.candidate_count) : 0}
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13 }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: supervisor.role === 'admin_supervisor' ? '#fef3c7' : '#dbeafe',
                          color: supervisor.role === 'admin_supervisor' ? '#92400e' : '#1e40af'
                        }}>
                          {supervisor.role === 'admin_supervisor' ? 'Admin' : 'Supervisor'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13 }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: supervisor.status === 'active' ? '#d1fae5' : '#fee2e2',
                          color: supervisor.status === 'active' ? '#065f46' : '#991b1b'
                        }}>
                          {supervisor.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13 }}>
                        {new Date(supervisor.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: 13 }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleViewSupervisorCandidates(supervisor)}
                            style={{
                              padding: '6px 12px',
                              background: '#10b981',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <FaEye style={{ fontSize: 10 }} />
                            Candidates
                          </button>
                          <button
                            onClick={() => openSupervisorModal(supervisor)}
                            style={{
                              padding: '6px 12px',
                              background: '#667eea',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSupervisor(supervisor.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : (
              <div style={{ 
                padding: 40, 
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <FaUsers style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }} />
                <p style={{ fontSize: 16, margin: 0 }}>No supervisors found</p>
                <p style={{ fontSize: 14, margin: '8px 0 0 0', opacity: 0.7 }}>Click "Add Supervisor" to create the first supervisor</p>
              </div>
            )}
          </div>
        )}

        {/* Registration Link Modal */}
        {showRegistrationLinkModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }} onClick={() => setShowRegistrationLinkModal(false)}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 32,
              maxWidth: 600,
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24
              }}>
                <h3 style={{
                  color: '#667eea',
                  fontWeight: 700,
                  fontSize: 24,
                  margin: 0
                }}>
                  Candidate Registration Link
                </h3>
                <button
                  onClick={() => setShowRegistrationLinkModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#666',
                    padding: 4,
                    borderRadius: 4
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <p style={{
                color: '#6b7280',
                fontSize: 14,
                marginBottom: 16,
                lineHeight: 1.6
              }}>
                Share this link with candidates. When they click it, they can fill in their details directly.
              </p>

              <div style={{
                display: 'flex',
                gap: 8,
                marginBottom: 24
              }}>
                <input
                  type="text"
                  value={registrationLink}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 14,
                    background: '#f9fafb',
                    color: '#374151'
                  }}
                />
                <button
                  onClick={copyRegistrationLink}
                  style={{
                    padding: '12px 20px',
                    background: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <FaCopy />
                  Copy
                </button>
              </div>

              <div style={{
                padding: 16,
                background: '#f0fdf4',
                borderRadius: 8,
                border: '1px solid #86efac'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: 12,
                  color: '#166534',
                  fontWeight: 600
                }}>
                  💡 Tip: Share this link via WhatsApp, Email, or SMS. Candidates can fill in all their details using this link.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default SupervisorDashboard;
